import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { DirectusService } from '../core/services/directus.service';

type WorkspaceTabKey = 'overview' | 'tickets-seating' | 'settings' | 'features' | 'orders' | 'guests';

interface WorkspaceTab {
  key: WorkspaceTabKey;
  label: string;
}

interface TicketRow {
  id: string;
  name: string;
  priceLabel: string;
  soldLabel: string;
  statusLabel: string;
  isHidden: boolean;
  status: string;
  priceValue: number;
  quantityMode: string;
  quantityTotal: number | null;
  sort: number;
}

type OrderFilter =
  | 'all'
  | 'paid'
  | 'not_paid'
  | 'free'
  | 'processing'
  | 'removed'
  | 'authorized';

interface OrderRow {
  id: string;
  numberLabel: string;
  buyerName: string;
  buyerEmail: string;
  buyerProfileId: string;
  status: OrderFilter;
  statusLabel: string;
  totalAmount: number;
  ticketCount: number;
  createdAt: Date | null;
  lines: Array<{
    ticketLabel: string;
    ticketNumber: string;
    guestName: string;
    price: number;
  }>;
}

type GuestStatusFilter = 'all' | 'checked_in' | 'not_arrived';
type OrderTicketStatus = 'not_paid' | 'paid' | 'free';

interface GuestRow {
  id: string;
  sourceCollection: 'event_attendees';
  sourceId: string;
  name: string;
  email: string;
  whatsapp: string;
  profileId: string;
  ticketId: string;
  ticketLabel: string;
  ticketPriceValue: number;
  ticketPriceLabel: string;
  ticketNumber: string;
  statusLabel: string;
  statusKind: 'paid' | 'not_paid' | 'free';
  buyerName: string;
  buyerEmail: string;
  orderDate: Date | null;
  orderId: string;
  checkedIn: boolean;
}

interface GuestTicketInputRow {
  id: number;
  ticketId: string;
  quantity: number;
}

interface GuestTicketAttendeeInput {
  id: number;
  sourceRowId: number;
  slotIndex: number;
  ticketId: string;
  ticketLabel: string;
  fields: Record<string, string>;
}

interface EventFeatureCard {
  id: string;
  key: string;
  name: string;
  description: string;
  selected: boolean;
  assignmentId: string | null;
  config?: Record<string, unknown> | null;
}

interface ScheduleItemDraft {
  draftId: number;
  name: string;
  startAt: Date;
  endAt: Date;
  place: string;
  tags: string;
  description: string;
  hidden?: boolean;
  canceled?: boolean;
}

interface ScheduleDayGroup {
  label: string;
  items: ScheduleItemDraft[];
}

interface LineupSpeakerDraft {
  id: string;
  fileId: string;
  assetUrl: string;
  croppedPreviewUrl?: string;
  cropBoxX: number;
  cropBoxY: number;
  cropBoxWidth: number;
  cropBoxHeight: number;
  name: string;
  tagline: string;
  description: string;
}

type CheckoutMode = 'per_order' | 'per_ticket';

interface CheckoutFieldOption {
  key: string;
  label: string;
  required?: boolean;
  locked?: boolean;
}

const DEFAULT_CHECKOUT_FIELDS: CheckoutFieldOption[] = [
  { key: 'first_name', label: 'First name', required: true, locked: true },
  { key: 'last_name', label: 'Last name', required: true, locked: true },
  { key: 'email', label: 'Email', required: true, locked: true },
  { key: 'whatsapp_number', label: 'WhatsApp number', required: true, locked: true }
];
const JCI_INDONESIA_CHAPTER_FIELD: CheckoutFieldOption = {
  key: 'jci_chapter',
  label: 'JCI Chapter',
  required: true,
  locked: true
};
const JCI_CHAPTER_OPTIONS = [
  'JCI Badung Bali',
  'JCI Bali',
  'JCI Bandung',
  'JCI Batavia',
  'JCI Bogor City',
  'JCI Borobudur',
  'JCI Central Java',
  'JCI Dewata',
  'JCI East Java',
  'JCI Femme',
  'JCI Jakarta',
  'JCI Jayakarta',
  'JCI Medan',
  'JCI Nusantara',
  'JCI Solo',
  'JCI Srikandi',
  'JCI West Sumatera',
  'JCI Yogyakarta'
] as const;

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive],
  template: `
    <section class="workspace-page">
      <header class="hero">
        <div class="crumbs">Events <span>›</span> {{ eventTitle || 'Event' }}</div>
        <div class="hero-top">
          <div>
            <h1>{{ eventTitle || 'Event workspace' }}</h1>
            <p class="subline">
              <span class="badge">{{ eventStatus || 'draft' }}</span>
            </p>
          </div>
          <div class="hero-actions">
            <button type="button" class="preview-btn" (click)="goBackToList()">Back to events</button>
            <button
              type="button"
              class="publish-btn"
              *ngIf="isDraft"
              (click)="publishEvent()"
              [disabled]="publishing"
            >
              {{ publishing ? 'Publishing...' : 'Publish' }}
            </button>
          </div>
        </div>
      </header>

      <nav class="tabs">
        <a
          *ngFor="let tab of tabs"
          [routerLink]="['/events', eventId, tab.key]"
          routerLinkActive="active"
          [routerLinkActiveOptions]="{ exact: true }"
        >
          {{ tab.label }}
        </a>
      </nav>

      <p class="state error" *ngIf="error">{{ error }}</p>

      <section class="panel" *ngIf="activeTab === 'overview'">
        <article class="event-details">
          <h2>Event details</h2>
          <div class="event-details-row">
            <img [src]="eventImageUrl" alt="" />
            <div class="meta-col">
              <h3>Date and time</h3>
              <p>{{ startDateLabel }}</p>
            </div>
            <div class="meta-col">
              <h3>Location</h3>
              <p>{{ eventLocation || '—' }}</p>
            </div>
            <div class="meta-col">
              <h3>Event type</h3>
              <p>{{ eventTypeLabel }}</p>
            </div>
            <button type="button" class="mini-btn" (click)="editEventDetails()">Edit details</button>
          </div>
        </article>

        <article class="setup-panel" *ngIf="isDraft">
          <div class="setup-head">
            <h2>Finish setting up your event</h2>
            <div class="progress">
              <span class="bar"><i></i></span>
              <strong>2/6</strong>
            </div>
          </div>
          <div class="setup-row">
            <div>
              <h3>Create tickets</h3>
              <p>Decide the price, quantity and details.</p>
            </div>
            <button type="button" class="mini-btn primary" (click)="goToTicketCreate()">Create tickets</button>
          </div>
          <div class="setup-row">
            <div>
              <h3>Customize checkout form</h3>
              <p>Choose what info you need from guests when they buy tickets.</p>
            </div>
            <button type="button" class="mini-btn" (click)="goToSettingsTab()">Customize form</button>
          </div>
          <div class="setup-row">
            <div>
              <h3>Review settings</h3>
              <p>Go over guest registration settings, ticket limits and email setup.</p>
            </div>
            <button type="button" class="mini-btn" (click)="goToSettingsTab()">Review settings</button>
          </div>
          <div class="setup-row">
            <div>
              <h3>Publish your event</h3>
              <p>Once everything is ready, make your event visible on your website.</p>
            </div>
            <button type="button" class="mini-btn" (click)="publishEvent()" [disabled]="publishing">
              {{ publishing ? 'Publishing...' : 'Publish event' }}
            </button>
          </div>
        </article>

        <div class="overview-grid" *ngIf="!isDraft">
          <article class="stats-card">
            <h2>Trends</h2>
            <div class="trends-filters">
              <label>
                Trend type
                <select [(ngModel)]="overviewTrendType">
                  <option value="net_sales">Net sales</option>
                  <option value="tickets_sold">Tickets sold</option>
                </select>
              </label>
              <label>
                Time period
                <select [(ngModel)]="overviewTrendDays">
                  <option [ngValue]="7">Last 7 days</option>
                  <option [ngValue]="30">Last 30 days</option>
                  <option [ngValue]="90">Last 90 days</option>
                </select>
              </label>
            </div>
            <p class="empty-title" *ngIf="!overviewTrendHasData">No data available for this time period</p>
            <p class="empty-sub" *ngIf="!overviewTrendHasData">When there’s data, you’ll see it here in a graph.</p>
            <div class="orders-strip" *ngIf="overviewTrendHasData">
              <div *ngIf="overviewTrendType === 'net_sales'">
                <strong>{{ formatIdr(overviewTrendNetSalesAmount) }}</strong>
                <span>Net sales ({{ overviewTrendDays }} days)</span>
              </div>
              <div *ngIf="overviewTrendType === 'tickets_sold'">
                <strong>{{ overviewTrendTicketsSoldCount }}</strong>
                <span>Tickets sold ({{ overviewTrendDays }} days)</span>
              </div>
            </div>
          </article>

          <article class="stats-card">
            <h2>Stats</h2>
            <div class="orders-strip">
              <div><strong>{{ formatIdr(overviewNetSalesAmount) }}</strong><span>Net sales</span></div>
              <div><strong>{{ formatIdr(overviewTotalSalesAmount) }}</strong><span>Total sales</span></div>
              <div><strong>{{ overviewTicketsSoldCount }}</strong><span>Tickets sold</span></div>
            </div>
            <div class="ticket-sales">
              <div class="ticket-item" *ngFor="let ticket of ticketRows">
                <p>{{ ticket.name }} ({{ ticket.priceLabel }})</p>
                <span>{{ ticket.soldLabel }}</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="settings-page" *ngIf="activeTab === 'settings'">
        <article class="panel checkout-summary" *ngIf="!isCheckoutFormEditing">
          <header class="settings-card-head">
            <h2>Checkout form</h2>
            <button type="button" class="mini-btn" (click)="openCheckoutFormEditor()">Edit</button>
          </header>
          <div class="settings-row">
            <span>Guests are required to fill</span>
            <span>{{ checkoutModeLabel }}</span>
          </div>
          <div class="settings-row">
            <span>Form fields</span>
            <span>{{ checkoutFieldsSummary }}</span>
          </div>
        </article>

        <article class="panel checkout-summary" *ngIf="!isRegistrationSettingsEditing">
          <header class="settings-card-head">
            <h2>Registration settings</h2>
            <button type="button" class="mini-btn" (click)="openRegistrationSettingsEditor()">Edit</button>
          </header>
          <div class="settings-row">
            <span>Registration status</span>
            <span>{{ registrationStatusLabel }}</span>
          </div>
        </article>

        <article class="panel registration-editor" *ngIf="isRegistrationSettingsEditing">
          <header class="checkout-editor-head">
            <h2>Registration settings</h2>
            <div class="checkout-editor-actions">
              <button type="button" class="mini-btn" (click)="cancelRegistrationSettingsEditor()">Cancel</button>
              <button type="button" class="mini-btn primary" (click)="saveRegistrationSettingsEditor()">Save</button>
            </div>
          </header>
          <section class="checkout-section">
            <h3>Registration status</h3>
            <div class="checkout-mode-group">
              <label class="mode-option">
                <input
                  type="radio"
                  name="registrationStatus"
                  value="open"
                  [checked]="registrationStatusDraft === 'open'"
                  (change)="registrationStatusDraft = 'open'"
                />
                <span>Open</span>
              </label>
              <label class="mode-option">
                <input
                  type="radio"
                  name="registrationStatus"
                  value="closed"
                  [checked]="registrationStatusDraft === 'closed'"
                  (change)="registrationStatusDraft = 'closed'"
                />
                <span>Closed</span>
              </label>
            </div>
          </section>
        </article>

        <article class="panel checkout-summary" *ngIf="!isTicketSettingsEditing">
          <header class="settings-card-head">
            <h2>Ticket settings</h2>
            <button type="button" class="mini-btn" (click)="openTicketSettingsEditor()">Edit</button>
          </header>
          <div class="settings-row">
            <span>Max tickets per order</span>
            <span>{{ maxTicketsPerOrder }}</span>
          </div>
          <div class="settings-row">
            <span>Checkout time limit</span>
            <span>{{ checkoutTimeLimitMinutes }} minutes</span>
          </div>
        </article>

        <article class="panel registration-editor" *ngIf="isTicketSettingsEditing">
          <header class="checkout-editor-head">
            <h2>Ticket settings</h2>
            <div class="checkout-editor-actions">
              <button type="button" class="mini-btn" (click)="cancelTicketSettingsEditor()">Cancel</button>
              <button type="button" class="mini-btn primary" (click)="saveTicketSettingsEditor()">Save</button>
            </div>
          </header>
          <section class="checkout-section">
            <div class="ticket-settings-grid">
              <label>
                <span>Max tickets per order</span>
                <input
                  type="number"
                  min="1"
                  [ngModel]="maxTicketsPerOrderDraft"
                  (ngModelChange)="maxTicketsPerOrderDraft = normalizePositiveInt($event, maxTicketsPerOrderDraft)"
                />
              </label>
              <label>
                <span>Checkout time limit (minutes)</span>
                <input
                  type="number"
                  min="1"
                  [ngModel]="checkoutTimeLimitMinutesDraft"
                  (ngModelChange)="checkoutTimeLimitMinutesDraft = normalizePositiveInt($event, checkoutTimeLimitMinutesDraft)"
                />
              </label>
            </div>
            <p class="settings-note">
              Max tickets per order applies to the total combined quantity across all ticket types.
            </p>
          </section>
        </article>

        <article class="panel checkout-editor" *ngIf="isCheckoutFormEditing">
          <header class="checkout-editor-head">
            <h2>Checkout form</h2>
            <div class="checkout-editor-actions">
              <button type="button" class="mini-btn" (click)="cancelCheckoutFormEditor()">Cancel</button>
              <button type="button" class="mini-btn primary" (click)="saveCheckoutFormEditor()">Save</button>
            </div>
          </header>

          <section class="checkout-section">
            <h3>Collect guest info</h3>
            <div class="checkout-mode-group">
              <label class="mode-option">
                <input
                  type="radio"
                  name="checkoutMode"
                  value="per_order"
                  [checked]="checkoutModeDraft === 'per_order'"
                  (change)="setCheckoutModeDraft('per_order')"
                />
                <span>1 form per order</span>
              </label>
              <label class="mode-option">
                <input
                  type="radio"
                  name="checkoutMode"
                  value="per_ticket"
                  [checked]="checkoutModeDraft === 'per_ticket'"
                  (change)="setCheckoutModeDraft('per_ticket')"
                />
                <span>1 form per ticket</span>
              </label>
            </div>
            <div class="ticket-form-selector" *ngIf="checkoutModeDraft === 'per_ticket' && checkoutTicketTargets.length > 0">
              <label for="checkout-ticket-target">Ticket form target</label>
              <select
                id="checkout-ticket-target"
                [ngModel]="selectedCheckoutTicketIdDraft"
                (ngModelChange)="setSelectedCheckoutTicket($event)"
              >
                <option *ngFor="let ticket of checkoutTicketTargets" [ngValue]="ticket.id">{{ ticket.name }}</option>
              </select>
            </div>
          </section>

          <section class="checkout-grid">
            <article class="checkout-config">
              <h3>Customize your checkout form</h3>
              <div class="checkout-block">
                <label>What info do you need?</label>
                <ul class="checkout-fields-list">
                  <li *ngFor="let field of activeCheckoutFieldsDraft; let i = index">
                    <div>
                      <strong>{{ field.label }}</strong>
                      <p>{{ field.required ? 'Required' : 'Optional' }}</p>
                    </div>
                    <button
                      *ngIf="!field.locked"
                      type="button"
                      class="icon-btn tiny"
                      (click)="removeCheckoutField(i)"
                    >
                      ✕
                    </button>
                    <span *ngIf="field.locked" class="locked-field-marker" aria-label="Required field">🔒</span>
                  </li>
                </ul>
                <div class="add-field-wrap">
                  <button type="button" class="link-btn" (click)="toggleAddFieldMenu($event)">+ Add Fields</button>
                  <div class="add-field-menu" *ngIf="showAddFieldMenu" (click)="$event.stopPropagation()">
                    <button type="button" *ngFor="let field of availableCheckoutFields" (click)="addCheckoutField(field)">
                      {{ field.label }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="checkout-block">
                <label>Button text</label>
                <input type="text" [(ngModel)]="checkoutButtonTextDraft" />
              </div>
            </article>

            <article class="checkout-preview">
              <h3>Form preview</h3>
              <div class="preview-canvas">
                <div class="preview-card">
                  <h4>Add your details</h4>
                  <div class="preview-grid-two">
                    <label class="preview-field" *ngIf="hasCheckoutField('first_name')">
                      <span>*First name</span>
                      <input type="text" disabled />
                    </label>
                    <label class="preview-field" *ngIf="hasCheckoutField('last_name')">
                      <span>*Last name</span>
                      <input type="text" disabled />
                    </label>
                  </div>
                  <label class="preview-field" *ngIf="hasCheckoutField('email')">
                    <span>*Email</span>
                    <input type="text" disabled />
                  </label>
                  <label class="preview-field" *ngFor="let field of previewAdditionalFields">
                    <span>{{ field.label }}</span>
                    <input type="text" disabled />
                  </label>
                  <button type="button" class="preview-submit">{{ checkoutButtonTextDraft || 'Continue' }}</button>
                </div>
              </div>
            </article>
          </section>
        </article>
      </section>

      <section class="schedule-setup-page" *ngIf="activeTab === 'features' && scheduleSetupOpen">
        <header class="schedule-setup-head">
          <div class="schedule-setup-head-left">
            <button type="button" class="icon-btn back-icon" aria-label="Back" (click)="closeScheduleSetup()">‹</button>
            <h2>Event schedule</h2>
          </div>
          <div class="schedule-setup-head-actions">
            <button type="button" class="mini-btn" (click)="closeScheduleSetup()">Cancel</button>
            <button type="button" class="mini-btn primary" (click)="saveScheduleSetup()">Save</button>
          </div>
        </header>
        <article class="panel schedule-list-panel">
          <header class="schedule-list-head">
            <div>
              <h3>Event schedule</h3>
              <p>Share the lineup of the event and show what's on.</p>
            </div>
            <button type="button" class="mini-btn" (click)="addScheduleItemFromSetup()">Add Schedule Item</button>
          </header>

          <div class="schedule-setup-body" *ngIf="scheduleItemsDraft.length === 0">
            <div class="schedule-setup-hero" aria-hidden="true">🗓</div>
            <h3>Build a schedule</h3>
            <p>Share the lineup of the event and show what's on.</p>
            <button type="button" class="mini-btn primary" (click)="addScheduleItemFromSetup()">
              Add Schedule Item
            </button>
          </div>

          <section class="schedule-list" *ngIf="scheduleItemsDraft.length > 0">
            <div class="schedule-day-group" *ngFor="let group of groupedScheduleItems">
              <div class="schedule-day-title">{{ group.label }}</div>
              <div class="schedule-item-row" *ngFor="let item of group.items">
                <div class="schedule-time">{{ formatScheduleTimeRange(item.startAt, item.endAt) }}</div>
                <div class="schedule-name">{{ item.name }}</div>
                <div class="schedule-row-actions">
                  <span class="canceled-marker" *ngIf="item.canceled">Canceled</span>
                  <span class="hidden-eye" *ngIf="item.hidden" aria-label="Hidden">👁</span>
                  <button type="button" class="mini-btn" (click)="editScheduleItem(item.draftId)">Edit</button>
                  <div class="schedule-menu-wrap">
                    <button type="button" class="icon-btn" aria-label="More actions" (click)="toggleScheduleItemMenu(item.draftId, $event)">…</button>
                    <div class="schedule-item-menu" *ngIf="openScheduleItemMenuId === item.draftId" (click)="$event.stopPropagation()">
                      <button type="button" (click)="toggleHideScheduleItem(item.draftId)">
                        {{ item.hidden ? 'Show' : 'Hide' }}
                      </button>
                      <button type="button" (click)="cancelScheduleItem(item.draftId)">Cancel</button>
                      <button type="button" class="danger" (click)="deleteScheduleItem(item.draftId)">Delete</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button type="button" class="schedule-add-inline" (click)="addScheduleItemFromSetup()">
              + Add Schedule Item
            </button>
          </section>
        </article>
      </section>

      <section class="features-page" *ngIf="activeTab === 'features' && !scheduleSetupOpen">
        <article class="panel features-panel" *ngIf="selectedFeatureCards.length > 0">
          <h2>Features you're using</h2>
          <div class="features-using-list">
            <div class="features-using-row" *ngFor="let feature of selectedFeatureCards">
              <div class="using-left">
                <span class="using-check">✓</span>
                <div>
                  <h3>{{ feature.name }}</h3>
                  <p>{{ selectedFeatureSubtitle(feature) }}</p>
                </div>
              </div>
              <div class="using-actions">
                <button type="button" class="link-btn" *ngIf="feature.key === 'video_conference'" (click)="openVideoMeetingLink(feature)">
                  {{ videoMeetingLinkLabel(feature) }}
                </button>
                <div class="using-menu-wrap">
                  <button type="button" class="icon-btn" aria-label="More actions" (click)="toggleUsingFeatureMenu(feature.id, $event)">…</button>
                  <div class="using-row-menu" *ngIf="openUsingFeatureMenuId === feature.id" (click)="$event.stopPropagation()">
                    <button type="button" *ngIf="feature.key === 'video_conference'" (click)="editFeatureLink(feature)">Edit link</button>
                    <button type="button" class="danger" (click)="disconnectFeature(feature)">
                      {{ feature.key === 'video_conference' ? 'Disconnect' : 'Remove' }}
                    </button>
                  </div>
                </div>
                <button type="button" class="mini-btn" (click)="handleFeatureAction(feature)">Manage</button>
              </div>
            </div>
          </div>
        </article>

        <article class="panel features-panel">
          <h2>Features you can add</h2>
          <p class="state" *ngIf="featuresLoading">Loading features...</p>
          <p class="state error" *ngIf="featuresError">{{ featuresError }}</p>
          <div class="features-grid" *ngIf="!featuresLoading && !featuresError">
            <article class="feature-card" *ngFor="let feature of availableFeatureCards">
              <div class="feature-icon" aria-hidden="true">{{ featureIcon(feature.key) }}</div>
              <div class="feature-body">
                <h3>{{ feature.name }}</h3>
                <p>{{ feature.description }}</p>
                <div class="feature-actions-row">
                  <button
                    type="button"
                    class="link-btn"
                    [disabled]="isFeatureBusy(feature.id) || isFeatureComingSoon(feature)"
                    (click)="handleFeatureAction(feature)"
                  >
                    {{ isFeatureBusy(feature.id) ? 'Saving...' : featurePrimaryActionLabel(feature) }}
                  </button>
                </div>
              </div>
            </article>
          </div>
          <p class="state" *ngIf="!featuresLoading && !featuresError && availableFeatureCards.length === 0">
            No additional features available for this event.
          </p>
        </article>
      </section>

      <div class="confirm-overlay" *ngIf="videoConfigDialogOpen" (click)="closeVideoConfigDialog()">
        <article class="confirm-dialog video-config-dialog" (click)="$event.stopPropagation()">
          <header class="confirm-head">
            <h3>{{ videoConfigIsNew ? 'Set Up Video Conferencing' : 'Video Conferencing' }}</h3>
            <button type="button" class="close-btn" (click)="closeVideoConfigDialog()">✕</button>
          </header>
          <div class="video-config-body">
            <label>
              <span>Provider</span>
              <select [(ngModel)]="videoProviderDraft">
                <option value="zoom">Zoom</option>
                <option value="google_meet">Google Meet</option>
              </select>
            </label>
            <label>
              <span>Meeting URL</span>
              <input type="url" placeholder="https://..." [(ngModel)]="videoUrlDraft" />
            </label>
          </div>
          <p class="state error video-config-error" *ngIf="videoConfigError">{{ videoConfigError }}</p>
          <div class="confirm-actions">
            <button type="button" class="mini-btn" (click)="closeVideoConfigDialog()" [disabled]="videoConfigSaving">
              Cancel
            </button>
            <button
              type="button"
              class="mini-btn primary"
              (click)="saveVideoConfigDialog()"
              [disabled]="videoConfigSaving"
            >
              {{ videoConfigSaving ? 'Saving...' : (videoConfigIsNew ? 'Set Up Video Conferencing' : 'Save') }}
            </button>
          </div>
        </article>
      </div>

      <div class="confirm-overlay" *ngIf="scheduleItemDialogOpen" (click)="closeScheduleItemDialog()">
        <article class="confirm-dialog schedule-item-dialog" (click)="$event.stopPropagation()">
          <header class="confirm-head">
            <h3>Add Schedule Item</h3>
            <button type="button" class="close-btn" (click)="closeScheduleItemDialog()">✕</button>
          </header>
          <div class="schedule-item-body">
            <label>
              <span>Item Name *</span>
              <input type="text" placeholder="Enter item name" [(ngModel)]="scheduleItemNameDraft" />
            </label>
            <div class="schedule-item-grid">
              <label>
                <span>Start date *</span>
                <input type="date" [(ngModel)]="scheduleStartDateDraft" />
              </label>
              <label>
                <span>Start time *</span>
                <input type="time" [(ngModel)]="scheduleStartTimeDraft" />
              </label>
              <label>
                <span>End date *</span>
                <input type="date" [(ngModel)]="scheduleEndDateDraft" />
              </label>
              <label>
                <span>End time *</span>
                <input type="time" [(ngModel)]="scheduleEndTimeDraft" />
              </label>
            </div>
            <div class="schedule-item-grid">
              <label>
                <span>Place</span>
                <input type="text" placeholder="e.g. Main stage" [(ngModel)]="schedulePlaceDraft" />
              </label>
              <label>
                <span>Tags</span>
                <input type="text" placeholder="e.g. Business & Startups" [(ngModel)]="scheduleTagsDraft" />
              </label>
            </div>
            <label>
              <span>Description</span>
              <textarea
                rows="4"
                placeholder="Use this space to tell guests more about this schedule item"
                [(ngModel)]="scheduleDescriptionDraft"
              ></textarea>
            </label>
          </div>
          <p class="state error video-config-error" *ngIf="scheduleItemDialogError">{{ scheduleItemDialogError }}</p>
          <div class="confirm-actions">
            <button type="button" class="mini-btn" (click)="closeScheduleItemDialog()">Cancel</button>
            <button type="button" class="mini-btn primary" (click)="saveScheduleItemDialog()">Add</button>
          </div>
        </article>
      </div>

      <div class="confirm-overlay" *ngIf="lineupDialogOpen" (click)="closeLineupDialog()">
        <article class="confirm-dialog lineup-dialog" (click)="$event.stopPropagation()">
          <header class="confirm-head">
            <h3>Lineup</h3>
            <button type="button" class="close-btn" (click)="closeLineupDialog()">✕</button>
          </header>
          <p class="state error video-config-error" *ngIf="lineupError">{{ lineupError }}</p>
          <input #lineupImageInput type="file" accept="image/*" hidden (change)="onLineupImageSelected($event)" />
          <p class="lineup-intro">
            Highlight your lineup of special guests and add info about each person.
          </p>
          <div class="lineup-list">
            <article class="lineup-item" *ngFor="let speaker of lineupSpeakersDraft">
              <div class="lineup-item-media">
                <button
                  type="button"
                  class="lineup-image-btn"
                  [class.empty]="!speaker.fileId"
                  (click)="openLineupImagePicker(speaker.id, lineupImageInput)"
                >
                  <img *ngIf="speaker.fileId" [src]="displayLineupImageUrl(speaker)" alt="" />
                  <span *ngIf="!speaker.fileId">Upload</span>
                </button>
                <button
                  *ngIf="speaker.fileId"
                  type="button"
                  class="mini-btn"
                  (click)="openLineupImageCropper(speaker.id)"
                >
                  Crop image
                </button>
              </div>
              <div class="lineup-item-fields">
                <label>
                  <span>Name</span>
                  <input [(ngModel)]="speaker.name" placeholder="Name" />
                </label>
                <label>
                  <span>Tagline</span>
                  <input [(ngModel)]="speaker.tagline" placeholder="Add a tagline" />
                </label>
                <label>
                  <span>Description</span>
                  <textarea rows="4" maxlength="5000" [(ngModel)]="speaker.description" placeholder="Description"></textarea>
                </label>
                <div class="lineup-item-meta">
                  <span>{{ speaker.description.length }}/5000</span>
                  <button type="button" class="link-btn danger" (click)="removeLineupSpeaker(speaker.id)">Delete</button>
                </div>
              </div>
            </article>
          </div>
          <div class="lineup-add-row">
            <button type="button" class="mini-btn" (click)="addLineupSpeaker()">+ Add another</button>
          </div>
          <div class="confirm-actions">
            <button type="button" class="mini-btn" (click)="closeLineupDialog()" [disabled]="lineupSaving">Cancel</button>
            <button type="button" class="mini-btn primary" (click)="saveLineupDialog()" [disabled]="lineupSaving">
              {{ lineupSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </article>
      </div>

      <div class="confirm-overlay" *ngIf="lineupCropDialogOpen" (click)="cancelLineupImageCropper()">
        <article class="confirm-dialog lineup-crop-dialog" (click)="$event.stopPropagation()">
          <header class="confirm-head">
            <h3>Crop Image</h3>
          </header>
          <div class="lineup-crop-stage">
            <div
              class="lineup-crop-frame"
              [class.dragging]="lineupCropDragActive"
              [style.--lineup-crop-ratio]="lineupCropFrameAspectRatio"
            >
              <img *ngIf="lineupCropDialogImageUrl" [src]="lineupCropDialogImageUrl" alt="" class="lineup-crop-image" />
              <div
                class="lineup-crop-selection"
                [style.left.%]="lineupCropBoxX"
                [style.top.%]="lineupCropBoxY"
                [style.width.%]="lineupCropBoxWidth"
                [style.height.%]="lineupCropBoxHeight"
                (pointerdown)="onLineupCropSelectionPointerDown($event)"
              >
                <button type="button" class="lineup-crop-handle nw" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'nw')"></button>
                <button type="button" class="lineup-crop-handle ne" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'ne')"></button>
                <button type="button" class="lineup-crop-handle sw" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'sw')"></button>
                <button type="button" class="lineup-crop-handle se" (pointerdown)="onLineupCropResizeHandlePointerDown($event, 'se')"></button>
              </div>
            </div>
          </div>
          <div class="confirm-actions">
            <button type="button" class="mini-btn" (click)="cancelLineupImageCropper()">Cancel</button>
            <button type="button" class="mini-btn primary" (click)="applyLineupImageCropper()">Save</button>
          </div>
        </article>
      </div>

      <section class="orders-page" *ngIf="activeTab === 'orders' && !orderDetailOpen">
        <article class="panel orders-metrics-panel">
          <div class="orders-metrics-strip">
            <div>
              <strong>{{ ordersNetSalesLabel }}</strong>
              <span>Net sales</span>
            </div>
            <div>
              <strong>{{ ordersTotalSalesLabel }}</strong>
              <span>Total sales</span>
            </div>
            <div>
              <strong>{{ filteredOrderRows.length }}</strong>
              <span>Orders</span>
            </div>
            <div>
              <strong>{{ ordersTicketsSoldCount }}</strong>
              <span>Tickets sold</span>
            </div>
          </div>
        </article>

        <article class="panel orders-panel">
          <header class="orders-head">
            <h2>Orders ({{ filteredOrderRows.length }})</h2>
            <div class="orders-head-actions">
              <div class="orders-more-wrap">
                <button type="button" class="mini-btn" (click)="toggleOrdersMoreMenu($event)">More Actions</button>
                <div class="orders-more-menu" *ngIf="ordersMoreMenuOpen" (click)="$event.stopPropagation()">
                  <button type="button" (click)="refreshOrders()">Refresh orders</button>
                  <button type="button" (click)="openAddGuest()">Add guest</button>
                </div>
              </div>
              <button type="button" class="mini-btn primary" (click)="exportOrderList()">Export Order List</button>
            </div>
          </header>

          <div class="orders-filter-row">
            <label class="orders-filter-select">
              <span>Filter by:</span>
              <select [(ngModel)]="ordersFilter">
                <option value="all">All Orders</option>
                <option value="paid">Paid</option>
                <option value="not_paid">Not paid</option>
                <option value="free">Free</option>
                <option value="processing">Processing</option>
                <option value="removed">Removed</option>
                <option value="authorized">Authorized</option>
              </select>
            </label>
            <label class="orders-search">
              <span class="search-icon">⌕</span>
              <input type="text" placeholder="Search..." [(ngModel)]="ordersSearch" />
            </label>
          </div>

          <div class="orders-empty" *ngIf="!ordersLoading && filteredOrderRows.length === 0">
            <div class="orders-empty-avatar" aria-hidden="true">👤</div>
            <h3>No guests yet</h3>
            <p>Once guests buy tickets to your event, you'll see your orders here.</p>
            <button type="button" class="link-btn" (click)="openAddGuest()">Add Guest</button>
          </div>

          <div class="orders-table-wrap" *ngIf="!ordersLoading && filteredOrderRows.length > 0">
            <div class="bulk-actions" *ngIf="hasOrderSelection">
              <span>{{ selectedOrderCount }} of {{ filteredOrderRows.length }} selected</span>
              <button type="button" class="link-btn" (click)="clearOrderSelection()">Deselect All</button>
              <button type="button" class="link-btn" (click)="bulkMarkOrdersPaid()">Mark Paid</button>
              <button type="button" class="link-btn" (click)="bulkMarkOrdersUnpaid()">Mark Unpaid</button>
              <button type="button" class="link-btn danger-link" (click)="bulkDeleteOrders()">Delete</button>
            </div>
            <table>
              <thead>
                <tr>
                  <th class="check-col">
                    <input
                      type="checkbox"
                      [checked]="allVisibleOrdersSelected"
                      [indeterminate]="hasOrderSelection && !allVisibleOrdersSelected"
                      (change)="toggleSelectAllOrders($event)"
                    />
                  </th>
                  <th>Order</th>
                  <th>Buyer</th>
                  <th>Status</th>
                  <th>Tickets</th>
                  <th>Total</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let order of filteredOrderRows" (mouseenter)="hoveredOrderId = order.id" (mouseleave)="hoveredOrderId = null">
                  <td>
                    <input
                      type="checkbox"
                      [checked]="isOrderSelected(order.id)"
                      (change)="toggleOrderSelection(order.id, $event)"
                    />
                  </td>
                  <td>{{ order.numberLabel }}</td>
                  <td>{{ order.buyerName || '—' }}</td>
                  <td>{{ order.statusLabel }}</td>
                  <td>{{ order.ticketCount }}</td>
                  <td>{{ formatIdr(order.totalAmount) }}</td>
                  <td>{{ order.createdAt ? (order.createdAt | date: 'MMM d, y, h:mm a') : '—' }}</td>
                  <td class="guest-view-cell">
                    <button type="button" class="mini-btn primary" *ngIf="hoveredOrderId === order.id" (click)="openOrderDetail(order)">View</button>
                    <button type="button" class="chevron-btn" *ngIf="hoveredOrderId !== order.id" (click)="openOrderDetail(order)">›</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section class="orders-page guest-detail-page" *ngIf="activeTab === 'orders' && orderDetailOpen && selectedOrder">
        <ng-container *ngIf="selectedOrder as order">
          <header class="schedule-setup-head">
            <div class="schedule-setup-head-left">
              <button type="button" class="icon-btn back-icon" aria-label="Back" (click)="closeOrderDetail()">‹</button>
              <h2>{{ order.buyerName || order.numberLabel }}</h2>
            </div>
          </header>

          <article class="panel guests-add-card">
            <header class="settings-card-head">
              <h2>Buyers info</h2>
              <button type="button" class="link-btn" (click)="openOrderBuyerProfile(order)" [disabled]="!order.buyerProfileId">
                View Contact Profile
              </button>
            </header>
            <div class="guest-detail-grid">
              <div>
                <span>Name</span>
                <strong>{{ order.buyerName || '—' }}</strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{{ order.buyerEmail || '—' }}</strong>
              </div>
              <div>
                <span>Order date</span>
                <strong>{{ order.createdAt ? (order.createdAt | date: 'MMM d, y, h:mm a') : '—' }}</strong>
              </div>
            </div>
          </article>

          <article class="panel guests-add-card">
            <header class="settings-card-head">
              <h2>Order summary
                <span class="status-pill" [class.paid]="order.status === 'paid'" [class.free]="order.status === 'free'">
                  {{ order.statusLabel }}
                </span>
              </h2>
              <button type="button" class="mini-btn" (click)="markSelectedOrderPaid()" [disabled]="markOrderPaidBusy || order.status === 'paid'">
                {{ markOrderPaidBusy ? 'Saving...' : 'Mark as Paid' }}
              </button>
            </header>
            <div class="orders-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ticket info</th>
                    <th>Ticket number</th>
                    <th>Ticket price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let line of order.lines">
                    <td>
                      {{ line.ticketLabel }}
                      <div class="order-line-sub">Guest: {{ line.guestName }}</div>
                    </td>
                    <td>{{ line.ticketNumber || '—' }}</td>
                    <td>{{ formatIdr(line.price) }}</td>
                  </tr>
                  <tr *ngIf="!order.lines.length">
                    <td colspan="3">No ticket lines linked to this order.</td>
                  </tr>
                  <tr>
                    <td></td>
                    <td><strong>Total</strong></td>
                    <td><strong>{{ formatIdr(order.totalAmount) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </ng-container>
      </section>

      <section class="guests-page" *ngIf="activeTab === 'guests' && !guestAddOpen && !guestDetailOpen">
        <article class="panel guests-metrics-panel">
          <div class="guests-checkin-head">
            <strong>{{ checkedInGuestsCount }}/{{ guestRows.length }}</strong>
            <span>Guests checked in</span>
            <em>{{ guestsArrivedPercentLabel }} arrived</em>
          </div>
          <div class="guests-checkin-bar"><i [style.width.%]="guestsArrivedPercent"></i></div>
        </article>

        <article class="panel guests-list-panel">
          <header class="orders-head">
            <h2>Guests ({{ filteredGuestRows.length }})</h2>
            <div class="orders-head-actions">
              <button type="button" class="icon-btn" aria-label="Refresh" (click)="refreshGuests()">↻</button>
              <div class="orders-more-wrap">
                <button type="button" class="mini-btn" (click)="toggleGuestsMoreMenu($event)">More Actions</button>
                <div class="orders-more-menu" *ngIf="guestsMoreMenuOpen" (click)="$event.stopPropagation()">
                  <button type="button" (click)="refreshGuests()">Refresh guests</button>
                  <button type="button" (click)="exportGuestList()">Export Guest List</button>
                </div>
              </div>
              <button type="button" class="mini-btn primary" (click)="goToAddGuests()">Add Guests</button>
            </div>
          </header>

          <div class="orders-filter-row">
            <div class="guests-filter-group">
              <label class="orders-filter-select">
                <span>Filter by:</span>
                <select [(ngModel)]="guestStatusFilter">
                  <option value="all">All Guests</option>
                  <option value="checked_in">Checked in</option>
                  <option value="not_arrived">Not arrived</option>
                </select>
              </label>
              <label class="orders-filter-select">
                <select [(ngModel)]="guestTicketFilter">
                  <option value="all">All Tickets</option>
                  <option *ngFor="let ticket of ticketRows" [value]="ticket.id">{{ ticket.name }}</option>
                </select>
              </label>
            </div>
            <label class="orders-search">
              <span class="search-icon">⌕</span>
              <input type="text" placeholder="Search..." [(ngModel)]="guestSearch" />
            </label>
          </div>

          <div class="orders-empty" *ngIf="!guestsLoading && filteredGuestRows.length === 0">
            <div class="orders-empty-avatar" aria-hidden="true">👤</div>
            <h3>No guests yet</h3>
            <p>Once guests buy tickets to your event, you'll see them here.</p>
            <button type="button" class="link-btn" (click)="goToAddGuests()">Add Guest</button>
          </div>

          <div class="orders-table-wrap" *ngIf="!guestsLoading && filteredGuestRows.length > 0">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Guest name</th>
                  <th>Ticket info</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Check-in</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let guest of filteredGuestRows"
                  (mouseenter)="hoveredGuestId = guest.id"
                  (mouseleave)="hoveredGuestId = null"
                >
                  <td><input type="checkbox" /></td>
                  <td>{{ guest.name || '—' }}</td>
                  <td>{{ guest.ticketLabel || '—' }}</td>
                  <td>{{ guest.ticketPriceLabel }}</td>
                  <td>
                    <span class="status-pill" [class.paid]="guest.statusKind === 'paid'" [class.free]="guest.statusKind === 'free'">
                      {{ guest.statusLabel }}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="checkin-circle"
                      [class.checked]="guest.checkedIn"
                      [disabled]="isGuestCheckinBusy(guest)"
                      (click)="toggleGuestCheckin(guest, $event)"
                    ></button>
                  </td>
                  <td class="guest-view-cell">
                    <button type="button" class="mini-btn primary" *ngIf="hoveredGuestId === guest.id" (click)="openGuestDetail(guest)">
                      View
                    </button>
                    <button type="button" class="chevron-btn" *ngIf="hoveredGuestId !== guest.id" (click)="openGuestDetail(guest)">›</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section class="guests-page guest-detail-page" *ngIf="activeTab === 'guests' && guestDetailOpen && selectedGuest">
        <ng-container *ngIf="selectedGuest as guest">
        <header class="schedule-setup-head">
          <div class="schedule-setup-head-left">
            <button type="button" class="icon-btn back-icon" aria-label="Back" (click)="closeGuestDetail()">‹</button>
            <h2>{{ guest.name }}</h2>
          </div>
        </header>

        <article class="panel guests-add-card">
          <header class="settings-card-head">
            <h2>Ticket info</h2>
            <span class="status-pill" [class.paid]="guest.statusKind === 'paid'" [class.free]="guest.statusKind === 'free'">
              {{ guest.statusLabel }}
            </span>
          </header>
          <div class="guest-detail-grid">
            <div>
              <span>Ticket type</span>
              <strong>{{ guest.ticketLabel || '—' }}</strong>
            </div>
            <div>
              <span>Ticket price</span>
              <strong>{{ guest.ticketPriceLabel }}</strong>
            </div>
            <div>
              <span>Ticket number</span>
              <strong>{{ guest.ticketNumber || '—' }}</strong>
            </div>
          </div>
        </article>

        <article class="panel guests-add-card">
          <header class="settings-card-head">
            <h2>Guest info</h2>
            <div class="guest-detail-actions">
              <button type="button" class="link-btn" (click)="openGuestContactProfile(guest)" [disabled]="!guest.profileId">
                View contact profile
              </button>
              <button type="button" class="mini-btn" (click)="toggleGuestCheckin(guest)" [disabled]="isGuestCheckinBusy(guest)">
                {{ guest.checkedIn ? 'Checked in' : 'Check in' }}
              </button>
            </div>
          </header>
          <div class="guest-detail-grid">
            <div>
              <span>Name</span>
              <strong>{{ guest.name || '—' }}</strong>
            </div>
            <div>
              <span>Email</span>
              <strong>{{ guest.email || '—' }}</strong>
            </div>
            <div>
              <span>WhatsApp number</span>
              <strong>{{ guest.whatsapp || '—' }}</strong>
            </div>
          </div>
        </article>

        <article class="panel guests-add-card">
          <header class="settings-card-head">
            <h2>Buyers info</h2>
            <div class="guest-detail-actions">
              <button type="button" class="link-btn" (click)="openGuestOrder(guest)" [disabled]="!guest.orderId">View order</button>
              <button type="button" class="mini-btn" (click)="editGuestOrder(guest)" [disabled]="!guest.orderId">Edit order</button>
            </div>
          </header>
          <div class="guest-detail-grid">
            <div>
              <span>Name</span>
              <strong>{{ guest.buyerName || '—' }}</strong>
            </div>
            <div>
              <span>Email</span>
              <strong>{{ guest.buyerEmail || '—' }}</strong>
            </div>
            <div>
              <span>Order date</span>
              <strong>{{ guest.orderDate ? (guest.orderDate | date: 'MMM d, y, h:mm a') : '—' }}</strong>
            </div>
          </div>
        </article>
        </ng-container>
      </section>

      <section class="guests-add-page" *ngIf="activeTab === 'guests' && guestAddOpen">
        <header class="schedule-setup-head">
          <div class="schedule-setup-head-left">
            <button type="button" class="icon-btn back-icon" aria-label="Back" (click)="closeGuestAdd()">‹</button>
            <h2>Add Guests</h2>
          </div>
          <div class="schedule-setup-head-actions">
            <button type="button" class="mini-btn" (click)="closeGuestAdd()">Cancel</button>
            <button type="button" class="mini-btn primary" (click)="saveGuestAdd()" [disabled]="guestAddSaving">
              {{ guestAddSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </header>

        <article class="panel guests-add-card">
          <header class="settings-card-head">
            <h2>Ticket details</h2>
            <p>Add up to {{ maxTicketsPerOrder }} tickets at once.</p>
          </header>
          <div class="guest-ticket-row" *ngFor="let row of guestTicketInputRows; let i = index; trackBy: trackByGuestTicketRowId">
            <label>
              <span>Ticket type</span>
              <select [(ngModel)]="row.ticketId" (ngModelChange)="onGuestTicketRowChanged()" [ngModelOptions]="{ standalone: true }">
                <option value="" disabled>Select ticket</option>
                <option *ngFor="let ticket of ticketRows; trackBy: trackByTicketId" [value]="ticket.id">{{ ticket.name }} ({{ ticket.priceLabel }})</option>
              </select>
            </label>
            <label>
              <span>Quantity</span>
              <select [(ngModel)]="row.quantity" (ngModelChange)="onGuestTicketRowChanged()" [ngModelOptions]="{ standalone: true }">
                <option *ngFor="let qty of guestQuantityOptions" [value]="qty">{{ qty }}</option>
              </select>
            </label>
            <button type="button" class="icon-btn" aria-label="Delete row" (click)="removeGuestTicketRow(i)">🗑</button>
          </div>
          <button type="button" class="schedule-add-inline" (click)="addGuestTicketRow()">+ Add Ticket</button>
          <p class="state error" *ngIf="guestAddError">{{ guestAddError }}</p>
        </article>

        <article class="panel guests-add-card">
          <header class="settings-card-head">
            <h2>Buyer details</h2>
          </header>
          <div class="guest-attendee-fields">
            <label>
              <span>First name *</span>
              <input type="text" [(ngModel)]="guestBuyerFirstName" (ngModelChange)="onBuyerDetailsChanged()" />
            </label>
            <label>
              <span>Last name *</span>
              <input type="text" [(ngModel)]="guestBuyerLastName" (ngModelChange)="onBuyerDetailsChanged()" />
            </label>
            <label>
              <span>Email *</span>
              <input type="email" [(ngModel)]="guestBuyerEmail" (ngModelChange)="onBuyerDetailsChanged()" />
            </label>
            <label>
              <span>WhatsApp number</span>
              <input type="text" [(ngModel)]="guestBuyerWhatsapp" (ngModelChange)="onBuyerDetailsChanged()" />
            </label>
          </div>
        </article>

        <article class="panel guests-add-card">
          <header class="settings-card-head">
            <h2>Guest details</h2>
            <p>Fill out required checkout form fields for each guest.</p>
          </header>
          <div class="guest-attendee-list" *ngIf="guestAttendeeInputs.length > 0">
            <article class="guest-attendee-item" *ngFor="let attendee of guestAttendeeInputs; let idx = index; trackBy: trackByGuestAttendeeId">
              <h3>Guest {{ idx + 1 }} · {{ attendee.ticketLabel }}</h3>
              <label class="toggle-row" *ngIf="idx === 0">
                <input
                  type="checkbox"
                  [(ngModel)]="useBuyerDetailsForFirstGuest"
                  (ngModelChange)="onUseBuyerDetailsForFirstGuestChanged()"
                  [ngModelOptions]="{ standalone: true }"
                />
                <span>Use buyer details for Guest 1</span>
              </label>
              <div class="guest-attendee-fields">
                <label *ngFor="let field of fieldsForGuestAttendee(attendee); trackBy: trackByCheckoutFieldKey">
                  <span>{{ field.label }}<b *ngIf="field.required">*</b></span>
                  <select
                    *ngIf="isJciChapterField(field); else attendeeTextInput"
                    [name]="'attendee-' + attendee.id + '-' + field.key"
                    [ngModel]="getGuestAttendeeField(attendee.id, field.key)"
                    (ngModelChange)="setGuestAttendeeField(attendee.id, field.key, $event)"
                    [ngModelOptions]="{ standalone: true }"
                    [disabled]="idx === 0 && useBuyerDetailsForFirstGuest"
                  >
                    <option value="">Select chapter</option>
                    <option *ngFor="let chapter of jciChapterOptions" [value]="chapter">{{ chapter }}</option>
                  </select>
                  <ng-template #attendeeTextInput>
                    <input
                      type="text"
                      [name]="'attendee-' + attendee.id + '-' + field.key"
                      [ngModel]="getGuestAttendeeField(attendee.id, field.key)"
                      (ngModelChange)="setGuestAttendeeField(attendee.id, field.key, $event)"
                      [ngModelOptions]="{ standalone: true }"
                      [disabled]="idx === 0 && useBuyerDetailsForFirstGuest"
                    />
                  </ng-template>
                </label>
              </div>
            </article>
          </div>
          <p class="placeholder" *ngIf="guestAttendeeInputs.length === 0">Select ticket types and quantities first.</p>
        </article>

        <article class="panel guests-add-card">
          <header class="settings-card-head">
            <h2>Order status</h2>
          </header>
          <div class="checkout-mode-group">
            <label><input type="radio" name="guest_order_status" value="not_paid" [(ngModel)]="guestOrderStatus" /> Not paid</label>
            <label><input type="radio" name="guest_order_status" value="paid" [(ngModel)]="guestOrderStatus" /> Paid</label>
            <label><input type="radio" name="guest_order_status" value="free" [(ngModel)]="guestOrderStatus" /> Free</label>
          </div>
          <label class="toggle-row">
            <input type="checkbox" [(ngModel)]="guestSendTicketsByEmail" />
            <span>Send tickets via email</span>
          </label>
        </article>
      </section>

      <section class="panel" *ngIf="activeTab !== 'overview' && activeTab !== 'tickets-seating' && activeTab !== 'settings' && activeTab !== 'features' && activeTab !== 'orders' && activeTab !== 'guests'">
        <h2>{{ activeTabLabel }}</h2>
        <p class="placeholder">
          Layout scaffold is ready. We can now design this tab in detail.
        </p>
      </section>

      <section class="tickets-page" *ngIf="activeTab === 'tickets-seating'">
        <article class="panel ticket-panel">
          <header class="ticket-head">
            <h2>Tickets</h2>
            <div class="ticket-actions">
              <select>
                <option>All tickets</option>
              </select>
              <button type="button" class="icon-btn" aria-label="Refresh">↻</button>
              <button type="button" class="icon-btn" aria-label="Settings">⚙</button>
              <button type="button" class="mini-btn primary" (click)="goToTicketCreate()">+ Create Ticket</button>
            </div>
          </header>

          <div class="empty-ticket-state" *ngIf="ticketRows.length === 0">
            <div class="empty-illustration" aria-hidden="true">🎫</div>
            <h3>Create tickets for your event</h3>
            <button type="button" class="link-btn" (click)="goToTicketCreate()">+ Create Ticket</button>
          </div>

          <div class="table-wrap" *ngIf="ticketRows.length > 0">
            <div class="bulk-actions" *ngIf="hasTicketSelection">
              <span>{{ selectedTicketCount }} of {{ ticketRows.length }} selected</span>
              <button type="button" class="link-btn" (click)="clearTicketSelection()">Deselect All</button>
              <button type="button" class="link-btn" (click)="bulkToggleTicketVisibility()">
                {{ bulkVisibilityActionLabel }}
              </button>
              <button type="button" class="link-btn danger-link" (click)="bulkDeleteTickets()">Delete</button>
            </div>
            <table>
              <thead>
                <tr>
                  <th class="check-col">
                    <input
                      type="checkbox"
                      [checked]="allVisibleTicketsSelected"
                      [indeterminate]="hasTicketSelection && !allVisibleTicketsSelected"
                      (change)="toggleSelectAllTickets($event)"
                    />
                  </th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Sold</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let ticket of ticketRows" class="ticket-row">
                  <td>
                    <input
                      type="checkbox"
                      [checked]="isTicketSelected(ticket.id)"
                      (change)="toggleTicketSelection(ticket.id, $event)"
                    />
                  </td>
                  <td>{{ ticket.name }}</td>
                  <td>{{ ticket.priceLabel }}</td>
                  <td>{{ ticket.soldLabel }}</td>
                  <td>
                    <span class="status-badge">{{ ticket.statusLabel }}</span>
                    <span class="hidden-marker" *ngIf="ticket.isHidden">Hidden</span>
                  </td>
                  <td class="actions-cell">
                    <button type="button" class="row-edit-btn" (click)="editTicket(ticket, $event)">Edit</button>
                    <div class="menu-wrap">
                      <button type="button" class="icon-btn" (click)="toggleTicketMenu(ticket.id, $event)">…</button>
                      <div
                        class="ticket-menu"
                        [style.top.px]="ticketMenuTop"
                        [style.left.px]="ticketMenuLeft"
                        *ngIf="openTicketMenuId === ticket.id"
                        (click)="$event.stopPropagation()"
                      >
                        <button type="button" (click)="hideOrShowTicket(ticket)">
                          {{ ticket.status === 'hidden' ? 'Show' : 'Hide' }}
                        </button>
                        <button type="button" (click)="duplicateTicket(ticket)">Duplicate</button>
                        <button type="button" class="danger" (click)="deleteTicket(ticket)">Delete</button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <div class="confirm-overlay" *ngIf="confirmDialogOpen" (click)="closeConfirmDialog()">
        <article class="confirm-dialog" (click)="$event.stopPropagation()">
          <header class="confirm-head">
            <h3>{{ confirmDialogTitle }}</h3>
            <button type="button" class="close-btn" (click)="closeConfirmDialog()">✕</button>
          </header>
          <p class="confirm-message">{{ confirmDialogMessage }}</p>
          <div class="confirm-actions">
            <button type="button" class="mini-btn" (click)="closeConfirmDialog()" [disabled]="confirmDialogBusy">
              Cancel
            </button>
            <button
              type="button"
              class="mini-btn danger-solid"
              (click)="confirmDialogProceed()"
              [disabled]="confirmDialogBusy"
            >
              {{ confirmDialogBusy ? 'Deleting...' : confirmDialogConfirmLabel }}
            </button>
          </div>
        </article>
      </div>

    </section>
  `,
  styles: [
    `
      .workspace-page {
        max-width: 1280px;
        margin: 0 auto;
        display: grid;
        gap: 0.9rem;
      }
      .hero {
        border: 1px solid #dbe3ee;
        background: #dbeafe;
        border-radius: 12px;
      }
      .crumbs {
        padding: 0.6rem 0.95rem 0;
        color: #475569;
        font-size: 0.84rem;
      }
      .crumbs span {
        margin: 0 0.35rem;
      }
      .hero-top {
        padding: 0.25rem 0.95rem 0.75rem;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.75rem;
      }
      h1 {
        margin: 0;
        font-size: 2rem;
        color: #0f172a;
      }
      .subline {
        margin: 0.35rem 0 0;
      }
      .badge {
        display: inline-flex;
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        background: #ffffff;
        color: #1e3a8a;
        padding: 0.15rem 0.55rem;
        font-size: 0.76rem;
        font-weight: 700;
        text-transform: capitalize;
      }
      .preview-btn {
        border: 1px solid #bfdbfe;
        background: #ffffff;
        color: #1d4ed8;
        border-radius: 999px;
        padding: 0.42rem 0.85rem;
        font-weight: 700;
        cursor: pointer;
      }
      .hero-actions {
        display: flex;
        gap: 0.45rem;
        align-items: center;
      }
      .publish-btn {
        border: 1px solid #2563eb;
        background: #2563eb;
        color: #ffffff;
        border-radius: 999px;
        padding: 0.42rem 0.95rem;
        font-weight: 700;
        cursor: pointer;
      }
      .publish-btn:disabled {
        opacity: 0.7;
        cursor: default;
      }
      .tabs {
        display: flex;
        gap: 0.2rem;
        background: #ffffff;
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        padding: 0 0.55rem;
        overflow-x: auto;
      }
      .tabs a {
        text-decoration: none;
        color: #334155;
        padding: 0.72rem 0.6rem;
        border-bottom: 2px solid transparent;
        font-size: 0.92rem;
      }
      .tabs a.active {
        color: #2563eb;
        border-bottom-color: #2563eb;
      }
      .panel {
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        background: #ffffff;
        padding: 0.95rem;
      }
      h2 {
        margin: 0;
        color: #0f172a;
      }
      .event-details {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
      }
      .event-details h2 {
        padding: 0.72rem 0.85rem;
        border-bottom: 1px solid #e2e8f0;
        font-size: 0.98rem;
      }
      .event-details-row {
        display: grid;
        grid-template-columns: 64px repeat(3, minmax(0, 1fr)) auto;
        gap: 0.85rem;
        padding: 0.72rem 0.85rem;
        align-items: start;
      }
      .event-details-row img {
        width: 64px;
        height: 48px;
        border-radius: 8px;
        object-fit: cover;
        border: 1px solid #dbe3ee;
      }
      .meta-col h3 {
        margin: 0;
        color: #334155;
        font-size: 0.78rem;
      }
      .meta-col p {
        margin: 0.3rem 0 0;
        color: #0f172a;
        font-size: 0.86rem;
        line-height: 1.35;
      }
      .good-to-know-card {
        margin-top: 0.85rem;
        border: 1px solid #d7dce6;
        border-radius: 10px;
        padding: 1rem;
        background: #ffffff;
      }
      .good-to-know-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .good-to-know-head h2 {
        margin: 0;
        font-size: 2rem;
        line-height: 1.1;
      }
      .good-to-know-plus {
        width: 36px;
        height: 36px;
        border-radius: 999px;
        border: 0;
        background: #edf4ff;
        color: #2563eb;
        font-size: 2rem;
        line-height: 1;
        cursor: pointer;
      }
      .good-to-know-section {
        margin-top: 1.4rem;
      }
      .good-to-know-section h3 {
        margin: 0;
        font-size: 2rem;
        line-height: 1.1;
      }
      .good-to-know-pills {
        margin-top: 0.75rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.6rem;
      }
      .good-to-know-pills button {
        border: 1px solid #3e3a4f;
        background: #ffffff;
        color: #2d2a3b;
        border-radius: 9px;
        padding: 0.45rem 0.7rem;
        font-size: 0.92rem;
        font-weight: 500;
        cursor: pointer;
      }
      .good-to-know-note {
        margin: 0.85rem 0 0;
        color: #4b5563;
        font-size: 0.95rem;
      }
      .good-to-know-link {
        margin-top: 0.95rem;
        border: 0;
        background: transparent;
        color: #2563eb;
        font-size: 1rem;
        font-weight: 700;
        cursor: pointer;
        padding: 0;
      }
      .mini-btn {
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        background: #ffffff;
        color: #2563eb;
        font-weight: 600;
        padding: 0.38rem 0.75rem;
        cursor: pointer;
        align-self: center;
      }
      .mini-btn.primary {
        background: #2563eb;
        color: #ffffff;
        border-color: #2563eb;
      }
      .setup-panel {
        margin-top: 0.85rem;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
      }
      .setup-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.72rem 0.85rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .setup-head h2 {
        font-size: 0.98rem;
      }
      .progress {
        display: flex;
        align-items: center;
        gap: 0.55rem;
      }
      .bar {
        width: 94px;
        height: 6px;
        border-radius: 999px;
        background: #e2e8f0;
        display: inline-flex;
      }
      .bar i {
        width: 34%;
        background: #16a34a;
        border-radius: inherit;
      }
      .setup-row {
        padding: 0.72rem 0.85rem;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.7rem;
      }
      .setup-row:last-child {
        border-bottom: 0;
      }
      .setup-row h3 {
        margin: 0;
        font-size: 0.9rem;
      }
      .setup-row p {
        margin: 0.22rem 0 0;
        color: #475569;
        font-size: 0.82rem;
      }
      .overview-grid {
        margin-top: 0.85rem;
        display: grid;
        gap: 0.75rem;
        grid-template-columns: 1fr 1fr;
      }
      .stats-card {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 0.75rem;
        min-height: 350px;
      }
      .stats-card h2 {
        font-size: 0.98rem;
      }
      .trends-filters {
        margin-top: 0.7rem;
        display: grid;
        gap: 0.55rem;
        grid-template-columns: 1fr 1fr;
      }
      .empty-title {
        margin: 6rem 0 0;
        text-align: center;
        font-weight: 700;
      }
      .empty-sub {
        margin: 0.3rem 0 0;
        text-align: center;
        color: #64748b;
        font-size: 0.85rem;
      }
      .orders-strip {
        margin-top: 0.7rem;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      .orders-strip div {
        padding: 0.55rem;
        text-align: center;
      }
      .orders-strip div + div {
        border-left: 1px solid #e2e8f0;
      }
      .orders-strip strong {
        display: block;
      }
      .orders-strip span {
        color: #64748b;
        font-size: 0.78rem;
      }
      .ticket-sales {
        margin-top: 0.72rem;
        display: grid;
        gap: 0.55rem;
      }
      .ticket-item {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 0.6rem;
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
      }
      .ticket-item p {
        margin: 0;
      }
      .ticket-item span {
        color: #0f172a;
        font-weight: 700;
      }
      .orders-page {
        display: grid;
        gap: 0.75rem;
      }
      .orders-metrics-panel,
      .orders-panel {
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #ffffff;
        padding: 0;
        overflow: hidden;
      }
      .orders-metrics-strip {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
      .orders-metrics-strip div {
        padding: 0.8rem 0.85rem;
        text-align: center;
      }
      .orders-metrics-strip div + div {
        border-left: 1px solid #e2e8f0;
      }
      .orders-metrics-strip strong {
        display: block;
        font-size: 2rem;
        line-height: 1;
        color: #0f172a;
      }
      .orders-metrics-strip span {
        color: #475569;
        font-size: 0.98rem;
      }
      .orders-head {
        padding: 0.82rem 1rem;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
      }
      .orders-head h2 {
        font-size: 2rem;
      }
      .orders-head-actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .orders-more-wrap {
        position: relative;
      }
      .orders-more-menu {
        position: absolute;
        right: 0;
        top: calc(100% + 8px);
        min-width: 178px;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #ffffff;
        box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
        padding: 0.24rem;
        z-index: 1300;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }
      .orders-more-menu button {
        width: 100%;
        text-align: left;
        border: 0;
        background: transparent;
        border-radius: 8px;
        padding: 0.45rem 0.55rem;
        color: #0f172a;
        cursor: pointer;
      }
      .orders-more-menu button:hover {
        background: #eff6ff;
      }
      .orders-filter-row {
        padding: 0.82rem 1rem;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
      }
      .orders-filter-select {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        color: #334155;
      }
      .orders-filter-select select {
        min-width: 190px;
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        padding: 0.35rem 0.7rem;
        font: inherit;
        background: #ffffff;
        color: #0f172a;
      }
      .orders-search {
        position: relative;
        display: inline-flex;
        align-items: center;
      }
      .orders-search .search-icon {
        position: absolute;
        left: 0.65rem;
        color: #2563eb;
        pointer-events: none;
      }
      .orders-search input {
        width: 240px;
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        padding: 0.35rem 0.72rem 0.35rem 1.55rem;
        font: inherit;
      }
      .orders-empty {
        min-height: 305px;
        display: grid;
        place-content: center;
        justify-items: center;
        text-align: center;
        gap: 0.4rem;
        padding: 1.2rem 1rem 1.55rem;
      }
      .orders-empty-avatar {
        width: 104px;
        height: 104px;
        border-radius: 999px;
        background: #dbeafe;
        display: grid;
        place-items: center;
        font-size: 52px;
        line-height: 1;
      }
      .orders-empty h3 {
        margin: 0.3rem 0 0;
        font-size: 2.2rem;
        color: #0f172a;
      }
      .orders-empty p {
        margin: 0;
        max-width: 540px;
        color: #475569;
        font-size: 1.05rem;
      }
      .orders-table-wrap {
        overflow: auto;
      }
      .guests-page,
      .guests-add-page {
        display: grid;
        gap: 0.75rem;
      }
      .guests-checkin-head {
        display: grid;
        grid-template-columns: auto auto 1fr;
        gap: 0.5rem;
        align-items: baseline;
        text-transform: uppercase;
        font-weight: 700;
        color: #0f172a;
      }
      .guests-checkin-head strong {
        font-size: 2rem;
        line-height: 1;
      }
      .guests-checkin-head span {
        font-size: 1rem;
      }
      .guests-checkin-head em {
        justify-self: end;
        font-style: normal;
      }
      .guests-checkin-bar {
        margin-top: 0.75rem;
        height: 4px;
        border-radius: 999px;
        background: #e2e8f0;
        overflow: hidden;
      }
      .guests-checkin-bar i {
        display: block;
        height: 100%;
        background: #16a34a;
      }
      .guests-filter-group {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        flex-wrap: wrap;
      }
      .guest-view-cell {
        width: 98px;
        text-align: right;
      }
      .chevron-btn {
        border: 1px solid #bfdbfe;
        background: #ffffff;
        color: #2563eb;
        border-radius: 999px;
        width: 32px;
        height: 32px;
        cursor: pointer;
      }
      .status-pill {
        display: inline-flex;
        align-items: center;
        border: 1px solid #fb923c;
        color: #ea580c;
        background: #fff7ed;
        border-radius: 4px;
        padding: 0.1rem 0.45rem;
        font-size: 0.9rem;
        line-height: 1.25;
      }
      .status-pill.paid {
        border-color: #86efac;
        color: #166534;
        background: #dcfce7;
      }
      .status-pill.free {
        border-color: #bfdbfe;
        color: #1d4ed8;
        background: #eff6ff;
      }
      .checkin-circle {
        width: 28px;
        height: 28px;
        border-radius: 999px;
        border: 1.5px solid #16a34a;
        background: #ffffff;
        cursor: pointer;
      }
      .checkin-circle.checked {
        background: #16a34a;
        box-shadow: inset 0 0 0 4px #ffffff;
      }
      .guest-detail-page .schedule-setup-head {
        padding: 0 0.35rem;
      }
      .guest-detail-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 1rem;
        padding: 0.95rem 1rem 1.1rem;
      }
      .guest-detail-grid span {
        display: block;
        color: #475569;
        margin-bottom: 0.25rem;
      }
      .guest-detail-grid strong {
        color: #0f172a;
        font-size: 1.08rem;
      }
      .guest-detail-actions {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
      }
      .order-line-sub {
        margin-top: 0.2rem;
        font-size: 0.9rem;
        color: #64748b;
      }
      .guests-add-card {
        padding: 0;
        overflow: hidden;
      }
      .guests-add-card .settings-card-head {
        padding: 0.82rem 1rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .guests-add-card .settings-card-head p {
        margin: 0.35rem 0 0;
        color: #475569;
      }
      .guest-ticket-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 180px auto;
        gap: 0.7rem;
        align-items: end;
        padding: 0.85rem 1rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .guest-ticket-row label,
      .guest-attendee-fields label {
        display: grid;
        gap: 0.35rem;
      }
      .guest-ticket-row span,
      .guest-attendee-fields span {
        font-weight: 600;
        color: #334155;
      }
      .guest-ticket-row select,
      .guest-attendee-fields input,
      .guest-attendee-fields select {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 0.5rem 0.62rem;
        font: inherit;
        width: 100%;
      }
      .guest-attendee-fields select {
        appearance: auto;
        background-color: #ffffff;
      }
      .guest-attendee-list {
        padding: 0.9rem 1rem;
        display: grid;
        gap: 0.75rem;
      }
      .guest-attendee-item {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 0.8rem;
        background: #f8fbff;
      }
      .guest-attendee-item h3 {
        margin: 0 0 0.6rem;
        font-size: 0.92rem;
      }
      .guest-attendee-fields {
        display: grid;
        gap: 0.7rem;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        padding: 0.85rem 1rem;
      }
      .guest-attendee-fields input,
      .guest-attendee-fields select {
        pointer-events: auto;
      }
      .toggle-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 1rem 1rem;
      }
      .placeholder {
        margin: 0.75rem 0 0;
        color: #475569;
      }
      .features-page {
        display: grid;
        gap: 0.75rem;
      }
      .schedule-setup-page {
        min-height: 520px;
        display: grid;
        grid-template-rows: auto 1fr;
        gap: 0.75rem;
      }
      .schedule-setup-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.7rem;
      }
      .schedule-setup-head-left {
        display: flex;
        align-items: center;
        gap: 0.7rem;
      }
      .schedule-setup-head-actions {
        display: flex;
        align-items: center;
        gap: 0.45rem;
      }
      .schedule-setup-head h2 {
        margin: 0;
        font-size: 2.2rem;
        color: #0f172a;
      }
      .back-icon {
        width: 34px;
        height: 34px;
        border-radius: 999px;
      }
      .schedule-setup-body {
        display: grid;
        align-content: center;
        justify-items: center;
        text-align: center;
        gap: 0.65rem;
        padding: 1.4rem 1rem 2.5rem;
      }
      .schedule-setup-hero {
        width: 90px;
        height: 90px;
        border-radius: 18px;
        background: #e2e8f0;
        color: #1d4ed8;
        display: grid;
        place-items: center;
        font-size: 2rem;
      }
      .schedule-setup-body h3 {
        margin: 0.2rem 0 0;
        font-size: 2rem;
        color: #0f172a;
      }
      .schedule-setup-body p {
        margin: 0;
        max-width: 680px;
        color: #334155;
        font-size: 1.02rem;
        line-height: 1.45;
      }
      .schedule-list-panel {
        padding: 0;
        overflow: hidden;
      }
      .schedule-list-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.8rem;
        padding: 0.95rem 1rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .schedule-list-head h3 {
        margin: 0;
        font-size: 2rem;
        color: #0f172a;
      }
      .schedule-list-head p {
        margin: 0.3rem 0 0;
        color: #334155;
      }
      .schedule-list {
        padding-bottom: 0.85rem;
      }
      .schedule-day-title {
        padding: 0.74rem 1rem;
        font-weight: 700;
        font-size: 1.02rem;
        color: #0f172a;
        border-top: 1px solid #e2e8f0;
      }
      .schedule-item-row {
        display: grid;
        grid-template-columns: 170px 1fr auto;
        gap: 0.75rem;
        align-items: center;
        padding: 0.74rem 1rem;
        border-top: 1px solid #e2e8f0;
      }
      .schedule-time {
        font-weight: 700;
        color: #0f172a;
      }
      .schedule-name {
        color: #0f172a;
        font-weight: 600;
      }
      .schedule-row-actions {
        display: flex;
        align-items: center;
        gap: 0.45rem;
        position: relative;
      }
      .schedule-menu-wrap {
        position: relative;
      }
      .schedule-item-menu {
        position: absolute;
        right: 0;
        top: calc(100% + 6px);
        min-width: 128px;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #ffffff;
        box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
        z-index: 1200;
        padding: 0.24rem;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }
      .schedule-item-menu button {
        width: 100%;
        display: block;
        text-align: left;
        border: 0;
        background: transparent;
        color: #0f172a;
        padding: 0.42rem 0.5rem;
        border-radius: 8px;
        cursor: pointer;
      }
      .schedule-item-menu button:hover {
        background: #eff6ff;
      }
      .schedule-item-menu .danger {
        color: #b91c1c;
      }
      .canceled-marker {
        display: inline-flex;
        border-radius: 4px;
        background: #fde68a;
        color: #854d0e;
        font-weight: 700;
        font-size: 0.78rem;
        padding: 0.15rem 0.45rem;
      }
      .hidden-eye {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        color: #334155;
      }
      .hidden-eye::after {
        content: '';
        position: absolute;
        width: 18px;
        height: 1.6px;
        background: #0f172a;
        transform: rotate(-28deg);
      }
      .schedule-add-inline {
        margin: 0.75rem 1rem 0;
        width: calc(100% - 2rem);
        border: 1px dashed #60a5fa;
        border-radius: 10px;
        background: #ffffff;
        color: #2563eb;
        font-weight: 700;
        padding: 0.7rem;
        cursor: pointer;
      }
      .schedule-add-inline:hover {
        background: #f8fbff;
      }
      .features-panel {
        padding: 0.9rem;
      }
      .features-grid {
        margin-top: 0.8rem;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
      }
      .features-using-list {
        margin-top: 0.78rem;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        overflow: visible;
      }
      .features-using-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.8rem;
        padding: 0.8rem 0.9rem;
        border-top: 1px solid #e2e8f0;
      }
      .features-using-row:first-child {
        border-top: 0;
      }
      .using-left {
        display: flex;
        align-items: center;
        gap: 0.6rem;
      }
      .using-left h3 {
        margin: 0;
        font-size: 1.02rem;
        color: #0f172a;
      }
      .using-left p {
        margin: 0.2rem 0 0;
        color: #334155;
        font-size: 0.9rem;
      }
      .using-check {
        color: #16a34a;
        font-weight: 700;
      }
      .using-actions {
        display: flex;
        align-items: center;
        gap: 0.45rem;
      }
      .using-menu-wrap {
        position: relative;
      }
      .using-row-menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        min-width: 140px;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #ffffff;
        box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
        z-index: 1200;
        padding: 0.24rem;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }
      .using-row-menu button {
        width: 100%;
        display: block;
        text-align: left;
        border: 0;
        background: transparent;
        color: #0f172a;
        padding: 0.42rem 0.5rem;
        border-radius: 8px;
        cursor: pointer;
      }
      .using-row-menu button:hover {
        background: #eff6ff;
      }
      .using-row-menu .danger {
        color: #b91c1c;
      }
      .feature-card {
        border: 1px solid #bfdbfe;
        border-radius: 10px;
        padding: 0.85rem;
        display: grid;
        grid-template-columns: 52px 1fr;
        gap: 0.65rem;
        align-items: start;
        background: #ffffff;
      }
      .feature-icon {
        width: 52px;
        height: 52px;
        border-radius: 10px;
        background: #eff6ff;
        color: #1d4ed8;
        display: grid;
        place-items: center;
        font-size: 1.35rem;
      }
      .feature-body h3 {
        margin: 0;
        font-size: 1.05rem;
        color: #0f172a;
      }
      .feature-body p {
        margin: 0.25rem 0 0.5rem;
        color: #334155;
        font-size: 0.92rem;
      }
      .feature-actions-row {
        display: flex;
        gap: 0.55rem;
        align-items: center;
      }
      .feature-body .link-btn[disabled] {
        opacity: 0.6;
        cursor: not-allowed;
      }
      .video-config-dialog {
        width: min(520px, 100%);
      }
      .schedule-item-dialog {
        width: min(760px, 100%);
      }
      .confirm-dialog.lineup-dialog {
        width: min(980px, 100%);
        max-height: calc(100vh - 2rem);
        overflow: auto;
      }
      .lineup-intro {
        margin: 0;
        padding: 0 0.9rem;
        color: #475569;
      }
      .lineup-list {
        padding: 0.9rem;
        display: grid;
        gap: 0.8rem;
      }
      .lineup-item {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 0.8rem;
        display: grid;
        grid-template-columns: 280px minmax(320px, 1fr);
        gap: 0.9rem;
      }
      .lineup-item-media {
        display: grid;
        justify-items: center;
        gap: 0.45rem;
      }
      .lineup-image-btn {
        width: 230px;
        height: 230px;
        border-radius: 999px;
        border: 0;
        background: transparent;
        overflow: hidden;
        display: grid;
        place-items: center;
        text-align: center;
        color: #334155;
        cursor: pointer;
        padding: 0;
      }
      .lineup-image-btn.empty {
        border: 1px solid #dbe3ee;
        background: #f8fafc;
      }
      .lineup-image-btn img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
      }
      .lineup-item-fields {
        display: grid;
        gap: 0.55rem;
      }
      .lineup-item-fields label {
        display: grid;
        gap: 0.32rem;
      }
      .lineup-item-fields span {
        color: #334155;
        font-size: 0.84rem;
        font-weight: 600;
      }
      .lineup-item-fields input,
      .lineup-item-fields textarea {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 0.5rem 0.58rem;
        font-size: 0.84rem;
        width: 100%;
        box-sizing: border-box;
      }
      .lineup-item-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: #64748b;
        font-size: 0.8rem;
      }
      .lineup-add-row {
        padding: 0 0.9rem 0.9rem;
        display: flex;
        justify-content: center;
      }
      .lineup-crop-dialog {
        width: min(900px, 100%);
      }
      .lineup-crop-stage {
        padding: 0.9rem;
      }
      .lineup-crop-frame {
        position: relative;
        width: 100%;
        aspect-ratio: var(--lineup-crop-ratio, 1 / 1);
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
        background: #e2e8f0;
      }
      .lineup-crop-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .lineup-crop-selection {
        position: absolute;
        border: 1px dashed #ffffff;
        box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.28);
        cursor: move;
      }
      .lineup-crop-handle {
        position: absolute;
        width: 14px;
        height: 14px;
        border: 1px solid #ffffff;
        background: #ffffff;
      }
      .lineup-crop-handle.nw { top: -7px; left: -7px; cursor: nwse-resize; }
      .lineup-crop-handle.ne { top: -7px; right: -7px; cursor: nesw-resize; }
      .lineup-crop-handle.sw { bottom: -7px; left: -7px; cursor: nesw-resize; }
      .lineup-crop-handle.se { bottom: -7px; right: -7px; cursor: nwse-resize; }
      .video-config-body {
        padding: 0.9rem;
        display: grid;
        gap: 0.72rem;
      }
      .schedule-item-body {
        padding: 0.9rem;
        display: grid;
        gap: 0.7rem;
      }
      .schedule-item-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.65rem;
      }
      .schedule-item-body label {
        display: grid;
        gap: 0.34rem;
      }
      .schedule-item-body span {
        color: #334155;
        font-size: 0.84rem;
        font-weight: 600;
      }
      .schedule-item-body input,
      .schedule-item-body textarea {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 0.5rem 0.58rem;
        font-size: 0.84rem;
      }
      .video-config-body label {
        display: grid;
        gap: 0.35rem;
      }
      .video-config-body span {
        color: #334155;
        font-size: 0.84rem;
        font-weight: 600;
      }
      .video-config-body input,
      .video-config-body select {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 0.5rem 0.58rem;
        font-size: 0.84rem;
      }
      .video-config-error {
        margin: 0 0.9rem;
      }
      .settings-page {
        display: grid;
        gap: 0.75rem;
      }
      .checkout-summary,
      .checkout-editor {
        padding: 0;
        overflow: hidden;
      }
      .settings-card-head,
      .checkout-editor-head {
        padding: 0.72rem 0.8rem;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.6rem;
      }
      .settings-row {
        display: grid;
        grid-template-columns: 240px 1fr;
        gap: 0.75rem;
        padding: 0.66rem 0.8rem;
        border-bottom: 1px solid #e2e8f0;
        color: #334155;
        font-size: 0.84rem;
      }
      .settings-row:last-child {
        border-bottom: 0;
      }
      .checkout-editor-actions {
        display: flex;
        align-items: center;
        gap: 0.45rem;
      }
      .checkout-section {
        border-bottom: 1px solid #e2e8f0;
        padding: 0.72rem 0.8rem;
      }
      .checkout-section h3,
      .checkout-grid h3 {
        margin: 0;
        font-size: 0.9rem;
      }
      .ticket-settings-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.7rem;
      }
      .ticket-settings-grid label {
        display: grid;
        gap: 0.36rem;
      }
      .ticket-settings-grid span {
        font-size: 0.8rem;
        color: #334155;
        font-weight: 600;
      }
      .ticket-settings-grid input {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 0.48rem 0.56rem;
        font-size: 0.84rem;
      }
      .settings-note {
        margin: 0.65rem 0 0;
        color: #64748b;
        font-size: 0.78rem;
      }
      .checkout-mode-group {
        margin-top: 0.75rem;
        display: grid;
        gap: 0.55rem;
      }
      .ticket-form-selector {
        margin-top: 0.7rem;
        display: grid;
        gap: 0.35rem;
        max-width: 320px;
      }
      .ticket-form-selector label {
        font-size: 0.8rem;
        color: #334155;
        font-weight: 600;
      }
      .ticket-form-selector select {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        background: #ffffff;
        padding: 0.46rem 0.56rem;
        font-size: 0.84rem;
      }
      .mode-option {
        display: flex;
        align-items: center;
        gap: 0.45rem;
        color: #0f172a;
        font-size: 0.84rem;
      }
      .checkout-grid {
        padding: 0.72rem 0.8rem 0.82rem;
        display: grid;
        grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.9fr);
        gap: 0.75rem;
      }
      .checkout-config,
      .checkout-preview {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: visible;
      }
      .checkout-config h3,
      .checkout-preview h3 {
        padding: 0.68rem 0.72rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .checkout-block {
        padding: 0.68rem 0.72rem;
        border-bottom: 1px solid #e2e8f0;
        display: grid;
        gap: 0.5rem;
      }
      .checkout-block:last-child {
        border-bottom: 0;
      }
      .checkout-block label {
        font-size: 0.8rem;
        color: #334155;
        font-weight: 600;
      }
      .checkout-block input {
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 0.48rem 0.56rem;
        font-size: 0.84rem;
      }
      .checkout-fields-list {
        list-style: none;
        margin: 0;
        padding: 0;
        border: 1px solid #dbe3ee;
        border-radius: 9px;
        overflow: hidden;
      }
      .checkout-fields-list li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.65rem;
        padding: 0.52rem 0.56rem;
        border-top: 1px solid #e2e8f0;
      }
      .checkout-fields-list li:first-child {
        border-top: 0;
      }
      .checkout-fields-list strong {
        font-size: 0.84rem;
      }
      .checkout-fields-list p {
        margin: 0.15rem 0 0;
        font-size: 0.75rem;
        color: #64748b;
      }
      .icon-btn.tiny {
        width: 24px;
        height: 24px;
        font-size: 0.74rem;
      }
      .locked-field-marker {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        font-size: 0.72rem;
        color: #64748b;
      }
      .add-field-wrap {
        position: relative;
        display: inline-block;
      }
      .add-field-menu {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        min-width: 180px;
        background: #ffffff;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
        z-index: 1300;
        padding: 0.3rem;
        display: grid;
        gap: 0.18rem;
      }
      .add-field-menu button {
        border: 0;
        background: transparent;
        text-align: left;
        border-radius: 7px;
        padding: 0.4rem 0.46rem;
        font-size: 0.82rem;
        cursor: pointer;
      }
      .add-field-menu button:hover {
        background: #eff6ff;
      }
      .checkout-preview {
        background: #f6f8fb;
      }
      .preview-canvas {
        padding: 0.72rem;
        background: #dbe6f1;
      }
      .preview-card {
        background: #ffffff;
        border-radius: 4px;
        border: 1px solid #dbe3ee;
        padding: 0.76rem 0.8rem;
        display: grid;
        gap: 0.5rem;
        max-width: 340px;
      }
      .preview-card h4 {
        margin: 0 0 0.25rem;
        font-size: 0.82rem;
        font-weight: 700;
        color: #334155;
      }
      .preview-grid-two {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.5rem;
      }
      .preview-field {
        display: grid;
        gap: 0.2rem;
      }
      .preview-field span {
        font-size: 0.72rem;
        color: #334155;
      }
      .preview-field input {
        width: 100%;
        box-sizing: border-box;
        border: 1px solid #cbd5e1;
        border-radius: 0;
        background: #ffffff;
        height: 20px;
        padding: 0 0.3rem;
      }
      .preview-submit {
        border: 0;
        border-radius: 0;
        background: #2b2f4b;
        color: #ffffff;
        padding: 0.36rem 0.62rem;
        font-size: 0.72rem;
        font-weight: 500;
      }
      .tickets-page {
        display: grid;
        gap: 0.75rem;
      }
      .ticket-panel {
        padding: 0;
        overflow: hidden;
      }
      .ticket-head {
        padding: 0.72rem 0.8rem;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.6rem;
      }
      .ticket-head h2 {
        font-size: 0.98rem;
      }
      .ticket-actions {
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }
      .ticket-actions select {
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        padding: 0.28rem 0.6rem;
        background: #ffffff;
      }
      .icon-btn {
        width: 30px;
        height: 30px;
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        background: #ffffff;
        color: #1d4ed8;
        cursor: pointer;
      }
      .empty-ticket-state {
        padding: 2rem 1rem 2.2rem;
        text-align: center;
      }
      .empty-illustration {
        font-size: 42px;
        line-height: 1;
      }
      .empty-ticket-state h3 {
        margin: 0.65rem 0 0;
      }
      .empty-ticket-state p {
        margin: 0.35rem 0 0.8rem;
        color: #475569;
      }
      .link-btn {
        border: 0;
        background: transparent;
        color: #2563eb;
        font-weight: 700;
        cursor: pointer;
      }
      .table-wrap {
        overflow-x: auto;
      }
      .bulk-actions {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        padding: 0.58rem 0.65rem;
        border-top: 1px solid #e2e8f0;
        border-bottom: 1px solid #e2e8f0;
        background: #f8fbff;
        color: #334155;
        font-size: 0.82rem;
      }
      .bulk-actions .link-btn {
        font-size: 0.82rem;
      }
      .danger-link {
        color: #b91c1c;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th,
      td {
        padding: 0.56rem 0.65rem;
        border-top: 1px solid #e2e8f0;
        text-align: left;
      }
      th {
        background: #dbeafe;
        color: #1e293b;
        font-size: 0.82rem;
      }
      .check-col {
        width: 34px;
      }
      .status-badge {
        display: inline-flex;
        border-radius: 999px;
        padding: 0.12rem 0.45rem;
        background: #dcfce7;
        color: #166534;
        font-size: 0.76rem;
        font-weight: 700;
      }
      .hidden-marker {
        display: inline-flex;
        margin-left: 0.34rem;
        border-radius: 999px;
        padding: 0.12rem 0.45rem;
        background: #e2e8f0;
        color: #334155;
        font-size: 0.74rem;
        font-weight: 700;
      }
      .actions-cell {
        width: 140px;
        text-align: right;
        white-space: nowrap;
      }
      .ticket-row .row-edit-btn {
        opacity: 0;
        visibility: hidden;
        margin-right: 0.35rem;
        border: 1px solid #bfdbfe;
        border-radius: 999px;
        background: #ffffff;
        color: #2563eb;
        font-size: 0.76rem;
        font-weight: 700;
        padding: 0.2rem 0.55rem;
        transition: opacity 0.12s ease;
      }
      .ticket-row:hover .row-edit-btn,
      .ticket-row:focus-within .row-edit-btn {
        opacity: 1;
        visibility: visible;
      }
      .menu-wrap {
        position: static;
        display: inline-block;
      }
      .ticket-menu {
        position: fixed;
        min-width: 124px;
        border: 1px solid #dbe3ee;
        border-radius: 10px;
        background: #ffffff;
        box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
        z-index: 1200;
        padding: 0.24rem;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }
      .ticket-menu button {
        width: 100%;
        display: block;
        text-align: left;
        border: 0;
        background: transparent;
        color: #0f172a;
        padding: 0.42rem 0.5rem;
        border-radius: 8px;
        cursor: pointer;
      }
      .ticket-menu button:hover {
        background: #eff6ff;
      }
      .ticket-menu .danger {
        color: #b91c1c;
      }
      .state {
        margin: 0;
        color: #475569;
      }
      .error {
        color: #b91c1c;
      }
      .confirm-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.36);
        display: grid;
        place-items: center;
        z-index: 1800;
        padding: 1rem;
      }
      .confirm-dialog {
        width: min(420px, 100%);
        border: 1px solid #dbe3ee;
        border-radius: 12px;
        background: #ffffff;
        box-shadow: 0 20px 40px rgba(15, 23, 42, 0.24);
        overflow: hidden;
      }
      .confirm-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.8rem 0.9rem;
        border-bottom: 1px solid #e2e8f0;
      }
      .confirm-head h3 {
        margin: 0;
        font-size: 1rem;
        color: #0f172a;
      }
      .close-btn {
        border: 0;
        background: transparent;
        color: #475569;
        cursor: pointer;
      }
      .confirm-message {
        margin: 0;
        padding: 0.9rem;
        color: #334155;
        font-size: 0.84rem;
        line-height: 1.45;
      }
      .confirm-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.45rem;
        padding: 0 0.9rem 0.9rem;
      }
      .danger-solid {
        background: #dc2626;
        border-color: #dc2626;
        color: #ffffff;
      }
      @media (max-width: 900px) {
        .event-details-row,
        .overview-grid,
        .trends-filters,
        .checkout-grid,
        .features-grid,
        .schedule-item-grid,
        .lineup-item {
          grid-template-columns: 1fr;
        }
        .features-using-row {
          flex-direction: column;
          align-items: flex-start;
        }
        .ticket-settings-grid {
          grid-template-columns: 1fr;
        }
        .orders-metrics-strip {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .orders-filter-row {
          flex-direction: column;
          align-items: flex-start;
        }
        .orders-search input {
          width: 100%;
          min-width: 220px;
        }
        .guests-checkin-head {
          grid-template-columns: 1fr;
        }
        .guest-ticket-row,
        .guest-attendee-fields {
          grid-template-columns: 1fr;
        }
        .guest-detail-grid {
          grid-template-columns: 1fr;
        }
        .settings-row {
          grid-template-columns: 1fr;
        }
      }
    `
  ]
})
export class EventWorkspaceComponent implements OnInit, OnDestroy {
  readonly tabs: WorkspaceTab[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'tickets-seating', label: 'Tickets & seating' },
    { key: 'settings', label: 'Settings' },
    { key: 'features', label: 'Features' },
    { key: 'orders', label: 'Orders' },
    { key: 'guests', label: 'Guests' }
  ];
  readonly jciChapterOptions = [...JCI_CHAPTER_OPTIONS];

  eventId = '';
  private canonicalEventIdForWrite: string | number | null = null;
  activeTab: WorkspaceTabKey = 'overview';
  eventTitle = '';
  eventStatus = '';
  eventLocation = '';
  eventType = '';
  eventImageUrl = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="48"><rect width="100%" height="100%" fill="%23dbeafe"/></svg>';
  ticketRows: TicketRow[] = [];
  orderRows: OrderRow[] = [];
  ordersLoading = false;
  ordersMoreMenuOpen = false;
  ordersFilter: OrderFilter = 'all';
  ordersSearch = '';
  selectedOrderIds = new Set<string>();
  orderDetailOpen = false;
  orderDetailId = '';
  selectedOrder: OrderRow | null = null;
  hoveredOrderId: string | null = null;
  markOrderPaidBusy = false;
  guestRows: GuestRow[] = [];
  guestsLoading = false;
  guestsMoreMenuOpen = false;
  guestStatusFilter: GuestStatusFilter = 'all';
  guestTicketFilter = 'all';
  guestSearch = '';
  guestAddOpen = false;
  guestDetailOpen = false;
  guestDetailId = '';
  hoveredGuestId: string | null = null;
  selectedGuest: GuestRow | null = null;
  guestCheckinBusyId: string | null = null;
  guestAddError = '';
  guestTicketInputRows: GuestTicketInputRow[] = [{ id: 1, ticketId: '', quantity: 1 }];
  guestAttendeeInputs: GuestTicketAttendeeInput[] = [];
  guestBuyerFirstName = '';
  guestBuyerLastName = '';
  guestBuyerEmail = '';
  guestBuyerWhatsapp = '';
  useBuyerDetailsForFirstGuest = false;
  guestOrderStatus: OrderTicketStatus = 'not_paid';
  guestSendTicketsByEmail = true;
  guestAddSaving = false;
  private nextGuestTicketInputRowId = 2;
  private nextGuestAttendeeInputId = 1;
  overviewTrendType: 'net_sales' | 'tickets_sold' = 'net_sales';
  overviewTrendDays = 30;
  startDateLabel = '—';
  private eventStartAt: Date | null = null;
  error = '';
  publishing = false;
  openTicketMenuId: string | null = null;
  openUsingFeatureMenuId: string | null = null;
  ticketMenuTop = 0;
  ticketMenuLeft = 0;
  selectedTicketIds = new Set<string>();
  isCheckoutFormEditing = false;
  checkoutMode: CheckoutMode = 'per_order';
  checkoutModeDraft: CheckoutMode = 'per_order';
  checkoutButtonText = 'Continue';
  checkoutButtonTextDraft = 'Continue';
  checkoutCurrency = 'IDR';
  private checkoutSettingsId = '';
  checkoutFields: CheckoutFieldOption[] = DEFAULT_CHECKOUT_FIELDS.map((field) => ({ ...field }));
  checkoutFieldsDraft: CheckoutFieldOption[] = [];
  checkoutFieldsByTicket: Record<string, CheckoutFieldOption[]> = {};
  checkoutFieldsByTicketDraft: Record<string, CheckoutFieldOption[]> = {};
  selectedCheckoutTicketId = '';
  selectedCheckoutTicketIdDraft = '';
  showAddFieldMenu = false;
  featureCards: EventFeatureCard[] = [];
  featuresLoading = false;
  featuresError = '';
  featureBusyIds = new Set<string>();
  scheduleSetupOpen = false;
  scheduleSetupFeatureId = '';
  scheduleItemDialogOpen = false;
  scheduleItemDialogError = '';
  private editingScheduleDraftId: number | null = null;
  private nextScheduleDraftId = 1;
  scheduleItemNameDraft = '';
  scheduleStartDateDraft = '';
  scheduleStartTimeDraft = '';
  scheduleEndDateDraft = '';
  scheduleEndTimeDraft = '';
  schedulePlaceDraft = '';
  scheduleTagsDraft = '';
  scheduleDescriptionDraft = '';
  scheduleItemsDraft: ScheduleItemDraft[] = [];
  openScheduleItemMenuId: number | null = null;
  videoConfigDialogOpen = false;
  videoConfigSaving = false;
  videoConfigIsNew = false;
  videoConfigError = '';
  videoProviderDraft: 'zoom' | 'google_meet' = 'zoom';
  videoUrlDraft = '';
  activeVideoFeatureId = '';
  lineupDialogOpen = false;
  lineupSaving = false;
  lineupError = '';
  activeLineupFeatureId = '';
  lineupSpeakersDraft: LineupSpeakerDraft[] = [];
  lineupPendingImageTargetId = '';
  lineupCropDialogOpen = false;
  lineupCropDialogTargetSpeakerId = '';
  lineupCropDialogImageUrl = '';
  lineupCropDragActive = false;
  lineupCropFrameAspectRatio = 1;
  lineupCropBoxX = 0;
  lineupCropBoxY = 0;
  lineupCropBoxWidth = 100;
  lineupCropBoxHeight = 100;
  private lineupCropResizeMode: '' | 'move' | 'resize' = '';
  private lineupCropResizeHandle: '' | 'nw' | 'ne' | 'sw' | 'se' = '';
  private lineupCropDragStartX = 0;
  private lineupCropDragStartY = 0;
  private lineupCropDragOriginBoxX = 0;
  private lineupCropDragOriginBoxY = 0;
  private lineupCropDragOriginBoxWidth = 100;
  private lineupCropDragOriginBoxHeight = 100;
  private lineupCropDragFrameWidth = 1;
  private lineupCropDragFrameHeight = 1;
  private lineupCropImageBounds = { x: 0, y: 0, width: 100, height: 100 };
  isRegistrationSettingsEditing = false;
  registrationStatus: 'open' | 'closed' = 'open';
  registrationStatusDraft: 'open' | 'closed' = 'open';
  isTicketSettingsEditing = false;
  maxTicketsPerOrder = 50;
  maxTicketsPerOrderDraft = 50;
  checkoutTimeLimitMinutes = 20;
  checkoutTimeLimitMinutesDraft = 20;
  confirmDialogOpen = false;
  confirmDialogTitle = '';
  confirmDialogMessage = '';
  confirmDialogConfirmLabel = 'Confirm';
  confirmDialogBusy = false;
  private confirmAction: 'delete-ticket' | 'bulk-delete-tickets' | 'cancel-schedule-item' | null = null;
  private pendingDeleteTicket: TicketRow | null = null;
  private pendingBulkDeleteIds: string[] = [];
  private pendingCancelScheduleItemId: number | null = null;
  readonly availableCheckoutFields: CheckoutFieldOption[] = [
    { key: 'phone_number', label: 'Phone Number', required: false },
    { key: 'address', label: 'Address', required: false },
    { key: 'date', label: 'Date', required: false },
    { key: 'comments', label: 'Comments', required: false },
    { key: 'custom_question', label: 'Custom Question', required: false }
  ];
  private routeSub?: Subscription;
  private readonly directusBase = environment.directusUrl.replace(/\/$/, '');
  private readonly scheduleDraftStoragePrefix = 'event_schedule_draft_';
  private readonly displayTimezone = 'Asia/Jakarta';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly directus: DirectusService
  ) {}

  async ngOnInit(): Promise<void> {
    this.routeSub = this.route.paramMap.subscribe(() => {
      void this.handleRoute();
    });
  }

  get activeTabLabel(): string {
    return this.tabs.find((tab) => tab.key === this.activeTab)?.label || 'Event';
  }

  get isDraft(): boolean {
    return this.eventStatus.trim().toLowerCase() === 'draft';
  }

  get eventTypeLabel(): string {
    if (!this.eventType) return 'Ticketed';
    return this.eventType.charAt(0).toUpperCase() + this.eventType.slice(1);
  }

  get selectedTicketCount(): number {
    return this.selectedTicketIds.size;
  }

  get hasTicketSelection(): boolean {
    return this.selectedTicketCount > 0;
  }

  get allVisibleTicketsSelected(): boolean {
    return this.ticketRows.length > 0 && this.selectedTicketCount === this.ticketRows.length;
  }

  get checkoutModeLabel(): string {
    return this.checkoutMode === 'per_ticket' ? '1 form per ticket' : '1 form per order';
  }

  get registrationStatusLabel(): string {
    return this.registrationStatus === 'closed' ? 'Closed' : 'Open';
  }

  get checkoutFieldsSummary(): string {
    if (this.checkoutMode === 'per_ticket') {
      const ticketCount = this.checkoutTicketTargets.length;
      return ticketCount
        ? `${ticketCount} ticket-specific form${ticketCount > 1 ? 's' : ''}`
        : 'No ticket-specific forms configured';
    }
    const labels = this.checkoutFields.map((field) => field.label);
    if (!labels.length) return 'No fields configured';
    return `${labels.length} field${labels.length > 1 ? 's' : ''}: ${labels.join(', ')}`;
  }

  get activeCheckoutFieldsDraft(): CheckoutFieldOption[] {
    if (this.checkoutModeDraft === 'per_ticket' && this.selectedCheckoutTicketIdDraft) {
      return this.checkoutFieldsByTicketDraft[this.selectedCheckoutTicketIdDraft] || [];
    }
    return this.checkoutFieldsDraft;
  }

  get previewAdditionalFields(): CheckoutFieldOption[] {
    return this.activeCheckoutFieldsDraft.filter(
      (field) => !['first_name', 'last_name', 'email'].includes(field.key)
    );
  }

  get checkoutTicketTargets(): TicketRow[] {
    return this.ticketRows;
  }

  get selectedFeatureCards(): EventFeatureCard[] {
    return this.featureCards.filter((feature) => feature.selected);
  }

  get availableFeatureCards(): EventFeatureCard[] {
    return this.featureCards.filter((feature) => !feature.selected);
  }

  get groupedScheduleItems(): ScheduleDayGroup[] {
    const sorted = [...this.scheduleItemsDraft].sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    const groups = new Map<string, ScheduleItemDraft[]>();
    for (const item of sorted) {
      const key = this.toDateInput(item.startAt);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(item);
    }
    return Array.from(groups.entries()).map(([key, items]) => ({
      label: this.formatScheduleDayLabel(key),
      items
    }));
  }

  get bulkVisibilityActionLabel(): string {
    const selectedRows = this.ticketRows.filter((row) => this.selectedTicketIds.has(row.id));
    if (!selectedRows.length) return 'Hide Tickets';
    const allHidden = selectedRows.every((row) => row.status.trim().toLowerCase() === 'hidden');
    return allHidden ? 'Show Tickets' : 'Hide Tickets';
  }

  get filteredOrderRows(): OrderRow[] {
    const term = this.ordersSearch.trim().toLowerCase();
    return this.orderRows.filter((row) => {
      if (this.ordersFilter !== 'all' && row.status !== this.ordersFilter) return false;
      if (!term) return true;
      return (
        row.numberLabel.toLowerCase().includes(term) ||
        row.buyerName.toLowerCase().includes(term) ||
        row.statusLabel.toLowerCase().includes(term)
      );
    });
  }

  get ordersNetSalesLabel(): string {
    return this.formatIdr(this.filteredOrderRows.reduce((sum, row) => sum + Math.max(0, row.totalAmount), 0));
  }

  get ordersTotalSalesLabel(): string {
    return this.ordersNetSalesLabel;
  }

  get ordersTicketsSoldCount(): number {
    return this.filteredOrderRows.reduce((sum, row) => sum + Math.max(0, row.ticketCount), 0);
  }

  get settledOrderRows(): OrderRow[] {
    return this.orderRows.filter((row) => row.status === 'paid' || row.status === 'free');
  }

  get overviewNetSalesAmount(): number {
    return this.settledOrderRows.reduce((sum, row) => sum + Math.max(0, row.totalAmount), 0);
  }

  get overviewTotalSalesAmount(): number {
    return this.overviewNetSalesAmount;
  }

  get overviewTicketsSoldCount(): number {
    return this.settledOrderRows.reduce((sum, row) => sum + Math.max(0, row.ticketCount), 0);
  }

  get overviewTrendRows(): OrderRow[] {
    const now = Date.now();
    const windowMs = Math.max(1, this.overviewTrendDays) * 24 * 60 * 60 * 1000;
    const startTs = now - windowMs;
    return this.settledOrderRows.filter((row) => {
      const created = row.createdAt?.getTime() || 0;
      return created >= startTs;
    });
  }

  get overviewTrendNetSalesAmount(): number {
    return this.overviewTrendRows.reduce((sum, row) => sum + Math.max(0, row.totalAmount), 0);
  }

  get overviewTrendTicketsSoldCount(): number {
    return this.overviewTrendRows.reduce((sum, row) => sum + Math.max(0, row.ticketCount), 0);
  }

  get overviewTrendHasData(): boolean {
    if (this.overviewTrendType === 'tickets_sold') return this.overviewTrendTicketsSoldCount > 0;
    return this.overviewTrendNetSalesAmount > 0;
  }

  get selectedOrderCount(): number {
    return this.selectedOrderIds.size;
  }

  get hasOrderSelection(): boolean {
    return this.selectedOrderCount > 0;
  }

  get allVisibleOrdersSelected(): boolean {
    return this.filteredOrderRows.length > 0 && this.filteredOrderRows.every((row) => this.selectedOrderIds.has(row.id));
  }

  get checkedInGuestsCount(): number {
    return this.guestRows.filter((row) => row.checkedIn).length;
  }

  get guestsArrivedPercent(): number {
    if (!this.guestRows.length) return 0;
    return Math.round((this.checkedInGuestsCount / this.guestRows.length) * 1000) / 10;
  }

  get guestsArrivedPercentLabel(): string {
    return `${this.guestsArrivedPercent.toFixed(1)}%`;
  }

  get filteredGuestRows(): GuestRow[] {
    const term = this.guestSearch.trim().toLowerCase();
    return this.guestRows.filter((row) => {
      if (this.guestStatusFilter === 'checked_in' && !row.checkedIn) return false;
      if (this.guestStatusFilter === 'not_arrived' && row.checkedIn) return false;
      if (this.guestTicketFilter !== 'all' && row.ticketId !== this.guestTicketFilter) return false;
      if (!term) return true;
      return (
        row.name.toLowerCase().includes(term) ||
        row.email.toLowerCase().includes(term) ||
        row.ticketLabel.toLowerCase().includes(term)
      );
    });
  }

  get guestQuantityOptions(): number[] {
    const cap = Math.max(1, this.maxTicketsPerOrder || 1);
    return Array.from({ length: cap }, (_, idx) => idx + 1);
  }

  goBackToList(): void {
    void this.router.navigateByUrl('/events/published');
  }

  goToTicketCreate(): void {
    if (!this.eventId) return;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/tickets/new`);
  }

  editEventDetails(): void {
    if (!this.eventId) return;
    void this.router.navigateByUrl(`/events/new?eventId=${encodeURIComponent(this.eventId)}&returnTab=overview`);
  }

  goToSettingsTab(): void {
    if (!this.eventId) return;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/settings`);
  }

  toggleOrdersMoreMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.ordersMoreMenuOpen = !this.ordersMoreMenuOpen;
  }

  async refreshOrders(): Promise<void> {
    this.ordersMoreMenuOpen = false;
    await this.loadOrders();
  }

  isOrderSelected(orderId: string): boolean {
    return this.selectedOrderIds.has(orderId);
  }

  toggleOrderSelection(orderId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const next = new Set(this.selectedOrderIds);
    if (input.checked) next.add(orderId);
    else next.delete(orderId);
    this.selectedOrderIds = next;
  }

  toggleSelectAllOrders(event: Event): void {
    const input = event.target as HTMLInputElement;
    const next = new Set(this.selectedOrderIds);
    if (input.checked) {
      for (const row of this.filteredOrderRows) next.add(row.id);
    } else {
      for (const row of this.filteredOrderRows) next.delete(row.id);
    }
    this.selectedOrderIds = next;
  }

  clearOrderSelection(): void {
    this.selectedOrderIds = new Set<string>();
  }

  async bulkMarkOrdersPaid(): Promise<void> {
    const ids = Array.from(this.selectedOrderIds);
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => this.directus.update('event_orders', id, { status: 'paid' })));
      await this.loadOrders();
      this.clearOrderSelection();
    } catch {
      this.error = 'Failed to mark selected orders as paid.';
    }
  }

  async bulkMarkOrdersUnpaid(): Promise<void> {
    const ids = Array.from(this.selectedOrderIds);
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => this.directus.update('event_orders', id, { status: 'pending' })));
      await this.loadOrders();
      this.clearOrderSelection();
    } catch {
      this.error = 'Failed to mark selected orders as unpaid.';
    }
  }

  async bulkDeleteOrders(): Promise<void> {
    const ids = Array.from(this.selectedOrderIds);
    if (!ids.length) return;
    const confirmed = window.confirm(`Delete ${ids.length} selected order(s)?`);
    if (!confirmed) return;
    try {
      await Promise.all(ids.map((id) => this.directus.remove('event_orders', id)));
      await this.loadOrders();
      this.clearOrderSelection();
    } catch {
      this.error = 'Failed to delete selected orders.';
    }
  }

  openOrderDetail(order: OrderRow): void {
    if (!this.eventId) return;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/orders/${encodeURIComponent(order.id)}`);
  }

  closeOrderDetail(): void {
    if (!this.eventId) return;
    this.orderDetailOpen = false;
    this.orderDetailId = '';
    this.selectedOrder = null;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/orders`);
  }

  async markSelectedOrderPaid(): Promise<void> {
    if (!this.selectedOrder || this.markOrderPaidBusy) return;
    this.markOrderPaidBusy = true;
    try {
      await this.directus.update('event_orders', this.selectedOrder.id, { status: 'paid' });
      await this.loadOrders();
      this.selectedOrder = this.orderRows.find((row) => row.id === this.orderDetailId) || null;
    } catch {
      this.error = 'Failed to update order status.';
    } finally {
      this.markOrderPaidBusy = false;
    }
  }

  openOrderBuyerProfile(order: OrderRow): void {
    if (!order.buyerProfileId) return;
    window.open(`${this.directusBase}/admin/content/profiles/${encodeURIComponent(order.buyerProfileId)}`, '_blank', 'noopener');
  }

  openAddGuest(): void {
    this.ordersMoreMenuOpen = false;
    this.goToAddGuests();
  }

  toggleGuestsMoreMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.guestsMoreMenuOpen = !this.guestsMoreMenuOpen;
  }

  async refreshGuests(): Promise<void> {
    this.guestsMoreMenuOpen = false;
    await this.loadGuests();
  }

  exportGuestList(): void {
    this.guestsMoreMenuOpen = false;
    const rows = this.filteredGuestRows;
    if (!rows.length) {
      this.error = 'No guests to export.';
      return;
    }
    const header = ['Name', 'Email', 'Ticket', 'Checked In'];
    const data = rows.map((row) => [row.name, row.email, row.ticketLabel, row.checkedIn ? 'Yes' : 'No']);
    const csv = [header, ...data]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${this.eventTitle || 'event'}-guests.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  goToAddGuests(): void {
    if (!this.eventId) return;
    this.resetGuestAddForm();
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/guests/add`);
  }

  openGuestDetail(guest: GuestRow): void {
    if (!this.eventId) return;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/guests/${encodeURIComponent(guest.id)}`);
  }

  closeGuestDetail(): void {
    if (!this.eventId) return;
    this.guestDetailOpen = false;
    this.guestDetailId = '';
    this.selectedGuest = null;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/guests`);
  }

  isGuestCheckinBusy(guest: GuestRow): boolean {
    return this.guestCheckinBusyId === guest.id;
  }

  async toggleGuestCheckin(guest: GuestRow, event?: MouseEvent): Promise<void> {
    event?.stopPropagation();
    if (this.isGuestCheckinBusy(guest)) return;
    const nextChecked = !guest.checkedIn;
    const previous = guest.checkedIn;
    guest.checkedIn = nextChecked;
    this.guestCheckinBusyId = guest.id;
    try {
      await this.directus.update('event_attendees', guest.sourceId, {
        checkin_status: nextChecked ? 'checked_in' : 'not_checked_in',
        checked_in_at: nextChecked ? new Date().toISOString() : null
      });
    } catch {
      guest.checkedIn = previous;
      this.error = 'Failed to update guest check-in status.';
    } finally {
      this.guestCheckinBusyId = null;
    }
  }

  openGuestContactProfile(guest: GuestRow): void {
    if (!guest.profileId) return;
    window.open(`${this.directusBase}/admin/content/profiles/${encodeURIComponent(guest.profileId)}`, '_blank', 'noopener');
  }

  openGuestOrder(guest: GuestRow): void {
    if (!guest.orderId) return;
    window.open(`${this.directusBase}/admin/content/event_orders/${encodeURIComponent(guest.orderId)}`, '_blank', 'noopener');
  }

  editGuestOrder(guest: GuestRow): void {
    this.openGuestOrder(guest);
  }

  closeGuestAdd(): void {
    this.guestAddError = '';
    this.guestAddOpen = false;
    if (!this.eventId) return;
    void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/guests`);
  }

  addGuestTicketRow(): void {
    this.guestTicketInputRows.push({
      id: this.nextGuestTicketInputRowId++,
      ticketId: this.ticketRows[0]?.id || '',
      quantity: 1
    });
    this.onGuestTicketRowChanged();
  }

  removeGuestTicketRow(index: number): void {
    if (this.guestTicketInputRows.length <= 1) return;
    this.guestTicketInputRows.splice(index, 1);
    this.onGuestTicketRowChanged();
  }

  onGuestTicketRowChanged(): void {
    this.guestAddError = '';
    const totalQty = this.guestTicketInputRows.reduce((sum, row) => sum + Math.max(0, Number(row.quantity || 0)), 0);
    if (totalQty > this.maxTicketsPerOrder) {
      this.guestAddError = `Total tickets cannot exceed ${this.maxTicketsPerOrder}.`;
    }
    this.rebuildGuestAttendeeInputs();
  }

  onBuyerDetailsChanged(): void {
    if (this.useBuyerDetailsForFirstGuest) {
      this.syncFirstGuestFromBuyerDetails();
    }
  }

  onUseBuyerDetailsForFirstGuestChanged(): void {
    if (this.useBuyerDetailsForFirstGuest) {
      this.syncFirstGuestFromBuyerDetails();
    }
  }

  fieldsForGuestAttendee(attendee: GuestTicketAttendeeInput): CheckoutFieldOption[] {
    const ticketSpecific = this.checkoutFieldsByTicket[attendee.ticketId];
    const fields = (ticketSpecific && ticketSpecific.length ? ticketSpecific : this.checkoutFields)
      .map((field) => ({ ...field, required: field.required !== false }));
    return this.normalizeCheckoutFields(fields, attendee.ticketId);
  }

  getGuestAttendeeField(attendeeId: number, fieldKey: string): string {
    const attendee = this.guestAttendeeInputs.find((item) => item.id === attendeeId);
    return String(attendee?.fields?.[fieldKey] || '');
  }

  setGuestAttendeeField(attendeeId: number, fieldKey: string, value: string): void {
    if (this.useBuyerDetailsForFirstGuest && this.guestAttendeeInputs[0]?.id === attendeeId) return;
    this.guestAttendeeInputs = this.guestAttendeeInputs.map((item) =>
      item.id === attendeeId ? { ...item, fields: { ...item.fields, [fieldKey]: value } } : item
    );
  }

  trackByGuestAttendeeId(_: number, attendee: GuestTicketAttendeeInput): number {
    return attendee.id;
  }

  trackByGuestTicketRowId(_: number, row: GuestTicketInputRow): number {
    return row.id;
  }

  trackByTicketId(_: number, ticket: TicketRow): string {
    return ticket.id;
  }

  trackByCheckoutFieldKey(_: number, field: CheckoutFieldOption): string {
    return field.key;
  }

  isJciChapterField(field: CheckoutFieldOption): boolean {
    const key = String(field?.key || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    const label = String(field?.label || '')
      .trim()
      .toLowerCase();
    return key === 'jci_chapter' || key === 'jcichapter' || label === 'jci chapter';
  }

  async saveGuestAdd(): Promise<void> {
    this.guestAddError = '';
    const totalQty = this.guestTicketInputRows.reduce((sum, row) => sum + Math.max(0, Number(row.quantity || 0)), 0);
    if (!totalQty) {
      this.guestAddError = 'Please add at least one ticket.';
      return;
    }
    if (totalQty > this.maxTicketsPerOrder) {
      this.guestAddError = `Total tickets cannot exceed ${this.maxTicketsPerOrder}.`;
      return;
    }
    if (!this.guestBuyerFirstName.trim() || !this.guestBuyerLastName.trim() || !this.guestBuyerEmail.trim()) {
      this.guestAddError = 'Buyer first name, last name, and email are required.';
      return;
    }
    if (this.guestTicketInputRows.some((row) => !String(row.ticketId || '').trim())) {
      this.guestAddError = 'Please select a ticket type for each ticket row.';
      return;
    }

    for (const attendee of this.guestAttendeeInputs) {
      if (!String(attendee.ticketId || '').trim()) {
        this.guestAddError = 'Each guest must have a valid ticket type.';
        return;
      }
      const requiredFields = this.fieldsForGuestAttendee(attendee).filter((field) => field.required !== false);
      for (const field of requiredFields) {
        const value = String(attendee.fields[field.key] || '').trim();
        if (!value) {
          this.guestAddError = `Please fill ${field.label} for ${attendee.ticketLabel}.`;
          return;
        }
      }
    }
    this.guestAddSaving = true;
    let createdOrderId = '';
    const createdOrderItemIds: string[] = [];
    const createdAttendeeIds: string[] = [];
    try {
      const normalizedEventId = this.normalizedEventIdForWrite();
      if (normalizedEventId === null) {
        throw new Error('Event id is invalid for write.');
      }
      const eventIdForWrite =
        typeof normalizedEventId === 'number'
          ? normalizedEventId
          : Number(String(normalizedEventId).trim());
      if (!Number.isFinite(eventIdForWrite)) {
        throw new Error('Event id must be numeric for event_orders/event_attendees.');
      }
      const totalAmount = this.guestTicketInputRows.reduce((sum, row) => {
        const ticket = this.ticketRows.find((item) => item.id === row.ticketId);
        return sum + (ticket?.priceValue || 0) * (row.quantity || 0);
      }, 0);
      const orderStatus: 'pending' | 'paid' =
        this.guestOrderStatus === 'paid' || this.guestOrderStatus === 'free' ? 'paid' : 'pending';
      const checkoutCurrency = await this.resolveCheckoutCurrency(normalizedEventId);
      const currency = typeof checkoutCurrency === 'string' && checkoutCurrency.trim() ? checkoutCurrency.trim() : 'IDR';
      const buyerFirstName = this.guestBuyerFirstName.trim();
      const buyerLastName = this.guestBuyerLastName.trim();
      const buyerName = [buyerFirstName, buyerLastName].filter(Boolean).join(' ').trim();
      const buyerEmail = this.guestBuyerEmail.trim();
      const buyerWhatsapp = this.guestBuyerWhatsapp.trim();
      const buyerProfileId = await this.findProfileIdForBuyer(buyerEmail);
      const buyerGuestContactId = buyerProfileId
        ? ''
        : await this.findOrCreateGuestContactByEmail(buyerEmail, buyerFirstName, buyerLastName, buyerWhatsapp);
      const paymentReference = JSON.stringify({
        buyer_first_name: buyerFirstName,
        buyer_last_name: buyerLastName,
        buyer_email: buyerEmail,
        buyer_whatsapp: buyerWhatsapp,
        ticket_count: this.guestAttendeeInputs.length
      });
      const generatedOrderNumber = await this.generateUniqueOrderNumber();
      let existingOrderCurrency: unknown = undefined;
      try {
        const existingOrders = await this.directus.list('event_orders', {
          fields: 'currency',
          'filter[event_id][_eq]': eventIdForWrite,
          limit: 1
        });
        existingOrderCurrency = existingOrders[0]?.['currency'];
      } catch {
        existingOrderCurrency = undefined;
      }

      const baseOrderPayload = {
        status: orderStatus,
        buyer_profile_id: buyerProfileId || null,
        buyer_guest_contact_id: buyerGuestContactId || null,
        buyer_first_name: buyerFirstName || null,
        buyer_last_name: buyerLastName || null,
        payment_reference: paymentReference,
        subtotal: totalAmount,
        service_fee_total: 0,
        tax_total: 0,
        grand_total: totalAmount
      };
      const orderPayloads: Array<Record<string, unknown>> = [];
      const eventIdVariants: Array<number | string> = [eventIdForWrite, String(eventIdForWrite)];
      const currencyVariants = new Set<unknown>([existingOrderCurrency, currency, 'IDR']);
      for (const eventIdVariant of eventIdVariants) {
        for (const currencyVariant of currencyVariants) {
          if (currencyVariant === undefined || currencyVariant === null || currencyVariant === '') continue;
          orderPayloads.push({
            ...baseOrderPayload,
            event_id: eventIdVariant,
            order_number: generatedOrderNumber,
            currency: currencyVariant
          });
          orderPayloads.push({
            ...baseOrderPayload,
            event_id: eventIdVariant,
            currency: currencyVariant
          });
        }
        orderPayloads.push({
          ...baseOrderPayload,
          event_id: eventIdVariant,
          order_number: generatedOrderNumber
        });
        orderPayloads.push({
          ...baseOrderPayload,
          event_id: eventIdVariant
        });
      }
      const createdOrder = await this.tryCreateWithPayloadVariants('event_orders', orderPayloads);
      createdOrderId = String(createdOrder['id'] ?? '').trim();
      if (!createdOrderId) {
        throw new Error('Order was created without an id.');
      }
      await this.directus.update('event_orders', createdOrderId, {
        status: orderStatus,
        buyer_first_name: buyerFirstName || null,
        buyer_last_name: buyerLastName || null,
        subtotal: totalAmount,
        service_fee_total: 0,
        tax_total: 0,
        grand_total: totalAmount,
        currency
      });

      const orderItemIdByTicketRowId = new Map<number, string>();
      for (const ticketRow of this.guestTicketInputRows) {
        const ticketId = String(ticketRow.ticketId || '').trim();
        const qty = Math.max(1, Number(ticketRow.quantity || 1));
        if (!ticketId) continue;
        const ticket = this.ticketRows.find((item) => item.id === ticketId);
        const unitPrice = Number(ticket?.priceValue || 0);
        const lineTotal = unitPrice * qty;
        const createdOrderItem = await this.directus.create('event_order_items', {
          order_id: createdOrderId,
          ticket_id: ticketId,
          qty,
          unit_price_snapshot: unitPrice,
          service_fee_snapshot: 0,
          line_total: lineTotal
        });
        const createdOrderItemId = String(createdOrderItem['id'] ?? '').trim();
        if (createdOrderItemId) {
          createdOrderItemIds.push(createdOrderItemId);
          orderItemIdByTicketRowId.set(ticketRow.id, createdOrderItemId);
        }
      }

      for (const attendee of this.guestAttendeeInputs) {
        const first = String(attendee.fields['first_name'] || '').trim();
        const last = String(attendee.fields['last_name'] || '').trim();
        const email = String(attendee.fields['email'] || '').trim();
        const whatsapp = String(attendee.fields['whatsapp_number'] || '').trim();
        const jciChapter = String(
          attendee.fields['jci_chapter'] ||
            attendee.fields['jciChapter'] ||
            attendee.fields['JCI Chapter'] ||
            ''
        ).trim();
        const fullName = [first, last].filter(Boolean).join(' ').trim() || 'Guest';
        const orderItemId = orderItemIdByTicketRowId.get(attendee.sourceRowId) || undefined;
        const attendeeProfileId = await this.findProfileIdForBuyer(email || buyerEmail);
        const attendeeGuestContactId = attendeeProfileId
          ? ''
          : await this.findOrCreateGuestContactByEmail(email || buyerEmail, first, last, whatsapp || buyerWhatsapp);
        const createdAttendee = await this.directus.create('event_attendees', {
          event_id: eventIdForWrite,
          order_id: createdOrderId,
          order_item_id: orderItemId,
          ticket_id: attendee.ticketId,
          profile_id: attendeeProfileId || undefined,
          guest_contact_id: attendeeGuestContactId || undefined,
          holder_first_name: first || null,
          holder_last_name: last || null,
          holder_email: email || buyerEmail,
          holder_phone: whatsapp || buyerWhatsapp || undefined,
          jci_chapter: jciChapter || null,
          checkin_status: 'not_checked_in'
        });
        const createdAttendeeId = String(createdAttendee['id'] ?? '').trim();
        if (createdAttendeeId) createdAttendeeIds.push(createdAttendeeId);
      }

      await Promise.all([this.loadGuests(), this.loadOrders()]);
      this.closeGuestAdd();
    } catch (error) {
      let rollbackFailed = false;
      if (createdOrderId) {
        rollbackFailed = await this.rollbackGuestAddTransaction(createdOrderId, createdAttendeeIds, createdOrderItemIds);
      }
      this.guestAddError = `Failed to save guests/order: ${this.extractErrorMessage(error)}${
        rollbackFailed ? ' (Rollback partially failed. Please check event_orders, event_order_items, and event_attendees.)' : ''
      }`;
    } finally {
      this.guestAddSaving = false;
    }
  }

  private async rollbackGuestAddTransaction(
    orderId: string,
    attendeeIds: string[],
    orderItemIds: string[]
  ): Promise<boolean> {
    let hasFailure = false;
    for (const attendeeId of attendeeIds) {
      try {
        await this.directus.remove('event_attendees', attendeeId);
      } catch {
        // Best-effort rollback.
        hasFailure = true;
      }
    }
    for (const orderItemId of orderItemIds) {
      try {
        await this.directus.remove('event_order_items', orderItemId);
      } catch {
        // Best-effort rollback.
        hasFailure = true;
      }
    }
    try {
      await this.directus.remove('event_orders', orderId);
    } catch {
      // Best-effort rollback.
      hasFailure = true;
    }
    return hasFailure;
  }

  exportOrderList(): void {
    const rows = this.filteredOrderRows;
    if (!rows.length) {
      this.error = 'No orders to export.';
      return;
    }
    const header = ['Order', 'Buyer', 'Status', 'Tickets', 'Total', 'Created'];
    const data = rows.map((row) => [
      row.numberLabel,
      row.buyerName,
      row.statusLabel,
      String(row.ticketCount),
      String(row.totalAmount),
      row.createdAt ? row.createdAt.toISOString() : ''
    ]);
    const csv = [header, ...data]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${this.eventTitle || 'event'}-orders.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  formatIdr(value: number): string {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(value || 0);
  }

  private normalizeOrderStatus(value: string): OrderFilter {
    if (value === 'paid') return 'paid';
    if (value === 'not paid' || value === 'not_paid' || value === 'unpaid' || value === 'pending') return 'not_paid';
    if (value === 'free') return 'free';
    if (value === 'processing') return 'processing';
    if (value === 'removed' || value === 'deleted' || value === 'canceled' || value === 'cancelled') return 'removed';
    if (value === 'authorized' || value === 'authorised') return 'authorized';
    return 'all';
  }

  private orderStatusLabel(value: OrderFilter): string {
    switch (value) {
      case 'paid':
        return 'Paid';
      case 'not_paid':
        return 'Not paid';
      case 'free':
        return 'Free';
      case 'processing':
        return 'Processing';
      case 'removed':
        return 'Removed';
      case 'authorized':
        return 'Authorized';
      default:
        return 'All Orders';
    }
  }

  featureIcon(key: string): string {
    const normalized = key.trim().toLowerCase();
    if (normalized.includes('lineup')) return '🎤';
    if (normalized.includes('video')) return '🎥';
    if (normalized.includes('schedule')) return '🗓';
    if (normalized.includes('seating')) return '🪑';
    return '✨';
  }

  isFeatureBusy(featureId: string): boolean {
    return this.featureBusyIds.has(featureId);
  }

  featurePrimaryActionLabel(feature: EventFeatureCard): string {
    if (this.isFeatureComingSoon(feature)) return 'Coming soon';
    if (feature.key === 'lineup' && feature.selected) return 'Manage';
    if (feature.key === 'video_conference' && feature.selected) return 'Configure';
    return feature.selected ? 'Added' : 'Add to Event';
  }

  selectedFeatureSubtitle(feature: EventFeatureCard): string {
    if (feature.key === 'lineup') {
      const config = feature.config && typeof feature.config === 'object' ? feature.config : null;
      const speakers = Array.isArray(config?.['speakers']) ? (config?.['speakers'] as unknown[]) : [];
      return `${speakers.length} speaker(s)`;
    }
    if (feature.key === 'video_conference') {
      const config = feature.config && typeof feature.config === 'object' ? feature.config : null;
      const provider = typeof config?.['provider'] === 'string' ? String(config['provider']) : '';
      return provider === 'google_meet' ? 'Google Meet connected' : 'Zoom connected';
    }
    if (feature.key === 'schedule') return 'Schedule enabled';
    if (feature.key === 'seating_map') return 'Seating map enabled';
    return 'Enabled';
  }

  videoMeetingLinkLabel(feature: EventFeatureCard): string {
    const config = feature.config && typeof feature.config === 'object' ? feature.config : null;
    const provider = typeof config?.['provider'] === 'string' ? String(config['provider']) : '';
    return provider === 'google_meet' ? 'View Google Meet Link' : 'View Zoom Link';
  }

  openVideoMeetingLink(feature: EventFeatureCard): void {
    const config = feature.config && typeof feature.config === 'object' ? feature.config : null;
    const url = typeof config?.['url'] === 'string' ? String(config['url']).trim() : '';
    if (!/^https?:\/\//i.test(url)) {
      this.openVideoConfigDialog(feature);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async handleFeatureAction(feature: EventFeatureCard): Promise<void> {
    if (this.isFeatureComingSoon(feature)) {
      this.featuresError = `${feature.name} is planned for a future release.`;
      return;
    }
    if (feature.key === 'schedule') {
      this.openScheduleSetup(feature);
      return;
    }
    if (feature.key === 'lineup') {
      await this.openLineupDialog(feature);
      return;
    }
    if (feature.key === 'video_conference') {
      this.openVideoConfigDialog(feature);
      return;
    }
    if (!feature.selected) {
      await this.toggleFeatureAssignment(feature);
      return;
    }
    this.featuresError = '';
  }

  openScheduleSetup(feature: EventFeatureCard): void {
    this.scheduleSetupOpen = true;
    this.scheduleSetupFeatureId = feature.id;
    this.featuresError = '';
    if (this.eventId) {
      void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/features/schedule`);
    }
  }

  closeScheduleSetup(): void {
    this.openScheduleItemMenuId = null;
    this.scheduleSetupOpen = false;
    this.scheduleSetupFeatureId = '';
    if (this.eventId) {
      void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/features`);
    }
  }

  async addScheduleItemFromSetup(): Promise<void> {
    const feature = this.featureCards.find((item) => item.id === this.scheduleSetupFeatureId || item.key === 'schedule');
    if (!feature) return;
    if (!feature.selected) {
      await this.toggleFeatureAssignment(feature);
    }
    this.openScheduleItemDialog();
  }

  openScheduleItemDialog(): void {
    this.scheduleItemDialogError = '';
    this.editingScheduleDraftId = null;
    this.scheduleItemNameDraft = '';
    this.schedulePlaceDraft = '';
    this.scheduleTagsDraft = '';
    this.scheduleDescriptionDraft = '';
    const previousEnd = this.scheduleItemsDraft.length
      ? this.scheduleItemsDraft[this.scheduleItemsDraft.length - 1].endAt
      : null;
    const base = previousEnd
      ? new Date(previousEnd)
      : (this.eventStartAt && !Number.isNaN(this.eventStartAt.getTime()) ? new Date(this.eventStartAt) : new Date());
    const end = new Date(base.getTime() + 60 * 60 * 1000);
    this.scheduleStartDateDraft = this.toDateInput(base);
    this.scheduleStartTimeDraft = this.toTimeInput(base);
    this.scheduleEndDateDraft = this.toDateInput(end);
    this.scheduleEndTimeDraft = this.toTimeInput(end);
    this.scheduleItemDialogOpen = true;
  }

  closeScheduleItemDialog(): void {
    this.scheduleItemDialogOpen = false;
    this.scheduleItemDialogError = '';
  }

  async saveScheduleItemDialog(): Promise<void> {
    if (!this.scheduleStartDateDraft || !this.scheduleStartTimeDraft || !this.scheduleEndDateDraft || !this.scheduleEndTimeDraft) {
      this.scheduleItemDialogError = 'Start and end date/time are required.';
      return;
    }
    const startAt = this.parseDateTimeInput(this.scheduleStartDateDraft, this.scheduleStartTimeDraft);
    const endAt = this.parseDateTimeInput(this.scheduleEndDateDraft, this.scheduleEndTimeDraft);
    if (!startAt || !endAt) {
      this.scheduleItemDialogError = 'Invalid start or end date/time.';
      return;
    }
    if (endAt.getTime() <= startAt.getTime()) {
      this.scheduleItemDialogError = 'End date/time must be later than start date/time.';
      return;
    }
    const itemName = this.scheduleItemNameDraft.trim() || 'Untitled item';
    const payload: ScheduleItemDraft = {
      draftId: this.editingScheduleDraftId || this.nextScheduleDraftId++,
      name: itemName,
      startAt,
      endAt,
      place: this.schedulePlaceDraft.trim(),
      tags: this.scheduleTagsDraft.trim(),
      description: this.scheduleDescriptionDraft.trim()
    };
    const previousItems = this.scheduleItemsDraft.map((item) => ({ ...item }));
    if (this.editingScheduleDraftId) {
      this.scheduleItemsDraft = this.scheduleItemsDraft.map((item) =>
        item.draftId === this.editingScheduleDraftId ? payload : item
      );
    } else {
      this.scheduleItemsDraft.push(payload);
    }
    try {
      await this.persistScheduleItemsToDirectus();
      this.scheduleItemDialogOpen = false;
      this.editingScheduleDraftId = null;
      this.featuresError = '';
    } catch {
      this.scheduleItemsDraft = previousItems;
      this.scheduleItemDialogError = 'Failed to save schedule item.';
    }
  }

  editScheduleItem(draftId: number): void {
    const item = this.scheduleItemsDraft.find((entry) => entry.draftId === draftId);
    if (!item) return;
    this.editingScheduleDraftId = draftId;
    this.scheduleItemDialogError = '';
    this.scheduleItemNameDraft = item.name;
    this.scheduleStartDateDraft = this.toDateInput(item.startAt);
    this.scheduleStartTimeDraft = this.toTimeInput(item.startAt);
    this.scheduleEndDateDraft = this.toDateInput(item.endAt);
    this.scheduleEndTimeDraft = this.toTimeInput(item.endAt);
    this.schedulePlaceDraft = item.place;
    this.scheduleTagsDraft = item.tags;
    this.scheduleDescriptionDraft = item.description;
    this.scheduleItemDialogOpen = true;
  }

  deleteScheduleItem(draftId: number): void {
    this.scheduleItemsDraft = this.scheduleItemsDraft.filter((entry) => entry.draftId !== draftId);
    this.openScheduleItemMenuId = null;
    this.saveScheduleDraftToStorage();
  }

  saveScheduleSetup(): void {
    this.saveScheduleDraftToStorage();
    this.featuresError = '';
    this.closeScheduleSetup();
  }

  toggleScheduleItemMenu(draftId: number, event: MouseEvent): void {
    event.stopPropagation();
    this.openScheduleItemMenuId = this.openScheduleItemMenuId === draftId ? null : draftId;
  }

  toggleHideScheduleItem(draftId: number): void {
    this.scheduleItemsDraft = this.scheduleItemsDraft.map((entry) => {
      if (entry.draftId !== draftId) return entry;
      return { ...entry, hidden: !entry.hidden };
    });
    this.openScheduleItemMenuId = null;
    this.saveScheduleDraftToStorage();
  }

  cancelScheduleItem(draftId: number): void {
    this.openScheduleItemMenuId = null;
    this.pendingCancelScheduleItemId = draftId;
    this.confirmAction = 'cancel-schedule-item';
    this.confirmDialogTitle = 'Cancel Schedule Item';
    this.confirmDialogMessage = 'Are you sure you want to cancel this schedule item? It will appear on the schedule as canceled.';
    this.confirmDialogConfirmLabel = 'Yes, Cancel';
    this.confirmDialogOpen = true;
  }

  isFeatureComingSoon(feature: EventFeatureCard): boolean {
    return feature.key === 'seating_map';
  }

  async toggleFeatureAssignment(feature: EventFeatureCard): Promise<void> {
    if (!this.eventId || this.isFeatureBusy(feature.id)) return;
    this.featureBusyIds.add(feature.id);
    this.featuresError = '';
    const previousSelected = feature.selected;
    const previousAssignmentId = feature.assignmentId;
    feature.selected = !feature.selected;
    try {
      if (previousSelected && previousAssignmentId) {
        await this.directus.remove('events_features', previousAssignmentId);
        feature.assignmentId = null;
        feature.config = null;
      } else {
        const created = await this.directus.create('events_features', {
          event_id: this.eventId,
          feature_id: feature.id,
          enabled: true
        });
        feature.assignmentId = String(created['id'] || '');
      }
    } catch {
      feature.selected = previousSelected;
      feature.assignmentId = previousAssignmentId;
      this.featuresError = 'Failed to update feature assignment.';
    } finally {
      this.featureBusyIds.delete(feature.id);
    }
  }

  openVideoConfigDialog(feature: EventFeatureCard): void {
    this.activeVideoFeatureId = feature.id;
    this.videoConfigIsNew = !feature.selected;
    const config = feature.config && typeof feature.config === 'object' ? feature.config : null;
    const provider = typeof config?.['provider'] === 'string' ? String(config['provider']) : 'zoom';
    const url = typeof config?.['url'] === 'string' ? String(config['url']) : '';
    this.videoProviderDraft = provider === 'google_meet' ? 'google_meet' : 'zoom';
    this.videoUrlDraft = url;
    this.videoConfigError = '';
    this.videoConfigDialogOpen = true;
  }

  closeVideoConfigDialog(): void {
    if (this.videoConfigSaving) return;
    this.videoConfigDialogOpen = false;
    this.videoConfigIsNew = false;
    this.videoConfigError = '';
    this.activeVideoFeatureId = '';
  }

  async openLineupDialog(feature: EventFeatureCard): Promise<void> {
    this.activeLineupFeatureId = feature.id;
    const config = feature.config && typeof feature.config === 'object' ? feature.config : null;
    const speakersRaw = Array.isArray(config?.['speakers']) ? config['speakers'] as unknown[] : [];
    this.lineupSpeakersDraft = speakersRaw
      .map((row) => this.parseLineupSpeakerRow(row))
      .filter((speaker): speaker is LineupSpeakerDraft => !!speaker);
    if (!this.lineupSpeakersDraft.length) {
      this.lineupSpeakersDraft = [this.createLineupSpeakerDraft()];
    } else {
      this.lineupSpeakersDraft = await Promise.all(this.lineupSpeakersDraft.map(async (speaker) => {
        const preview = await this.generateLineupCroppedPreviewDataUrl(
          speaker.assetUrl,
          speaker.cropBoxX,
          speaker.cropBoxY,
          speaker.cropBoxWidth,
          speaker.cropBoxHeight
        );
        return preview ? { ...speaker, croppedPreviewUrl: preview } : speaker;
      }));
    }
    this.lineupError = '';
    this.lineupDialogOpen = true;
  }

  closeLineupDialog(): void {
    if (this.lineupSaving) return;
    this.lineupDialogOpen = false;
    this.lineupError = '';
    this.activeLineupFeatureId = '';
    this.lineupPendingImageTargetId = '';
    this.cancelLineupImageCropper();
  }

  addLineupSpeaker(): void {
    this.lineupSpeakersDraft.push(this.createLineupSpeakerDraft());
  }

  async removeLineupSpeaker(id: string): Promise<void> {
    const target = this.lineupSpeakersDraft.find((speaker) => speaker.id === id);
    this.lineupSpeakersDraft = this.lineupSpeakersDraft.filter((speaker) => speaker.id !== id);
    if (!this.lineupSpeakersDraft.length) {
      this.lineupSpeakersDraft = [this.createLineupSpeakerDraft()];
    }
    if (target?.fileId) {
      try {
        await this.directus.removeFile(target.fileId);
      } catch {
        // Keep UX responsive; stale files can be cleaned manually.
      }
    }
  }

  openLineupImagePicker(speakerId: string, input: HTMLInputElement): void {
    this.lineupPendingImageTargetId = speakerId;
    input.value = '';
    input.click();
  }

  async onLineupImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    const speakerId = this.lineupPendingImageTargetId;
    if (!input || !file || !speakerId) return;
    this.lineupError = '';
    try {
      const uploaded = await this.directus.uploadFile(file, {
        title: this.eventTitle || file.name,
        description: 'Lineup speaker image'
      });
      const fileId = String(uploaded['id'] || '').trim();
      if (!fileId) throw new Error('Upload failed.');
      const assetUrl = this.directus.buildAssetUrl(fileId);
      this.lineupSpeakersDraft = this.lineupSpeakersDraft.map((speaker) =>
        speaker.id === speakerId
          ? {
              ...speaker,
              fileId,
              assetUrl,
              croppedPreviewUrl: '',
              cropBoxX: 0,
              cropBoxY: 0,
              cropBoxWidth: 100,
              cropBoxHeight: 100
            }
          : speaker
      );
      this.openLineupImageCropper(speakerId);
    } catch {
      this.lineupError = 'Failed to upload lineup image.';
    } finally {
      input.value = '';
      this.lineupPendingImageTargetId = '';
    }
  }

  openLineupImageCropper(speakerId: string): void {
    const speaker = this.lineupSpeakersDraft.find((item) => item.id === speakerId);
    if (!speaker || !speaker.assetUrl) return;
    this.lineupCropDialogTargetSpeakerId = speakerId;
    this.lineupCropDialogImageUrl = speaker.assetUrl;
    this.loadImageForCrop(speaker.assetUrl)
      .then((img) => {
        const imageRatio = Math.max(1e-6, (img.naturalWidth || img.width) / Math.max(1, img.naturalHeight || img.height));
        this.lineupCropFrameAspectRatio = imageRatio;
        this.lineupCropImageBounds = {
          x: 0,
          y: 0,
          width: 100,
          height: 100
        };
        const maxSquareWidth = Math.min(100, 100 / this.lineupCropFrameAspectRatio);
        const defaultSquareWidth = Math.max(18, Math.min(maxSquareWidth, 32));
        const defaultSquareHeight = defaultSquareWidth * this.lineupCropFrameAspectRatio;

        const hasExisting =
          Number.isFinite(Number(speaker.cropBoxWidth))
          && Number(speaker.cropBoxWidth) > 0
          && Number.isFinite(Number(speaker.cropBoxHeight))
          && Number(speaker.cropBoxHeight) > 0;
        if (hasExisting) {
          this.lineupCropBoxX = Number(speaker.cropBoxX || 0);
          this.lineupCropBoxY = Number(speaker.cropBoxY || 0);
          this.lineupCropBoxWidth = Number(speaker.cropBoxWidth || defaultSquareWidth);
          this.lineupCropBoxHeight = Number(speaker.cropBoxHeight || defaultSquareHeight);
          this.clampLineupCropBoxToImageBounds();
        } else {
          this.lineupCropBoxX = 0;
          this.lineupCropBoxY = 0;
          this.lineupCropBoxWidth = defaultSquareWidth;
          this.lineupCropBoxHeight = defaultSquareHeight;
        }
        this.lineupCropDialogOpen = true;
      })
      .catch(() => {
        this.lineupError = 'Failed to load image for cropping.';
      });
  }

  cancelLineupImageCropper(): void {
    this.lineupCropDialogOpen = false;
    this.lineupCropDragActive = false;
    this.lineupCropResizeMode = '';
    this.lineupCropResizeHandle = '';
    this.lineupCropDialogTargetSpeakerId = '';
    this.lineupCropDialogImageUrl = '';
  }

  async applyLineupImageCropper(): Promise<void> {
    if (!this.lineupCropDialogTargetSpeakerId || !this.lineupCropDialogImageUrl) return;
    const preview = await this.generateLineupCroppedPreviewDataUrl(
      this.lineupCropDialogImageUrl,
      this.lineupCropBoxX,
      this.lineupCropBoxY,
      this.lineupCropBoxWidth,
      this.lineupCropBoxHeight
    );
    this.lineupSpeakersDraft = this.lineupSpeakersDraft.map((speaker) =>
      speaker.id === this.lineupCropDialogTargetSpeakerId
        ? {
            ...speaker,
            croppedPreviewUrl: preview || speaker.croppedPreviewUrl,
            cropBoxX: this.lineupCropBoxX,
            cropBoxY: this.lineupCropBoxY,
            cropBoxWidth: this.lineupCropBoxWidth,
            cropBoxHeight: this.lineupCropBoxHeight
          }
        : speaker
    );
    this.cancelLineupImageCropper();
  }

  displayLineupImageUrl(speaker: LineupSpeakerDraft): string {
    return speaker.croppedPreviewUrl || speaker.assetUrl || '';
  }

  async saveLineupDialog(): Promise<void> {
    const feature = this.featureCards.find((item) => item.id === this.activeLineupFeatureId);
    if (!feature || !this.eventId || this.lineupSaving) return;
    this.lineupSaving = true;
    this.lineupError = '';
    this.featureBusyIds.add(feature.id);
    try {
      const speakers = this.lineupSpeakersDraft
        .filter((speaker) => !!speaker.fileId.trim() && !!speaker.name.trim())
        .map((speaker) => ({
          fileId: speaker.fileId,
          name: speaker.name.trim(),
          tagline: speaker.tagline.trim(),
          description: speaker.description.trim(),
          cropBoxX: Number(speaker.cropBoxX || 0),
          cropBoxY: Number(speaker.cropBoxY || 0),
          cropBoxWidth: Number(speaker.cropBoxWidth || 100),
          cropBoxHeight: Number(speaker.cropBoxHeight || 100)
        }));
      const payload = { speakers };
      if (!feature.selected || !feature.assignmentId) {
        const created = await this.directus.create('events_features', {
          event_id: this.eventId,
          feature_id: feature.id,
          enabled: true,
          config_json: payload
        });
        feature.selected = true;
        feature.assignmentId = String(created['id'] || '');
      } else {
        await this.directus.update('events_features', feature.assignmentId, {
          enabled: true,
          config_json: payload
        });
      }
      feature.config = payload;
      this.lineupDialogOpen = false;
      this.activeLineupFeatureId = '';
      this.lineupPendingImageTargetId = '';
      this.cancelLineupImageCropper();
    } catch {
      this.lineupError = 'Failed to save lineup.';
    } finally {
      this.lineupSaving = false;
      this.featureBusyIds.delete(feature.id);
    }
  }

  onLineupCropSelectionPointerDown(event: PointerEvent): void {
    if (!this.lineupCropDialogOpen || event.button !== 0) return;
    const frame = (event.currentTarget as HTMLElement | null)?.closest('.lineup-crop-frame') as HTMLElement | null;
    if (!frame) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = frame.getBoundingClientRect();
    this.lineupCropDragActive = true;
    this.lineupCropResizeMode = 'move';
    this.lineupCropDragStartX = event.clientX;
    this.lineupCropDragStartY = event.clientY;
    this.lineupCropDragOriginBoxX = this.lineupCropBoxX;
    this.lineupCropDragOriginBoxY = this.lineupCropBoxY;
    this.lineupCropDragOriginBoxWidth = this.lineupCropBoxWidth;
    this.lineupCropDragOriginBoxHeight = this.lineupCropBoxHeight;
    this.lineupCropDragFrameWidth = Math.max(1, rect.width);
    this.lineupCropDragFrameHeight = Math.max(1, rect.height);
  }

  onLineupCropResizeHandlePointerDown(event: PointerEvent, handle: 'nw' | 'ne' | 'sw' | 'se'): void {
    if (!this.lineupCropDialogOpen || event.button !== 0) return;
    const frame = (event.currentTarget as HTMLElement | null)?.closest('.lineup-crop-frame') as HTMLElement | null;
    if (!frame) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = frame.getBoundingClientRect();
    this.lineupCropDragActive = true;
    this.lineupCropResizeMode = 'resize';
    this.lineupCropResizeHandle = handle;
    this.lineupCropDragStartX = event.clientX;
    this.lineupCropDragStartY = event.clientY;
    this.lineupCropDragOriginBoxX = this.lineupCropBoxX;
    this.lineupCropDragOriginBoxY = this.lineupCropBoxY;
    this.lineupCropDragOriginBoxWidth = this.lineupCropBoxWidth;
    this.lineupCropDragOriginBoxHeight = this.lineupCropBoxHeight;
    this.lineupCropDragFrameWidth = Math.max(1, rect.width);
    this.lineupCropDragFrameHeight = Math.max(1, rect.height);
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (!this.lineupCropDragActive) return;
    const deltaX = event.clientX - this.lineupCropDragStartX;
    const deltaY = event.clientY - this.lineupCropDragStartY;
    const dx = (deltaX / this.lineupCropDragFrameWidth) * 100;
    const dy = (deltaY / this.lineupCropDragFrameHeight) * 100;

    if (this.lineupCropResizeMode === 'move') {
      const nextX = this.lineupCropDragOriginBoxX + dx;
      const nextY = this.lineupCropDragOriginBoxY + dy;
      const maxX = this.lineupCropImageBounds.x + this.lineupCropImageBounds.width - this.lineupCropBoxWidth;
      const maxY = this.lineupCropImageBounds.y + this.lineupCropImageBounds.height - this.lineupCropBoxHeight;
      this.lineupCropBoxX = Math.max(this.lineupCropImageBounds.x, Math.min(maxX, nextX));
      this.lineupCropBoxY = Math.max(this.lineupCropImageBounds.y, Math.min(maxY, nextY));
      return;
    }

    if (this.lineupCropResizeMode === 'resize') {
      const ratio = Math.max(0.0001, this.lineupCropFrameAspectRatio);
      const minWidth = 8;
      const left = this.lineupCropDragOriginBoxX;
      const top = this.lineupCropDragOriginBoxY;
      const right = this.lineupCropDragOriginBoxX + this.lineupCropDragOriginBoxWidth;
      const bottom = this.lineupCropDragOriginBoxY + this.lineupCropDragOriginBoxHeight;
      const signedDelta = Math.abs(dx) >= Math.abs(dy / ratio) ? dx : (dy / ratio);

      if (this.lineupCropResizeHandle === 'se') {
        const maxWidth = Math.min(
          this.lineupCropImageBounds.x + this.lineupCropImageBounds.width - left,
          (this.lineupCropImageBounds.y + this.lineupCropImageBounds.height - top) / ratio
        );
        const newWidth = Math.max(minWidth, Math.min(maxWidth, this.lineupCropDragOriginBoxWidth + signedDelta));
        this.lineupCropBoxWidth = newWidth;
        this.lineupCropBoxHeight = newWidth * ratio;
        this.lineupCropBoxX = left;
        this.lineupCropBoxY = top;
        return;
      }
      if (this.lineupCropResizeHandle === 'sw') {
        const maxWidth = Math.min(
          right - this.lineupCropImageBounds.x,
          (this.lineupCropImageBounds.y + this.lineupCropImageBounds.height - top) / ratio
        );
        const newWidth = Math.max(minWidth, Math.min(maxWidth, this.lineupCropDragOriginBoxWidth - signedDelta));
        this.lineupCropBoxX = right - newWidth;
        this.lineupCropBoxY = top;
        this.lineupCropBoxWidth = newWidth;
        this.lineupCropBoxHeight = newWidth * ratio;
        return;
      }
      if (this.lineupCropResizeHandle === 'ne') {
        const maxWidth = Math.min(
          this.lineupCropImageBounds.x + this.lineupCropImageBounds.width - left,
          (bottom - this.lineupCropImageBounds.y) / ratio
        );
        const newWidth = Math.max(minWidth, Math.min(maxWidth, this.lineupCropDragOriginBoxWidth + signedDelta));
        this.lineupCropBoxWidth = newWidth;
        this.lineupCropBoxHeight = newWidth * ratio;
        this.lineupCropBoxX = left;
        this.lineupCropBoxY = bottom - (newWidth * ratio);
        return;
      }
      if (this.lineupCropResizeHandle === 'nw') {
        const maxWidth = Math.min(
          right - this.lineupCropImageBounds.x,
          (bottom - this.lineupCropImageBounds.y) / ratio
        );
        const newWidth = Math.max(minWidth, Math.min(maxWidth, this.lineupCropDragOriginBoxWidth - signedDelta));
        this.lineupCropBoxX = right - newWidth;
        this.lineupCropBoxY = bottom - (newWidth * ratio);
        this.lineupCropBoxWidth = newWidth;
        this.lineupCropBoxHeight = newWidth * ratio;
      }
    }
  }

  @HostListener('document:pointerup')
  onDocumentPointerUp(): void {
    this.lineupCropDragActive = false;
    this.lineupCropResizeMode = '';
    this.lineupCropResizeHandle = '';
  }

  async saveVideoConfigDialog(): Promise<void> {
    const feature = this.featureCards.find((item) => item.id === this.activeVideoFeatureId);
    if (!feature || !this.eventId) return;
    const url = this.videoUrlDraft.trim();
    if (!/^https?:\/\//i.test(url)) {
      this.videoConfigError = 'Please enter a valid meeting URL starting with http:// or https://';
      return;
    }
    this.videoConfigSaving = true;
    this.videoConfigError = '';
    this.featureBusyIds.add(feature.id);
    try {
      if (!feature.selected || !feature.assignmentId) {
        const created = await this.directus.create('events_features', {
          event_id: this.eventId,
          feature_id: feature.id,
          enabled: true,
          config_json: {
            provider: this.videoProviderDraft,
            url
          }
        });
        feature.selected = true;
        feature.assignmentId = String(created['id'] || '');
      } else {
        await this.directus.update('events_features', feature.assignmentId, {
          enabled: true,
          config_json: {
            provider: this.videoProviderDraft,
            url
          }
        });
      }
      await this.directus.update('events', this.eventId, {
        online_meeting_url: url
      });
      feature.config = {
        provider: this.videoProviderDraft,
        url
      };
      this.videoConfigDialogOpen = false;
      this.activeVideoFeatureId = '';
    } catch {
      this.videoConfigError = 'Failed to save video conferencing settings.';
    } finally {
      this.videoConfigSaving = false;
      this.featureBusyIds.delete(feature.id);
    }
  }

  async publishEvent(): Promise<void> {
    if (!this.eventId || this.publishing) return;
    this.publishing = true;
    this.error = '';
    try {
      await this.directus.update('events', this.eventId, { status: 'published' });
      await this.loadEvent();
    } catch {
      this.error = 'Failed to publish event.';
    } finally {
      this.publishing = false;
    }
  }

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.openTicketMenuId = null;
    this.openUsingFeatureMenuId = null;
    this.openScheduleItemMenuId = null;
    this.ordersMoreMenuOpen = false;
    this.guestsMoreMenuOpen = false;
    this.showAddFieldMenu = false;
  }

  toggleUsingFeatureMenu(featureId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openUsingFeatureMenuId = this.openUsingFeatureMenuId === featureId ? null : featureId;
  }

  editFeatureLink(feature: EventFeatureCard): void {
    this.openUsingFeatureMenuId = null;
    if (feature.key === 'video_conference') {
      this.openVideoConfigDialog(feature);
    }
  }

  async disconnectFeature(feature: EventFeatureCard): Promise<void> {
    this.openUsingFeatureMenuId = null;
    if (!this.eventId) return;
    if (this.isFeatureBusy(feature.id)) return;

    const originalConfig = feature.config;
    const originalSelected = feature.selected;
    const originalAssignmentId = feature.assignmentId;
    this.featureBusyIds.add(feature.id);
    this.featuresError = '';
    feature.selected = false;
    feature.assignmentId = null;
    feature.config = null;

    try {
      if (feature.key === 'lineup') {
        const config = originalConfig && typeof originalConfig === 'object' ? originalConfig : null;
        const speakers = Array.isArray(config?.['speakers']) ? (config?.['speakers'] as Array<Record<string, unknown>>) : [];
        for (const speaker of speakers) {
          const fileId = String(speaker?.['fileId'] || '').trim();
          if (!fileId) continue;
          await this.directus.removeFile(fileId).catch(() => undefined);
        }
      }
      if (originalAssignmentId) {
        await this.directus.remove('events_features', originalAssignmentId);
      }
      if (feature.key === 'video_conference') {
        await this.directus.update('events', this.eventId, { online_meeting_url: null });
      }
    } catch {
      feature.selected = originalSelected;
      feature.assignmentId = originalAssignmentId;
      feature.config = originalConfig;
      this.featuresError = `Failed to remove ${feature.name.toLowerCase()}.`;
    } finally {
      this.featureBusyIds.delete(feature.id);
    }
  }

  toggleTicketMenu(id: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.openTicketMenuId === id) {
      this.openTicketMenuId = null;
      return;
    }
    const trigger = event.currentTarget as HTMLElement | null;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const estimatedMenuHeight = 116;
      const estimatedMenuWidth = 124;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < estimatedMenuHeight;
      this.ticketMenuTop = openUp
        ? Math.max(8, rect.top - estimatedMenuHeight - 6)
        : Math.min(window.innerHeight - estimatedMenuHeight - 8, rect.bottom + 6);
      this.ticketMenuLeft = Math.max(
        8,
        Math.min(window.innerWidth - estimatedMenuWidth - 8, rect.right - estimatedMenuWidth)
      );
    } else {
      this.ticketMenuTop = 8;
      this.ticketMenuLeft = 8;
    }
    this.openTicketMenuId = id;
  }

  editTicket(ticket: TicketRow, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.eventId || !ticket.id) return;
    void this.router.navigateByUrl(
      `/events/${encodeURIComponent(this.eventId)}/tickets/new?ticketId=${encodeURIComponent(ticket.id)}&returnTab=tickets-seating`
    );
  }

  async hideOrShowTicket(ticket: TicketRow): Promise<void> {
    const originalStatus = ticket.status;
    const originalIsHidden = ticket.isHidden;
    const originalStatusLabel = ticket.statusLabel;
    const nextStatus = ticket.status === 'hidden' ? 'available' : 'hidden';

    ticket.status = nextStatus;
    ticket.isHidden = nextStatus === 'hidden';
    ticket.statusLabel = nextStatus === 'hidden'
      ? 'Available'
      : nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1);
    this.openTicketMenuId = null;
    this.error = '';

    try {
      await this.directus.update('event_tickets', ticket.id, { status: nextStatus });
    } catch {
      ticket.status = originalStatus;
      ticket.isHidden = originalIsHidden;
      ticket.statusLabel = originalStatusLabel;
      this.error = 'Failed to update ticket.';
    }
  }

  isTicketSelected(ticketId: string): boolean {
    return this.selectedTicketIds.has(ticketId);
  }

  toggleTicketSelection(ticketId: string, event: Event): void {
    event.stopPropagation();
    if (this.selectedTicketIds.has(ticketId)) {
      this.selectedTicketIds.delete(ticketId);
      return;
    }
    this.selectedTicketIds.add(ticketId);
  }

  toggleSelectAllTickets(event: Event): void {
    event.stopPropagation();
    const target = event.target as HTMLInputElement | null;
    const shouldSelectAll = !!target?.checked;
    if (!shouldSelectAll) {
      this.selectedTicketIds.clear();
      return;
    }
    this.selectedTicketIds = new Set(this.ticketRows.map((ticket) => ticket.id));
  }

  clearTicketSelection(): void {
    this.selectedTicketIds.clear();
  }

  async bulkToggleTicketVisibility(): Promise<void> {
    const selectedIds = Array.from(this.selectedTicketIds);
    if (!selectedIds.length) return;
    const selectedRows = this.ticketRows.filter((row) => this.selectedTicketIds.has(row.id));
    const allHidden = selectedRows.every((row) => row.status.trim().toLowerCase() === 'hidden');
    const nextStatus = allHidden ? 'available' : 'hidden';

    const originalRows = this.ticketRows.map((row) => ({ ...row }));
    this.ticketRows = this.ticketRows.map((row) => {
      if (!this.selectedTicketIds.has(row.id)) return row;
      return {
        ...row,
        status: nextStatus,
        isHidden: nextStatus === 'hidden',
        statusLabel: 'Available'
      };
    });
    this.error = '';
    this.openTicketMenuId = null;

    try {
      await Promise.all(selectedIds.map((id) => this.directus.update('event_tickets', id, { status: nextStatus })));
    } catch {
      this.ticketRows = originalRows;
      this.error = 'Failed to update selected tickets.';
    }
  }

  async bulkDeleteTickets(): Promise<void> {
    const selectedIds = Array.from(this.selectedTicketIds);
    if (!selectedIds.length) return;
    this.confirmAction = 'bulk-delete-tickets';
    this.pendingBulkDeleteIds = selectedIds;
    this.pendingDeleteTicket = null;
    this.confirmDialogTitle = 'Delete tickets?';
    this.confirmDialogMessage = `Are you sure you want to delete ${selectedIds.length} selected ticket(s) from this event? This action cannot be undone.`;
    this.confirmDialogConfirmLabel = 'Delete Tickets';
    this.confirmDialogOpen = true;
    return;
  }

  private async performBulkDeleteTickets(selectedIds: string[]): Promise<void> {
    const originalRows = this.ticketRows.map((row) => ({ ...row }));
    this.ticketRows = this.ticketRows.filter((row) => !this.selectedTicketIds.has(row.id));
    this.selectedTicketIds.clear();
    this.openTicketMenuId = null;
    this.error = '';

    try {
      await Promise.all(selectedIds.map((id) => this.directus.remove('event_tickets', id)));
    } catch {
      this.ticketRows = originalRows;
      this.error = 'Failed to delete selected tickets.';
      this.syncTicketSelection();
    }
  }

  openCheckoutFormEditor(): void {
    this.checkoutModeDraft = this.checkoutMode;
    this.checkoutButtonTextDraft = this.checkoutButtonText;
    this.checkoutFieldsDraft = this.checkoutFields.map((field) => ({ ...field }));
    this.checkoutFieldsByTicketDraft = Object.fromEntries(
      Object.entries(this.checkoutFieldsByTicket).map(([ticketId, fields]) => [
        ticketId,
        fields.map((field) => ({ ...field }))
      ])
    );
    this.selectedCheckoutTicketIdDraft = this.selectedCheckoutTicketId || this.ticketRows[0]?.id || '';
    this.ensureCheckoutDraftTargets();
    this.showAddFieldMenu = false;
    this.isCheckoutFormEditing = true;
  }

  cancelCheckoutFormEditor(): void {
    this.isCheckoutFormEditing = false;
    this.showAddFieldMenu = false;
  }

  async saveCheckoutFormEditor(): Promise<void> {
    this.checkoutMode = this.checkoutModeDraft;
    this.checkoutButtonText = this.checkoutButtonTextDraft.trim() || 'Continue';
    this.checkoutFields = this.checkoutFieldsDraft.map((field) => ({ ...field }));
    this.checkoutFieldsByTicket = Object.fromEntries(
      Object.entries(this.checkoutFieldsByTicketDraft).map(([ticketId, fields]) => [
        ticketId,
        fields.map((field) => ({ ...field }))
      ])
    );
    this.selectedCheckoutTicketId = this.selectedCheckoutTicketIdDraft;
    this.isCheckoutFormEditing = false;
    this.showAddFieldMenu = false;
    try {
      await this.persistCheckoutConfigToDirectus();
      this.error = '';
    } catch {
      this.error = 'Failed to save checkout form settings.';
    }
  }

  openRegistrationSettingsEditor(): void {
    this.registrationStatusDraft = this.registrationStatus;
    this.isRegistrationSettingsEditing = true;
  }

  cancelRegistrationSettingsEditor(): void {
    this.isRegistrationSettingsEditing = false;
  }

  async saveRegistrationSettingsEditor(): Promise<void> {
    this.registrationStatus = this.registrationStatusDraft;
    this.isRegistrationSettingsEditing = false;
    try {
      await this.persistCheckoutSettingsOnly();
      this.error = '';
    } catch {
      this.error = 'Failed to save registration settings.';
    }
  }

  openTicketSettingsEditor(): void {
    this.maxTicketsPerOrderDraft = this.maxTicketsPerOrder;
    this.checkoutTimeLimitMinutesDraft = this.checkoutTimeLimitMinutes;
    this.isTicketSettingsEditing = true;
  }

  cancelTicketSettingsEditor(): void {
    this.isTicketSettingsEditing = false;
  }

  async saveTicketSettingsEditor(): Promise<void> {
    this.maxTicketsPerOrder = this.normalizePositiveInt(this.maxTicketsPerOrderDraft, this.maxTicketsPerOrder);
    this.checkoutTimeLimitMinutes = this.normalizePositiveInt(this.checkoutTimeLimitMinutesDraft, this.checkoutTimeLimitMinutes);
    this.isTicketSettingsEditing = false;
    try {
      await this.persistCheckoutSettingsOnly();
      this.error = '';
    } catch {
      this.error = 'Failed to save ticket settings.';
    }
  }

  normalizePositiveInt(value: unknown, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    const rounded = Math.round(parsed);
    return rounded > 0 ? rounded : fallback;
  }

  hasCheckoutField(fieldKey: string): boolean {
    return this.activeCheckoutFieldsDraft.some((field) => field.key === fieldKey);
  }

  setCheckoutModeDraft(mode: CheckoutMode): void {
    this.checkoutModeDraft = mode;
    if (mode === 'per_ticket' && !this.selectedCheckoutTicketIdDraft) {
      this.selectedCheckoutTicketIdDraft = this.ticketRows[0]?.id || '';
    }
    this.ensureCheckoutDraftTargets();
  }

  setSelectedCheckoutTicket(ticketId: string): void {
    this.selectedCheckoutTicketIdDraft = String(ticketId || '');
    this.ensureCheckoutDraftTargets();
  }

  toggleAddFieldMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showAddFieldMenu = !this.showAddFieldMenu;
  }

  addCheckoutField(field: CheckoutFieldOption): void {
    const target = this.getEditableCheckoutFieldsDraft();
    const exists = target.some((item) => item.key === field.key);
    if (!exists) {
      target.push({ ...field });
    }
    this.showAddFieldMenu = false;
  }

  removeCheckoutField(index: number): void {
    const target = this.getEditableCheckoutFieldsDraft();
    const field = target[index];
    if (!field || field.locked) return;
    target.splice(index, 1);
  }

  async duplicateTicket(ticket: TicketRow): Promise<void> {
    try {
      const currentSort = this.ticketRows.map((item) => item.sort).filter((value) => Number.isFinite(value));
      const nextSort = currentSort.length ? Math.max(...currentSort) + 1 : this.ticketRows.length + 1;
      const isFree = Math.round(ticket.priceValue) <= 0;
      await this.directus.create('event_tickets', {
        event_id: this.eventId,
        name: `${ticket.name} (copy)`,
        pricing_method: isFree ? 'free' : 'fixed',
        price: isFree ? 0 : (Number.isFinite(ticket.priceValue) ? ticket.priceValue : 0),
        quantity_mode: ticket.quantityMode || 'unlimited',
        quantity_total: ticket.quantityMode === 'limited' ? ticket.quantityTotal || 0 : null,
        status: ticket.status || 'available',
        sort: nextSort
      });
      this.openTicketMenuId = null;
      await this.loadTickets();
    } catch {
      this.error = 'Failed to duplicate ticket.';
    }
  }

  async deleteTicket(ticket: TicketRow): Promise<void> {
    this.confirmAction = 'delete-ticket';
    this.pendingDeleteTicket = ticket;
    this.pendingBulkDeleteIds = [];
    this.confirmDialogTitle = 'Delete ticket?';
    this.confirmDialogMessage = `Are you sure you want to delete "${ticket.name}" from this event? This action cannot be undone.`;
    this.confirmDialogConfirmLabel = 'Delete Ticket';
    this.confirmDialogOpen = true;
    return;
  }

  private async performDeleteTicket(ticket: TicketRow): Promise<void> {
    try {
      await this.directus.remove('event_tickets', ticket.id);
      this.openTicketMenuId = null;
      await this.loadTickets();
    } catch {
      this.error = 'Failed to delete ticket.';
    }
  }

  closeConfirmDialog(): void {
    if (this.confirmDialogBusy) return;
    this.confirmDialogOpen = false;
    this.confirmAction = null;
    this.pendingDeleteTicket = null;
    this.pendingBulkDeleteIds = [];
    this.pendingCancelScheduleItemId = null;
  }

  async confirmDialogProceed(): Promise<void> {
    if (!this.confirmAction || this.confirmDialogBusy) return;
    this.confirmDialogBusy = true;
    try {
      if (this.confirmAction === 'bulk-delete-tickets') {
        await this.performBulkDeleteTickets([...this.pendingBulkDeleteIds]);
      } else if (this.confirmAction === 'delete-ticket' && this.pendingDeleteTicket) {
        await this.performDeleteTicket(this.pendingDeleteTicket);
      } else if (this.confirmAction === 'cancel-schedule-item' && this.pendingCancelScheduleItemId !== null) {
        this.scheduleItemsDraft = this.scheduleItemsDraft.map((entry) => {
          if (entry.draftId !== this.pendingCancelScheduleItemId) return entry;
          return { ...entry, canceled: true };
        });
        this.saveScheduleDraftToStorage();
      }
      this.confirmDialogOpen = false;
      this.confirmAction = null;
      this.pendingDeleteTicket = null;
      this.pendingBulkDeleteIds = [];
      this.pendingCancelScheduleItemId = null;
    } finally {
      this.confirmDialogBusy = false;
    }
  }

  private async handleRoute(): Promise<void> {
    this.eventId = String(this.route.snapshot.paramMap.get('id') || '');
    this.canonicalEventIdForWrite = null;
    const urlSegments = this.route.snapshot.url.map((segment) => segment.path);
    let tab = String(this.route.snapshot.paramMap.get('tab') || '') as WorkspaceTabKey | '';
    if (!tab) {
      if (urlSegments.includes('features')) {
        tab = 'features';
      } else if (urlSegments.includes('settings')) {
        tab = 'settings';
      } else if (urlSegments.includes('tickets-seating')) {
        tab = 'tickets-seating';
      } else if (urlSegments.includes('orders')) {
        tab = 'orders';
      } else if (urlSegments.includes('guests')) {
        tab = 'guests';
      } else {
        tab = 'overview';
      }
    }
    const isScheduleSubview = urlSegments.includes('schedule');
    const orderIdFromRoute = this.extractOrderIdFromUrlSegments(urlSegments);
    const guestIdFromRoute = this.extractGuestIdFromUrlSegments(urlSegments);
    const isGuestAddSubview = urlSegments.includes('add') && urlSegments.includes('guests');
    if (!this.eventId) {
      void this.router.navigateByUrl('/events/published');
      return;
    }

    if (!this.tabs.some((item) => item.key === tab)) {
      void this.router.navigateByUrl(`/events/${encodeURIComponent(this.eventId)}/overview`);
      return;
    }
    this.activeTab = tab;
    this.scheduleSetupOpen = tab === 'features' && isScheduleSubview;
    this.orderDetailOpen = tab === 'orders' && !!orderIdFromRoute;
    this.orderDetailId = this.orderDetailOpen ? orderIdFromRoute : '';
    this.selectedOrder = null;
    this.guestAddOpen = tab === 'guests' && isGuestAddSubview;
    this.guestDetailOpen = tab === 'guests' && !isGuestAddSubview && !!guestIdFromRoute;
    this.guestDetailId = this.guestDetailOpen ? guestIdFromRoute : '';
    this.selectedGuest = null;
    await Promise.all([this.loadEvent(), this.loadTickets(), this.loadFeatures(), this.loadOrders(), this.loadGuests()]);
    await this.loadCheckoutConfig();
    this.loadScheduleDraftFromStorage();
    if (this.orderDetailOpen) {
      this.selectedOrder = this.orderRows.find((row) => row.id === this.orderDetailId) || null;
      if (!this.selectedOrder) this.error = 'Order not found.';
    }
    if (this.guestAddOpen) {
      this.onGuestTicketRowChanged();
    }
    if (this.guestDetailOpen) {
      this.selectedGuest = this.guestRows.find((guest) => guest.id === this.guestDetailId) || null;
      if (!this.selectedGuest) {
        this.error = 'Guest not found.';
      }
    }
    if (this.activeTab === 'features' && this.scheduleSetupOpen && !this.scheduleSetupFeatureId) {
      const scheduleFeature = this.featureCards.find((item) => item.key === 'schedule');
      this.scheduleSetupFeatureId = scheduleFeature?.id || '';
    }
  }

  private async loadEvent(): Promise<void> {
    this.error = '';
    try {
      const rows = await this.directus.list('events', {
        fields: 'id,title,status,start_date,location,event_type,image',
        'filter[id][_eq]': this.eventId,
        limit: 1
      });
      const row = rows[0];
      if (!row) {
        this.error = 'Event not found.';
        return;
      }
      const rawEventId = row['id'];
      if (typeof rawEventId === 'number' || typeof rawEventId === 'string') {
        this.canonicalEventIdForWrite = rawEventId;
      } else {
        this.canonicalEventIdForWrite = this.eventId;
      }
      const title = row['title'];
      const status = row['status'];
      const location = row['location'];
      const startDate = row['start_date'];
      const eventType = row['event_type'];
      const image = row['image'];

      this.eventTitle = typeof title === 'string' ? title : 'Untitled event';
      this.eventStatus = typeof status === 'string' ? status : 'draft';
      this.eventLocation = typeof location === 'string' ? location : '';
      this.eventType = typeof eventType === 'string' ? eventType : 'ticketed';
      const imageId =
        typeof image === 'string'
          ? image
          : (image && typeof image === 'object' && 'id' in image && typeof (image as { id?: unknown }).id === 'string'
              ? String((image as { id?: unknown }).id)
              : '');
      if (imageId) {
        this.eventImageUrl = this.directus.buildAssetUrl(imageId, { width: 128, height: 96, fit: 'cover' });
      }
      if (typeof startDate === 'string') {
        const parsed = new Date(startDate);
        this.eventStartAt = Number.isNaN(parsed.getTime()) ? null : parsed;
        this.startDateLabel = Number.isNaN(parsed.getTime())
          ? '—'
          : this.formatJakartaDateTime(parsed);
      } else {
        this.eventStartAt = null;
      }
    } catch {
      this.error = 'Failed to load event.';
    }
  }

  private toDateInput(value: Date): string {
    const parts = this.getJakartaParts(value);
    const year = parts.year;
    const month = parts.month;
    const day = parts.day;
    return `${year}-${month}-${day}`;
  }

  private toTimeInput(value: Date): string {
    const parts = this.getJakartaParts(value);
    const hours = parts.hour;
    const minutes = parts.minute;
    return `${hours}:${minutes}`;
  }

  private formatScheduleDayLabel(dateValue: string): string {
    const parsed = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return dateValue;
    return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  }

  formatScheduleTimeRange(startAt: Date, endAt: Date): string {
    return `${startAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: this.displayTimezone })} - ${endAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: this.displayTimezone })}`;
  }

  private parseDateTimeInput(dateValue: string, timeValue: string): Date | null {
    const iso = `${dateValue}T${timeValue}:00+07:00`;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatJakartaDateTime(value: Date): string {
    return value.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: this.displayTimezone
    });
  }

  private getJakartaParts(value: Date): { year: string; month: string; day: string; hour: string; minute: string } {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: this.displayTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(value);
    const mapped = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
      year: String(mapped['year'] || ''),
      month: String(mapped['month'] || ''),
      day: String(mapped['day'] || ''),
      hour: String(mapped['hour'] || '00'),
      minute: String(mapped['minute'] || '00')
    };
  }

  private loadScheduleDraftFromStorage(): void {
    void (async () => {
      const eventIdCandidates = this.eventIdCandidatesForFilter();
      for (const candidate of eventIdCandidates) {
        try {
          const rows = await this.directus.list('event_schedule_items', {
            fields: 'id,name,start_at,end_at,place,tags,description,hidden,canceled,sort,status',
            'filter[event_id][_eq]': candidate,
            'filter[status][_neq]': 'archived',
            sort: 'sort,start_at',
            limit: 2000
          });
          if (rows.length) {
            this.scheduleItemsDraft = rows
              .map((row, index) => {
                const startAt = new Date(String(row['start_at'] || ''));
                const endAt = new Date(String(row['end_at'] || ''));
                if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) return null;
                return {
                  draftId: index + 1,
                  name: String(row['name'] || 'Untitled item'),
                  startAt,
                  endAt,
                  place: String(row['place'] || ''),
                  tags: String(row['tags'] || ''),
                  description: String(row['description'] || ''),
                  hidden: Boolean(row['hidden']),
                  canceled: Boolean(row['canceled'])
                } as ScheduleItemDraft;
              })
              .filter((item): item is ScheduleItemDraft => !!item);
            const maxId = this.scheduleItemsDraft.reduce((acc, item) => Math.max(acc, item.draftId), 0);
            this.nextScheduleDraftId = maxId + 1;
            return;
          }
        } catch {
          // Continue fallback chain.
        }
      }

      const scheduleFeature = this.featureCards.find((item) => item.key === 'schedule');
      const rawItems = Array.isArray(scheduleFeature?.config?.['schedule_items'])
        ? (scheduleFeature?.config?.['schedule_items'] as Array<Record<string, unknown>>)
        : [];
      this.scheduleItemsDraft = rawItems
        .map((item, index) => {
          const startAt = new Date(String(item['startAt'] || item['start_at'] || ''));
          const endAt = new Date(String(item['endAt'] || item['end_at'] || ''));
          if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) return null;
          return {
            draftId: typeof item['draftId'] === 'number' ? item['draftId'] : index + 1,
            name: String(item['name'] || 'Untitled item'),
            startAt,
            endAt,
            place: String(item['place'] || ''),
            tags: String(item['tags'] || ''),
            description: String(item['description'] || ''),
            hidden: Boolean(item['hidden']),
            canceled: Boolean(item['canceled'])
          } as ScheduleItemDraft;
        })
        .filter((item): item is ScheduleItemDraft => !!item);
      const maxId = this.scheduleItemsDraft.reduce((acc, item) => Math.max(acc, item.draftId), 0);
      this.nextScheduleDraftId = maxId + 1;
    })();
  }

  private saveScheduleDraftToStorage(): void {
    void this.persistScheduleItemsToDirectus().catch((error) => {
      this.featuresError = `Failed to save schedule: ${this.extractErrorMessage(error)}`;
    });
  }

  private async persistScheduleItemsToDirectus(): Promise<void> {
    if (!this.eventId) return;
    const scheduleFeature = this.featureCards.find((item) => item.key === 'schedule');
    if (!scheduleFeature) return;
    const eventIdForWrite = this.normalizedEventIdForWrite();
    if (eventIdForWrite === null) return;

    let assignmentId = scheduleFeature.assignmentId ? String(scheduleFeature.assignmentId).trim() : '';
    if (!assignmentId) {
      const created = await this.directus.create('events_features', {
        event_id: eventIdForWrite,
        feature_id: scheduleFeature.id,
        enabled: true
      });
      assignmentId = String(created['id'] ?? '').trim();
      scheduleFeature.assignmentId = assignmentId || null;
      scheduleFeature.selected = true;
    }
    if (!assignmentId) return;

    const existingScheduleRows = await this.directus.list('event_schedule_items', {
      fields: 'id',
      'filter[event_id][_eq]': eventIdForWrite,
      limit: 2000
    });
    for (const row of existingScheduleRows) {
      const id = String(row['id'] ?? '').trim();
      if (!id) continue;
      await this.directus.remove('event_schedule_items', id);
    }

    const scheduleItems = this.scheduleItemsDraft.map((item) => ({
      name: item.name,
      start_at: item.startAt.toISOString(),
      end_at: item.endAt.toISOString(),
      place: item.place || null,
      tags: item.tags || null,
      description: item.description || null,
      hidden: !!item.hidden,
      canceled: !!item.canceled
    }));
    for (let index = 0; index < scheduleItems.length; index += 1) {
      await this.directus.create('event_schedule_items', {
        event_id: eventIdForWrite,
        status: 'draft',
        sort: index + 1,
        ...scheduleItems[index]
      });
    }

    const existingConfig =
      scheduleFeature.config && typeof scheduleFeature.config === 'object'
        ? { ...(scheduleFeature.config as Record<string, unknown>) }
        : {};
    const nextConfig = { ...existingConfig, schedule_items_count: scheduleItems.length };
    await this.directus.update('events_features', assignmentId, { enabled: true, config_json: nextConfig });
    scheduleFeature.config = nextConfig;
    scheduleFeature.selected = true;
    this.featuresError = '';
  }

  private eventIdCandidatesForFilter(): Array<string | number> {
    const candidates = new Set<string | number>();
    const normalized = this.normalizedEventIdForWrite();
    if (typeof normalized === 'number' && Number.isFinite(normalized)) {
      candidates.add(normalized);
      candidates.add(String(normalized));
    } else if (typeof normalized === 'string') {
      const trimmed = normalized.trim();
      if (trimmed) {
        candidates.add(trimmed);
        const parsed = Number(trimmed);
        if (Number.isFinite(parsed)) candidates.add(parsed);
      }
    }
    const routeId = String(this.eventId || '').trim();
    if (routeId) {
      candidates.add(routeId);
      const parsed = Number(routeId);
      if (Number.isFinite(parsed)) candidates.add(parsed);
    }
    return Array.from(candidates).filter((value) => String(value).trim() !== '');
  }

  private async loadTickets(): Promise<void> {
    this.ticketRows = [];
    this.selectedTicketIds.clear();
    if (!this.eventId) return;
    try {
      const rows = await this.directus.list('event_tickets', {
        fields: 'id,name,price,status,quantity_mode,quantity_total,sort',
        'filter[event_id][_eq]': this.eventId,
        sort: 'sort',
        limit: 200
      });
      this.error = '';
      this.ticketRows = rows.map((row) => {
        const id = String(row['id'] ?? '');
        const name = typeof row['name'] === 'string' && row['name'].trim() ? row['name'] : 'Untitled ticket';
        const priceRaw = row['price'];
        const price = typeof priceRaw === 'number' ? priceRaw : Number(priceRaw || 0);
        const statusRaw = typeof row['status'] === 'string' ? row['status'] : 'available';
        const normalizedStatus = statusRaw.trim().toLowerCase();
        const mode = typeof row['quantity_mode'] === 'string' ? row['quantity_mode'] : 'unlimited';
        const qtyRaw = row['quantity_total'];
        const qty = typeof qtyRaw === 'number' ? qtyRaw : Number(qtyRaw || 0);
        const sortRaw = row['sort'];
        return {
          id,
          name,
          priceLabel: new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(price),
          soldLabel: mode === 'limited' ? `0/${qty || 0}` : '0/unlimited',
          statusLabel: normalizedStatus === 'hidden' ? 'Available' : statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1),
          isHidden: normalizedStatus === 'hidden',
          status: statusRaw,
          priceValue: price,
          quantityMode: mode,
          quantityTotal: mode === 'limited' ? qty || 0 : null,
          sort: typeof sortRaw === 'number' ? sortRaw : Number(sortRaw || 0)
        };
      });
      this.recomputeTicketSoldLabels();
      this.ensureCheckoutTargets();
      this.ensureCheckoutDraftTargets();
      this.syncTicketSelection();
    } catch {
      this.error = 'Failed to load tickets.';
      this.ticketRows = [];
      this.selectedTicketIds.clear();
    }
  }

  private async loadOrders(): Promise<void> {
    this.orderRows = [];
    if (!this.eventId) return;
    this.ordersLoading = true;
    try {
      let rows: Record<string, unknown>[] = [];
      const normalizedEventId = this.normalizedEventIdForWrite();
      const filterCandidates = new Set<string | number>();
      if (typeof normalizedEventId === 'number' && Number.isFinite(normalizedEventId)) {
        filterCandidates.add(normalizedEventId);
        filterCandidates.add(String(normalizedEventId));
      } else if (typeof normalizedEventId === 'string') {
        const trimmed = normalizedEventId.trim();
        if (trimmed) {
          filterCandidates.add(trimmed);
          const parsed = Number(trimmed);
          if (Number.isFinite(parsed)) filterCandidates.add(parsed);
        }
      }
      const routeParsed = Number(String(this.eventId).trim());
      if (Number.isFinite(routeParsed)) filterCandidates.add(routeParsed);
      filterCandidates.add(String(this.eventId).trim());
      const candidateList = Array.from(filterCandidates).filter((value) => String(value).trim() !== '');

      for (const eventIdCandidate of candidateList) {
        try {
          rows = await this.directus.list('event_orders', {
            fields: '*',
            'filter[event_id][_eq]': eventIdCandidate,
            limit: 300
          });
          if (rows.length) break;
        } catch {
          rows = [];
        }
      }

      if (!rows.length) {
        for (const eventIdCandidate of candidateList) {
          try {
            rows = await this.directus.list('orders', {
              fields: '*',
              'filter[event_id][_eq]': eventIdCandidate,
              limit: 300
            });
            if (rows.length) break;
          } catch {
            rows = [];
          }
        }
      }
      let attendeeRows: Record<string, unknown>[] = [];
      for (const eventIdCandidate of candidateList) {
        try {
          attendeeRows = await this.directus.list('event_attendees', {
            fields: 'id,order_id,ticket_id,holder_first_name,holder_last_name,holder_email,holder_phone,ticket_number,checkin_status',
            'filter[event_id][_eq]': eventIdCandidate,
            limit: 2000
          });
          if (attendeeRows.length) break;
        } catch {
          attendeeRows = [];
        }
      }

      const attendeesByOrder = new Map<string, Array<Record<string, unknown>>>();
      for (const attendee of attendeeRows) {
        const orderId = this.extractRelationId(attendee['order_id']);
        if (!orderId) continue;
        const bucket = attendeesByOrder.get(orderId) || [];
        bucket.push(attendee);
        attendeesByOrder.set(orderId, bucket);
      }
      const ticketNameById = new Map(this.ticketRows.map((ticket) => [ticket.id, ticket.name]));
      const ticketPriceById = new Map(this.ticketRows.map((ticket) => [ticket.id, ticket.priceValue]));

      this.orderRows = rows.map((row) => {
        const id = String(row['id'] ?? '');
        const linkedAttendees = attendeesByOrder.get(id) || [];
        const numberLabel =
          typeof row['order_number'] === 'string'
            ? row['order_number']
            : typeof row['code'] === 'string'
              ? row['code']
              : id ? id.slice(0, 8).toUpperCase() : '—';
        const paymentReferenceRaw = String(row['payment_reference'] ?? '').trim();
        let buyerNameFromRef = '';
        let buyerFirstNameFromRef = '';
        let buyerLastNameFromRef = '';
        let buyerEmailFromRef = '';
        if (paymentReferenceRaw.startsWith('{') && paymentReferenceRaw.endsWith('}')) {
          try {
            const parsed = JSON.parse(paymentReferenceRaw) as {
              buyer_name?: unknown;
              buyer_first_name?: unknown;
              buyer_last_name?: unknown;
              buyer_email?: unknown;
            };
            if (typeof parsed.buyer_name === 'string') buyerNameFromRef = parsed.buyer_name.trim();
            if (typeof parsed.buyer_first_name === 'string') buyerFirstNameFromRef = parsed.buyer_first_name.trim();
            if (typeof parsed.buyer_last_name === 'string') buyerLastNameFromRef = parsed.buyer_last_name.trim();
            if (typeof parsed.buyer_email === 'string') buyerEmailFromRef = parsed.buyer_email.trim();
          } catch {
            // ignore invalid JSON
          }
        }
        const firstAttendee = linkedAttendees[0];
        const buyerProfileId = '';
        const attendeeName = this.composePersonName(firstAttendee?.['holder_first_name'], firstAttendee?.['holder_last_name']);
        const orderBuyerName = this.composePersonName(row['buyer_first_name'], row['buyer_last_name'], row['buyer_name']);
        const paymentRefName = this.composePersonName(buyerFirstNameFromRef, buyerLastNameFromRef, buyerNameFromRef);
        const buyerName =
          paymentRefName ||
          attendeeName ||
          orderBuyerName ||
          (typeof row['name'] === 'string'
            ? row['name']
            : typeof row['email'] === 'string'
              ? row['email']
              : 'Guest');
        const buyerEmail =
          buyerEmailFromRef ||
          String(firstAttendee?.['holder_email'] ?? '').trim() ||
          String(row['email'] ?? '').trim();
        const rawStatus = String(row['status'] ?? '').trim().toLowerCase();
        const status = this.normalizeOrderStatus(rawStatus);
        const attendeesTotal = linkedAttendees.reduce((sum, attendee) => {
          const ticketId = String(attendee['ticket_id'] ?? '').trim();
          const value = Number(ticketPriceById.get(ticketId) ?? 0);
          return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
        const rowTotalRaw = Number(row['grand_total'] ?? row['total_amount'] ?? row['amount'] ?? row['total'] ?? row['subtotal'] ?? 0);
        const totalAmount = Number.isFinite(rowTotalRaw) && rowTotalRaw > 0
          ? rowTotalRaw
          : linkedAttendees.length
            ? attendeesTotal
            : 0;
        const ticketCount = linkedAttendees.length
          ? linkedAttendees.length
          : Number(row['ticket_count'] ?? row['qty'] ?? row['quantity'] ?? 0);
        const createdRaw = row['date_created'] ?? row['created_at'] ?? row['created_on'] ?? row['expires_at'];
        const createdAt = typeof createdRaw === 'string' ? new Date(createdRaw) : null;
        const lines = linkedAttendees.map((attendee) => {
          const ticketId = this.extractRelationId(attendee['ticket_id']);
          const price = Number(ticketPriceById.get(ticketId) ?? 0);
          return {
            ticketLabel: ticketNameById.get(ticketId) || 'Ticket',
            ticketNumber: String(attendee['ticket_number'] ?? '').trim(),
            guestName: this.composePersonName(
              attendee['holder_first_name'],
              attendee['holder_last_name']
            ) || 'Guest',
            price: Number.isFinite(price) ? price : 0
          };
        });
        return {
          id,
          numberLabel,
          buyerName: buyerName || buyerEmailFromRef || 'Guest',
          buyerEmail,
          buyerProfileId,
          status,
          statusLabel: this.orderStatusLabel(status),
          totalAmount: Number.isFinite(totalAmount) ? totalAmount : 0,
          ticketCount: Number.isFinite(ticketCount) ? ticketCount : 0,
          createdAt: createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt : null,
          lines
        } satisfies OrderRow;
      });
      this.recomputeTicketSoldLabels();
      const visibleIds = new Set(this.orderRows.map((row) => row.id));
      this.selectedOrderIds = new Set(Array.from(this.selectedOrderIds).filter((id) => visibleIds.has(id)));
      if (this.orderDetailOpen && this.orderDetailId) {
        this.selectedOrder = this.orderRows.find((row) => row.id === this.orderDetailId) || null;
      }
    } catch {
      this.orderRows = [];
      this.recomputeTicketSoldLabels();
      this.selectedOrderIds = new Set<string>();
    } finally {
      this.ordersLoading = false;
    }
  }

  private recomputeTicketSoldLabels(): void {
    if (!this.ticketRows.length) return;
    const soldByTicketName = new Map<string, number>();
    this.settledOrderRows.forEach((order) => {
      order.lines.forEach((line) => {
        const ticketName = String(line.ticketLabel || '').trim();
        if (!ticketName) return;
        soldByTicketName.set(ticketName, (soldByTicketName.get(ticketName) || 0) + 1);
      });
    });
    this.ticketRows = this.ticketRows.map((ticket) => {
      const sold = soldByTicketName.get(ticket.name) || 0;
      return {
        ...ticket,
        soldLabel: ticket.quantityMode === 'limited'
          ? `${sold}/${ticket.quantityTotal || 0}`
          : `${sold}/unlimited`
      };
    });
  }

  private async loadGuests(): Promise<void> {
    this.guestRows = [];
    if (!this.eventId) return;
    this.guestsLoading = true;
    try {
      const normalizedEventId = this.normalizedEventIdForWrite();
      const filterCandidates = new Set<string | number>();
      if (typeof normalizedEventId === 'number' && Number.isFinite(normalizedEventId)) {
        filterCandidates.add(normalizedEventId);
        filterCandidates.add(String(normalizedEventId));
      } else if (typeof normalizedEventId === 'string') {
        const trimmed = normalizedEventId.trim();
        if (trimmed) {
          filterCandidates.add(trimmed);
          const parsed = Number(trimmed);
          if (Number.isFinite(parsed)) filterCandidates.add(parsed);
        }
      }
      const routeParsed = Number(String(this.eventId).trim());
      if (Number.isFinite(routeParsed)) filterCandidates.add(routeParsed);
      filterCandidates.add(String(this.eventId).trim());
      const candidateList = Array.from(filterCandidates).filter((value) => String(value).trim() !== '');

      let attendeeRows: Record<string, unknown>[] = [];
      for (const eventIdCandidate of candidateList) {
        try {
          attendeeRows = await this.directus.list('event_attendees', {
            fields: 'id,event_id,order_id,ticket_id,profile_id,holder_first_name,holder_last_name,holder_email,holder_phone,ticket_number,checkin_status',
            'filter[event_id][_eq]': eventIdCandidate,
            limit: 500
          });
          if (attendeeRows.length) break;
        } catch {
          attendeeRows = [];
        }
      }
      const rows: Array<Record<string, unknown> & { __source_collection: 'event_attendees' }> =
        attendeeRows.map((row) => ({ ...(row as Record<string, unknown>), __source_collection: 'event_attendees' as const }));
      const dedupedRows: typeof rows = [];
      const seen = new Set<string>();
      for (const row of rows) {
        const orderId = this.extractRelationId(row['order_id']);
        const ticketNumber = String(row['ticket_number'] ?? '').trim();
        const email = String(row['holder_email'] ?? '').trim().toLowerCase();
        const key = `${orderId}:${ticketNumber}:${email}`;
        if (seen.has(key)) continue;
        seen.add(key);
        dedupedRows.push(row);
      }

      const ticketNameById = new Map(this.ticketRows.map((ticket) => [ticket.id, ticket.name]));
      const ticketPriceById = new Map(this.ticketRows.map((ticket) => [ticket.id, ticket.priceValue]));
      const orderById = new Map<string, Record<string, unknown>>();
      if (attendeeRows.length) {
        for (const eventIdCandidate of candidateList) {
          try {
            const orderRows = await this.directus.list('event_orders', {
              fields: 'id,status,date_created,payment_reference',
              'filter[event_id][_eq]': eventIdCandidate,
              limit: 500
            });
            for (const order of orderRows) {
              const id = String(order['id'] ?? '').trim();
              if (id) orderById.set(id, order);
            }
            if (orderById.size) break;
          } catch {
            // continue
          }
        }
      }

      this.guestRows = dedupedRows.map((row, index) => {
        const sourceCollection = row.__source_collection;
        const sourceId = String(row['id'] ?? `guest-${index + 1}`);
        const id = sourceId;
        const firstName = String(row['holder_first_name'] ?? row['first_name'] ?? row['firstname'] ?? '').trim();
        const lastName = String(row['holder_last_name'] ?? row['last_name'] ?? row['lastname'] ?? '').trim();
        const holderName = this.composePersonName(firstName, lastName);
        const email = String(row['email'] ?? row['holder_email'] ?? '').trim();
        const whatsapp = String(row['phone'] ?? row['holder_phone'] ?? '').trim();
        const profileId = String(row['profile_id'] ?? '').trim();
        const ticketId = this.extractRelationId(row['ticket_id'] ?? row['event_ticket_id']);
        const ticketLabel =
          ticketNameById.get(ticketId) ||
          String(row['ticket_name'] ?? row['ticket_type'] ?? '').trim() ||
          'Ticket';
        const ticketPriceValueRaw = Number(ticketPriceById.get(ticketId) ?? row['ticket_price'] ?? row['price'] ?? row['total'] ?? 0);
        const ticketPriceValue = Number.isFinite(ticketPriceValueRaw) ? ticketPriceValueRaw : 0;
        const orderId = this.extractRelationId(row['order_id'] ?? row['event_order_id']);
        const order = orderId ? orderById.get(orderId) : undefined;
        const orderStatusRaw = String(order?.['status'] ?? '').trim().toLowerCase();
        const normalizedOrderStatus = this.normalizeOrderStatus(orderStatusRaw);
        const paid = normalizedOrderStatus === 'paid' || Boolean(row['paid'] ?? false);
        const statusKind: 'paid' | 'not_paid' | 'free' =
          ticketPriceValue <= 0 ? 'free' : paid ? 'paid' : 'not_paid';
        const ticketNumber = String(row['ticket_number'] ?? '').trim();
        let buyerNameFromRef = '';
        let buyerFirstNameFromRef = '';
        let buyerLastNameFromRef = '';
        let buyerEmailFromRef = '';
        const paymentReferenceRaw = String(order?.['payment_reference'] ?? '').trim();
        if (paymentReferenceRaw.startsWith('{') && paymentReferenceRaw.endsWith('}')) {
          try {
            const parsed = JSON.parse(paymentReferenceRaw) as {
              buyer_name?: unknown;
              buyer_first_name?: unknown;
              buyer_last_name?: unknown;
              buyer_email?: unknown;
            };
            if (typeof parsed.buyer_name === 'string') buyerNameFromRef = parsed.buyer_name.trim();
            if (typeof parsed.buyer_first_name === 'string') buyerFirstNameFromRef = parsed.buyer_first_name.trim();
            if (typeof parsed.buyer_last_name === 'string') buyerLastNameFromRef = parsed.buyer_last_name.trim();
            if (typeof parsed.buyer_email === 'string') buyerEmailFromRef = parsed.buyer_email.trim();
          } catch {
            // ignore invalid JSON
          }
        }
        const buyerName = this.composePersonName(
          buyerFirstNameFromRef || row['buyer_first_name'],
          buyerLastNameFromRef || row['buyer_last_name'],
          buyerNameFromRef || row['buyer_name']
        ) ||
          [firstName, lastName].filter(Boolean).join(' ').trim() ||
          holderName ||
          'Guest';
        const buyerEmail = buyerEmailFromRef || String(row['buyer_email'] ?? email).trim();
        const orderDateRaw = String(order?.['date_created'] ?? row['order_date'] ?? row['date_created'] ?? row['created_at'] ?? '').trim();
        const orderDate = orderDateRaw ? new Date(orderDateRaw) : null;
        const rawCheckin = String(row['checkin_status'] ?? '').trim().toLowerCase();
        const checkedIn = rawCheckin
          ? rawCheckin === 'checked_in'
          : Boolean(row['checked_in'] ?? row['is_checked_in'] ?? row['attended'] ?? false);
        return {
          id,
          sourceCollection,
          sourceId,
          name:
            [firstName, lastName].filter(Boolean).join(' ').trim() ||
            holderName ||
            String(row['name'] ?? '').trim() ||
            'Guest',
          email,
          whatsapp,
          profileId,
          ticketId,
          ticketLabel,
          ticketPriceValue,
          ticketPriceLabel: this.formatIdr(ticketPriceValue),
          ticketNumber,
          statusLabel: statusKind === 'paid' ? 'Paid' : statusKind === 'free' ? 'Free' : 'Not Paid',
          statusKind,
          buyerName,
          buyerEmail,
          orderDate: orderDate && !Number.isNaN(orderDate.getTime()) ? orderDate : null,
          orderId,
          checkedIn
        } satisfies GuestRow;
      });
      if (this.guestDetailOpen && this.guestDetailId) {
        this.selectedGuest = this.guestRows.find((guest) => guest.id === this.guestDetailId) || null;
      }
    } catch {
      this.guestRows = [];
    } finally {
      this.guestsLoading = false;
    }
  }

  private extractGuestIdFromUrlSegments(urlSegments: string[]): string {
    const guestsIndex = urlSegments.lastIndexOf('guests');
    if (guestsIndex < 0) return '';
    const candidate = urlSegments[guestsIndex + 1] || '';
    if (!candidate || candidate === 'add') return '';
    return decodeURIComponent(candidate);
  }

  private extractOrderIdFromUrlSegments(urlSegments: string[]): string {
    const ordersIndex = urlSegments.lastIndexOf('orders');
    if (ordersIndex < 0) return '';
    const candidate = urlSegments[ordersIndex + 1] || '';
    if (!candidate) return '';
    return decodeURIComponent(candidate);
  }

  private normalizedEventIdForWrite(): string | number | null {
    const base = this.canonicalEventIdForWrite ?? this.eventId;
    if (typeof base === 'number' && Number.isFinite(base)) return base;
    if (typeof base === 'string') {
      const trimmed = base.trim();
      if (!trimmed) return null;
      const numeric = Number(trimmed);
      return Number.isFinite(numeric) ? numeric : trimmed;
    }
    if (base && typeof base === 'object') {
      const nested = (base as { id?: unknown }).id;
      if (typeof nested === 'number' && Number.isFinite(nested)) return nested;
      if (typeof nested === 'string') {
        const trimmed = nested.trim();
        if (!trimmed) return null;
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? numeric : trimmed;
      }
    }
    return null;
  }

  private resetGuestAddForm(): void {
    this.guestAddError = '';
    this.guestTicketInputRows = [
      {
        id: 1,
        ticketId: this.ticketRows[0]?.id || '',
        quantity: 1
      }
    ];
    this.nextGuestTicketInputRowId = 2;
    this.nextGuestAttendeeInputId = 1;
    this.guestAttendeeInputs = [];
    this.guestBuyerFirstName = '';
    this.guestBuyerLastName = '';
    this.guestBuyerEmail = '';
    this.guestBuyerWhatsapp = '';
    this.useBuyerDetailsForFirstGuest = false;
    this.guestOrderStatus = 'not_paid';
    this.guestSendTicketsByEmail = true;
    this.rebuildGuestAttendeeInputs();
  }

  private rebuildGuestAttendeeInputs(): void {
    const orderedRows = [...this.guestTicketInputRows].sort((a, b) => a.id - b.id);
    const totalQty = orderedRows.reduce((sum, row) => sum + Math.max(0, Number(row.quantity || 0)), 0);
    const cappedQty = Math.min(totalQty, Math.max(1, this.maxTicketsPerOrder || 1));

    const preservedBySlot = new Map<string, GuestTicketAttendeeInput>(
      this.guestAttendeeInputs.map((entry) => [`${entry.sourceRowId}:${entry.slotIndex}`, entry])
    );

    const next: GuestTicketAttendeeInput[] = [];
    let created = 0;
    for (const row of orderedRows) {
      const qty = Math.max(0, Number(row.quantity || 0));
      const ticket = this.ticketRows.find((item) => item.id === row.ticketId);
      const ticketLabel = ticket?.name || 'Ticket';
      for (let i = 0; i < qty; i++) {
        if (created >= cappedQty) break;
        const slotKey = `${row.id}:${i}`;
        const preserved = preservedBySlot.get(slotKey);
        const id = preserved?.id || this.nextGuestAttendeeInputId++;
        next.push({
          id,
          sourceRowId: row.id,
          slotIndex: i,
          ticketId: row.ticketId,
          ticketLabel,
          fields: preserved ? { ...preserved.fields } : {}
        });
        created += 1;
      }
    }
    this.guestAttendeeInputs = next;
    if (this.useBuyerDetailsForFirstGuest) {
      this.syncFirstGuestFromBuyerDetails();
    }
  }

  private syncFirstGuestFromBuyerDetails(): void {
    const first = this.guestAttendeeInputs[0];
    if (!first) return;
    const nextFields: Record<string, string> = {
      ...first.fields,
      first_name: this.guestBuyerFirstName.trim(),
      last_name: this.guestBuyerLastName.trim(),
      email: this.guestBuyerEmail.trim(),
      whatsapp_number: this.guestBuyerWhatsapp.trim()
    };
    this.guestAttendeeInputs = this.guestAttendeeInputs.map((item, index) =>
      index === 0 ? { ...item, fields: nextFields } : item
    );
  }

  private extractErrorMessage(error: unknown): string {
    const err = error as { error?: { errors?: Array<{ message?: string }> }; message?: string };
    const apiMessage = err?.error?.errors?.[0]?.message;
    if (apiMessage && apiMessage.trim()) return apiMessage.trim();
    if (err?.message && err.message.trim()) return err.message.trim();
    return 'Unknown error';
  }

  private extractRelationId(value: unknown): string {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (value && typeof value === 'object' && 'id' in value) {
      const nested = (value as { id?: unknown }).id;
      if (typeof nested === 'string') return nested.trim();
      if (typeof nested === 'number' && Number.isFinite(nested)) return String(nested);
    }
    return '';
  }

  private async generateUniqueOrderNumber(): Promise<string> {
    for (let attempt = 0; attempt < 8; attempt++) {
      const candidate = this.generateOrderNumberCandidate();
      try {
        const rows = await this.directus.list('event_orders', {
          fields: 'id',
          'filter[order_number][_eq]': candidate,
          limit: 1
        });
        if (!rows.length) return candidate;
      } catch {
        return candidate;
      }
    }
    return this.generateOrderNumberCandidate();
  }

  private generateOrderNumberCandidate(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const segment = () =>
      Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    return `${segment()}-${segment()}-${segment()}`;
  }

  private async findProfileIdForBuyer(email: string): Promise<string> {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (normalizedEmail) {
      try {
        const rows = await this.directus.list('profiles', {
          fields: 'id,email',
          'filter[email][_eq]': normalizedEmail,
          limit: 1
        });
        const id = String(rows[0]?.['id'] ?? '').trim();
        if (id) return id;
      } catch {
        // ignore and continue fallback
      }
      try {
        const rows = await this.directus.list('profiles', {
          fields: 'id,email',
          'filter[email][_icontains]': normalizedEmail,
          limit: 5
        });
        const exact = rows.find((row) => String(row['email'] ?? '').trim().toLowerCase() === normalizedEmail);
        const id = String((exact || rows[0])?.['id'] ?? '').trim();
        if (id) return id;
      } catch {
        // ignore and continue fallback
      }
    }
    return '';
  }

  private composePersonName(first: unknown, last: unknown, fallback: unknown = ''): string {
    const firstName = typeof first === 'string' ? first.trim() : '';
    const lastName = typeof last === 'string' ? last.trim() : '';
    const split = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (split) return split;
    return typeof fallback === 'string' ? fallback.trim() : '';
  }

  private async findOrCreateGuestContactByEmail(
    email: string,
    firstName: string,
    lastName: string,
    whatsapp: string
  ): Promise<string> {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) return '';
    const cleanFirstName = String(firstName || '').trim();
    const cleanLastName = String(lastName || '').trim();
    const fullName = this.composePersonName(cleanFirstName, cleanLastName, 'Guest') || 'Guest';
    let existingId = '';
    try {
      const rows = await this.directus.list('guest_contacts', {
        fields: 'id,email',
        'filter[email][_eq]': normalizedEmail,
        limit: 1
      });
      existingId = String(rows[0]?.['id'] ?? '').trim();
      if (!existingId && rows.length) {
        existingId = String(rows[0]['id'] ?? '').trim();
      }
    } catch {
      existingId = '';
    }
    if (!existingId) {
      try {
        const rows = await this.directus.list('guest_contacts', {
          fields: 'id,email',
          'filter[email][_icontains]': normalizedEmail,
          limit: 5
        });
        const exact = rows.find((row) => String(row['email'] ?? '').trim().toLowerCase() === normalizedEmail);
        existingId = String((exact || rows[0])?.['id'] ?? '').trim();
      } catch {
        existingId = '';
      }
    }
    if (existingId) {
      await this.directus.update('guest_contacts', existingId, {
        first_name: cleanFirstName || null,
        last_name: cleanLastName || null,
        email: normalizedEmail,
        whatsapp: whatsapp || null,
        source: 'event_registration',
        last_seen_at: new Date().toISOString()
      });
      return existingId;
    }
    const created = await this.directus.create('guest_contacts', {
      first_name: cleanFirstName || null,
      last_name: cleanLastName || null,
      email: normalizedEmail,
      whatsapp: whatsapp || null,
      is_member: false,
      source: 'event_registration',
      last_seen_at: new Date().toISOString()
    });
    return String(created['id'] ?? '').trim();
  }

  private async tryCreateWithPayloadVariants(
    collection: string,
    payloads: Array<Record<string, unknown>>
  ): Promise<Record<string, unknown>> {
    let lastError: unknown = null;
    const failures: string[] = [];
    for (const payload of payloads) {
      try {
        return await this.directus.create(collection, payload);
      } catch (error) {
        lastError = error;
        const compactPayload = JSON.stringify(payload);
        failures.push(`${this.extractErrorMessage(error)} | payload=${compactPayload}`);
      }
    }
    const details = failures.length ? failures.join(' || ') : this.extractErrorMessage(lastError);
    throw new Error(`Failed to create item in ${collection}. Attempts: ${details}`);
  }

  private async resolveCheckoutCurrency(eventIdValue: number | string): Promise<string | number | undefined> {
    try {
      const rows = await this.directus.list('event_checkout_settings', {
        fields: 'currency',
        'filter[event_id][_eq]': String(eventIdValue),
        limit: 1
      });
      const raw = rows[0]?.['currency'];
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'string' && raw.trim()) {
        const numeric = Number(raw.trim());
        if (!Number.isNaN(numeric) && String(numeric) === raw.trim()) return numeric;
        return raw.trim();
      }
    } catch {
      // Fallback to collection default when checkout settings are unavailable.
    }
    return undefined;
  }

  private async loadCheckoutConfig(): Promise<void> {
    const normalizedEventId = this.normalizedEventIdForWrite();
    if (normalizedEventId === null) return;
    const eventIdNumber =
      typeof normalizedEventId === 'number' ? normalizedEventId : Number(String(normalizedEventId).trim());
    if (!Number.isFinite(eventIdNumber)) return;
    try {
      const settingsRows = await this.directus.list('event_checkout_settings', {
        fields: 'id,form_mode,max_tickets_per_order,currency,checkout_time_limit_minutes,registration_status',
        'filter[event_id][_eq]': eventIdNumber,
        limit: 1
      });
      const settings = settingsRows[0];
      if (settings) {
        this.checkoutSettingsId = String(settings['id'] ?? '').trim();
        const formMode = String(settings['form_mode'] ?? '').trim();
        if (formMode === 'per_order' || formMode === 'per_ticket') {
          this.checkoutMode = formMode;
          this.checkoutModeDraft = formMode;
        }
        this.maxTicketsPerOrder = this.normalizePositiveInt(settings['max_tickets_per_order'], this.maxTicketsPerOrder);
        this.maxTicketsPerOrderDraft = this.maxTicketsPerOrder;
        this.checkoutTimeLimitMinutes = this.normalizePositiveInt(
          settings['checkout_time_limit_minutes'],
          this.checkoutTimeLimitMinutes
        );
        this.checkoutTimeLimitMinutesDraft = this.checkoutTimeLimitMinutes;
        const registrationStatus = String(settings['registration_status'] ?? '').trim().toLowerCase();
        if (registrationStatus === 'open' || registrationStatus === 'closed') {
          this.registrationStatus = registrationStatus;
          this.registrationStatusDraft = registrationStatus;
        }
        const currency = String(settings['currency'] ?? '').trim();
        if (currency) this.checkoutCurrency = currency;
      }

      const fieldRows = await this.directus.list('event_form_fields', {
        fields: 'id,scope,field_key,label,field_type,required,sort,active,ticket_id,locked',
        'filter[event_id][_eq]': eventIdNumber,
        'filter[active][_neq]': false,
        sort: 'sort',
        limit: 1000
      });
      const baseFields: CheckoutFieldOption[] = [];
      const byTicket: Record<string, CheckoutFieldOption[]> = {};
      for (const row of fieldRows) {
        const fieldKey = String(row['field_key'] ?? '').trim();
        if (!fieldKey) continue;
        const label = String(row['label'] ?? '').trim() || fieldKey;
        const required = Boolean(row['required'] ?? false);
        const locked = Boolean(row['locked'] ?? false);
        const ticketId = this.extractRelationId(row['ticket_id']);
        const option: CheckoutFieldOption = { key: fieldKey, label, required, locked };
        if (ticketId) {
          if (!byTicket[ticketId]) byTicket[ticketId] = [];
          byTicket[ticketId].push(option);
        } else {
          baseFields.push(option);
        }
      }
      if (baseFields.length) {
        this.checkoutFields = this.normalizeCheckoutFields(baseFields);
      } else {
        this.checkoutFields = this.normalizeCheckoutFields(this.checkoutFields);
      }
      this.checkoutFieldsByTicket = {};
      for (const ticket of this.ticketRows) {
        const ticketFields = byTicket[ticket.id] || [];
        if (ticketFields.length) {
          this.checkoutFieldsByTicket[ticket.id] = this.normalizeCheckoutFields(ticketFields, ticket.id);
        } else {
          this.checkoutFieldsByTicket[ticket.id] = this.normalizeCheckoutFields(this.checkoutFields, ticket.id);
        }
      }
      this.checkoutFieldsDraft = this.checkoutFields.map((field) => ({ ...field }));
      this.checkoutFieldsByTicketDraft = Object.fromEntries(
        Object.entries(this.checkoutFieldsByTicket).map(([ticketId, fields]) => [
          ticketId,
          fields.map((field) => ({ ...field }))
        ])
      );
      this.ensureCheckoutTargets();
      this.ensureCheckoutDraftTargets();
    } catch {
      // Keep local defaults if Directus configuration rows are unavailable.
    }
  }

  private async persistCheckoutSettingsOnly(): Promise<void> {
    const normalizedEventId = this.normalizedEventIdForWrite();
    if (normalizedEventId === null) return;
    const eventIdNumber =
      typeof normalizedEventId === 'number' ? normalizedEventId : Number(String(normalizedEventId).trim());
    if (!Number.isFinite(eventIdNumber)) return;
    const payload: Record<string, unknown> = {
      event_id: eventIdNumber,
      form_mode: this.checkoutMode,
      max_tickets_per_order: this.maxTicketsPerOrder,
      currency: this.checkoutCurrency || 'IDR',
      tax_mode: 'none',
      checkout_time_limit_minutes: this.checkoutTimeLimitMinutes,
      require_login: false,
      allow_guest_checkout: true,
      registration_visibility: 'public',
      registration_status: this.registrationStatus
    };
    if (this.checkoutSettingsId) {
      await this.directus.update('event_checkout_settings', this.checkoutSettingsId, payload);
      return;
    }
    const created = await this.directus.create('event_checkout_settings', payload);
    this.checkoutSettingsId = String(created['id'] ?? '').trim();
  }

  private mapCheckoutFieldType(fieldKey: string): string {
    if (fieldKey === 'email') return 'email';
    if (fieldKey === 'whatsapp_number' || fieldKey === 'phone_number') return 'phone';
    if (fieldKey === 'date') return 'date';
    if (fieldKey === 'comments') return 'textarea';
    return 'text';
  }

  private async persistCheckoutConfigToDirectus(): Promise<void> {
    await this.persistCheckoutSettingsOnly();
    const normalizedEventId = this.normalizedEventIdForWrite();
    if (normalizedEventId === null) return;
    const eventIdNumber =
      typeof normalizedEventId === 'number' ? normalizedEventId : Number(String(normalizedEventId).trim());
    if (!Number.isFinite(eventIdNumber)) return;

    const existingRows = await this.directus.list('event_form_fields', {
      fields: 'id',
      'filter[event_id][_eq]': eventIdNumber,
      limit: 1000
    });
    for (const row of existingRows) {
      const id = String(row['id'] ?? '').trim();
      if (!id) continue;
      await this.directus.remove('event_form_fields', id);
    }

    const desired: Array<Record<string, unknown>> = [];
    if (this.checkoutMode === 'per_ticket') {
      for (const ticket of this.ticketRows) {
        const fields = this.normalizeCheckoutFields(this.checkoutFieldsByTicket[ticket.id] || this.checkoutFields, ticket.id);
        fields.forEach((field, index) => {
          desired.push({
            event_id: eventIdNumber,
            scope: 'attendee',
            ticket_id: ticket.id,
            field_key: field.key,
            label: field.label,
            field_type: this.mapCheckoutFieldType(field.key),
            required: field.required !== false,
            locked: !!field.locked,
            sort: index + 1,
            active: true
          });
        });
      }
    } else {
      const fields = this.normalizeCheckoutFields(this.checkoutFields);
      fields.forEach((field, index) => {
        desired.push({
          event_id: eventIdNumber,
          scope: 'order',
          field_key: field.key,
          label: field.label,
          field_type: this.mapCheckoutFieldType(field.key),
          required: field.required !== false,
          locked: !!field.locked,
          sort: index + 1,
          active: true
        });
      });
    }
    for (const payload of desired) {
      await this.directus.create('event_form_fields', payload);
    }
  }

  private createLineupSpeakerDraft(): LineupSpeakerDraft {
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fileId: '',
      assetUrl: '',
      croppedPreviewUrl: '',
      cropBoxX: 0,
      cropBoxY: 0,
      cropBoxWidth: 100,
      cropBoxHeight: 100,
      name: '',
      tagline: '',
      description: ''
    };
  }

  private parseLineupSpeakerRow(row: unknown): LineupSpeakerDraft | null {
    if (!row || typeof row !== 'object') return null;
    const item = row as Record<string, unknown>;
    const fileId = String(item['fileId'] || '').trim();
    if (!fileId) return null;
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fileId,
      assetUrl: this.directus.buildAssetUrl(fileId),
      croppedPreviewUrl: '',
      cropBoxX: Number(item['cropBoxX'] ?? 0),
      cropBoxY: Number(item['cropBoxY'] ?? 0),
      cropBoxWidth: Number(item['cropBoxWidth'] ?? 100),
      cropBoxHeight: Number(item['cropBoxHeight'] ?? 100),
      name: typeof item['name'] === 'string' ? item['name'] : '',
      tagline: typeof item['tagline'] === 'string' ? item['tagline'] : '',
      description: typeof item['description'] === 'string' ? item['description'] : ''
    };
  }

  private loadImageForCrop(imageUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load image'));
      image.src = imageUrl;
    });
  }

  private async generateLineupCroppedPreviewDataUrl(
    imageUrl: string,
    cropBoxX: number,
    cropBoxY: number,
    cropBoxWidth: number,
    cropBoxHeight: number
  ): Promise<string | null> {
    if (!imageUrl) return null;
    try {
      const imageEl = await this.loadImageForCrop(imageUrl);
      const naturalWidth = Math.max(1, imageEl.naturalWidth || imageEl.width);
      const naturalHeight = Math.max(1, imageEl.naturalHeight || imageEl.height);
      const boxLeft = Math.max(0, Math.min(100, cropBoxX)) / 100;
      const boxTop = Math.max(0, Math.min(100, cropBoxY)) / 100;
      const boxWidth = Math.max(1, Math.min(100, cropBoxWidth)) / 100;
      const boxHeight = Math.max(1, Math.min(100, cropBoxHeight)) / 100;
      const sourceLeft = boxLeft * naturalWidth;
      const sourceTop = boxTop * naturalHeight;
      const sourceWidth = boxWidth * naturalWidth;
      const sourceHeight = boxHeight * naturalHeight;
      const sx = Math.max(0, Math.min(naturalWidth - 1, sourceLeft));
      const sy = Math.max(0, Math.min(naturalHeight - 1, sourceTop));
      const sw = Math.max(1, Math.min(naturalWidth - sx, sourceWidth));
      const sh = Math.max(1, Math.min(naturalHeight - sy, sourceHeight));

      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 640;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imageEl, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.92);
    } catch {
      return null;
    }
  }

  private clampLineupCropBoxToImageBounds(): void {
    const b = this.lineupCropImageBounds;
    const ratio = Math.max(0.0001, this.lineupCropFrameAspectRatio);
    const maxWidth = Math.min(b.width, b.height / ratio);
    this.lineupCropBoxWidth = Math.max(8, Math.min(this.lineupCropBoxWidth, maxWidth));
    this.lineupCropBoxHeight = this.lineupCropBoxWidth * ratio;
    const maxX = b.x + b.width - this.lineupCropBoxWidth;
    const maxY = b.y + b.height - this.lineupCropBoxHeight;
    this.lineupCropBoxX = Math.max(b.x, Math.min(maxX, this.lineupCropBoxX));
    this.lineupCropBoxY = Math.max(b.y, Math.min(maxY, this.lineupCropBoxY));
  }

  private async loadFeatures(): Promise<void> {
    this.featureCards = [];
    this.featuresError = '';
    if (!this.eventId) return;
    this.featuresLoading = true;
    try {
      let [catalogRows, assignmentRows] = await Promise.all([
        this.directus.list('event_features', {
          fields: 'id,key,name,description,active',
          limit: 200
        }),
        this.directus.list('events_features', {
          fields: 'id,feature_id,enabled,config_json',
          'filter[event_id][_eq]': this.eventId,
          limit: 200
        })
      ]);
      if (!catalogRows.length) {
        catalogRows = await this.seedDefaultFeatureCatalog();
      }
      const targetKeys = new Set(['video_conference', 'schedule', 'seating_map', 'lineup']);
      const assignmentByFeatureId = new Map<string, { id: string; enabled: boolean; config: Record<string, unknown> | null }>();
      for (const row of assignmentRows) {
        const assignmentId = String(row['id'] ?? '');
        const enabledRaw = row['enabled'];
        const enabled = typeof enabledRaw === 'boolean' ? enabledRaw : enabledRaw !== false;
        const configRaw = row['config_json'];
        const config = configRaw && typeof configRaw === 'object' ? (configRaw as Record<string, unknown>) : null;
        const rawFeature = row['feature_id'];
        const featureId = typeof rawFeature === 'string'
          ? rawFeature
          : (rawFeature && typeof rawFeature === 'object' && 'id' in rawFeature ? String((rawFeature as { id?: unknown }).id || '') : '');
        if (!featureId || !assignmentId) continue;
        assignmentByFeatureId.set(featureId, { id: assignmentId, enabled, config });
      }

      const normalizeFeatureKey = (raw: string): string => {
        const key = raw.trim().toLowerCase().replace(/[-\s]+/g, '_');
        if (key === 'video' || key === 'video_conferencing' || key === 'video_conferenceing') return 'video_conference';
        if (key === 'seating' || key === 'seat_map') return 'seating_map';
        return key;
      };

      const mappedCatalog = catalogRows
        .map((row) => {
          const id = String(row['id'] ?? '');
          const keyRaw = typeof row['key'] === 'string' ? row['key'] : '';
          const key = normalizeFeatureKey(keyRaw);
          const name = typeof row['name'] === 'string' ? row['name'] : 'Feature';
          const description = typeof row['description'] === 'string' ? row['description'] : '';
          const activeRaw = row['active'];
          const isActive = activeRaw !== false;
          const assignment = assignmentByFeatureId.get(id);
          return {
            id,
            key,
            name,
            description,
            isActive,
            selected: !!assignment?.enabled,
            assignmentId: assignment?.id || null,
            config: assignment?.config || null
          };
        })
        .filter((item) => item.id && item.isActive && targetKeys.has(item.key));

      this.featureCards = mappedCatalog
        .map((item) => {
          const assignment = assignmentByFeatureId.get(item.id);
          return {
            id: item.id,
            key: item.key,
            name: item.name,
            description: item.description,
            selected: item.selected || !!assignment?.enabled,
            assignmentId: item.assignmentId || assignment?.id || null,
            config: item.config || assignment?.config || null
          } as EventFeatureCard;
        })
        .sort((a, b) => {
          const order = ['lineup', 'video_conference', 'schedule', 'seating_map'];
          return order.indexOf(a.key) - order.indexOf(b.key);
        });
    } catch {
      this.featuresError = 'Failed to load features.';
      this.featureCards = [];
    } finally {
      this.featuresLoading = false;
    }
  }

  private async seedDefaultFeatureCatalog(): Promise<Array<Record<string, unknown>>> {
    const defaults: Array<{ key: string; name: string; description: string; active?: boolean }> = [
      { key: 'lineup', name: 'Lineup', description: 'Highlight your lineup of special guests and speakers.', active: true },
      { key: 'video_conference', name: 'Video Conferencing', description: 'Add online conferencing details (Zoom/Meet/etc).', active: true },
      { key: 'schedule', name: 'Schedule', description: 'Create and publish your event schedule.', active: true },
      { key: 'seating_map', name: 'Seating Map', description: 'Seat plan and seat-linked ticket assignment.', active: true }
    ];
    for (const feature of defaults) {
      try {
        await this.directus.create('event_features', feature);
      } catch {
        // Ignore duplicates/permission errors and continue with best-effort seeding.
      }
    }
    try {
      return await this.directus.list('event_features', {
        fields: 'id,key,name,description,active',
        limit: 200
      });
    } catch {
      return [];
    }
  }

  private syncTicketSelection(): void {
    if (!this.selectedTicketIds.size) return;
    const currentIds = new Set(this.ticketRows.map((row) => row.id));
    this.selectedTicketIds = new Set(Array.from(this.selectedTicketIds).filter((id) => currentIds.has(id)));
  }

  private getEditableCheckoutFieldsDraft(): CheckoutFieldOption[] {
    if (this.checkoutModeDraft === 'per_ticket' && this.selectedCheckoutTicketIdDraft) {
      if (!this.checkoutFieldsByTicketDraft[this.selectedCheckoutTicketIdDraft]) {
        this.checkoutFieldsByTicketDraft[this.selectedCheckoutTicketIdDraft] = this.normalizeCheckoutFields(
          this.checkoutFields,
          this.selectedCheckoutTicketIdDraft
        );
      } else {
        this.checkoutFieldsByTicketDraft[this.selectedCheckoutTicketIdDraft] = this.normalizeCheckoutFields(
          this.checkoutFieldsByTicketDraft[this.selectedCheckoutTicketIdDraft],
          this.selectedCheckoutTicketIdDraft
        );
      }
      return this.checkoutFieldsByTicketDraft[this.selectedCheckoutTicketIdDraft];
    }
    return this.checkoutFieldsDraft;
  }

  private ensureCheckoutTargets(): void {
    if (!this.ticketRows.length) return;
    if (!this.selectedCheckoutTicketId || !this.ticketRows.some((ticket) => ticket.id === this.selectedCheckoutTicketId)) {
      this.selectedCheckoutTicketId = this.ticketRows[0].id;
    }
    const validTicketIds = new Set(this.ticketRows.map((ticket) => ticket.id));
    for (const ticket of this.ticketRows) {
      if (!this.checkoutFieldsByTicket[ticket.id]) {
        this.checkoutFieldsByTicket[ticket.id] = this.normalizeCheckoutFields(this.checkoutFields, ticket.id);
      } else {
        this.checkoutFieldsByTicket[ticket.id] = this.normalizeCheckoutFields(
          this.checkoutFieldsByTicket[ticket.id],
          ticket.id
        );
      }
    }
    for (const key of Object.keys(this.checkoutFieldsByTicket)) {
      if (!validTicketIds.has(key)) delete this.checkoutFieldsByTicket[key];
    }
  }

  private ensureCheckoutDraftTargets(): void {
    if (!this.ticketRows.length) {
      this.selectedCheckoutTicketIdDraft = '';
      this.checkoutFieldsByTicketDraft = {};
      return;
    }
    if (!this.selectedCheckoutTicketIdDraft || !this.ticketRows.some((ticket) => ticket.id === this.selectedCheckoutTicketIdDraft)) {
      this.selectedCheckoutTicketIdDraft = this.ticketRows[0].id;
    }
    const validTicketIds = new Set(this.ticketRows.map((ticket) => ticket.id));
    for (const ticket of this.ticketRows) {
      if (!this.checkoutFieldsByTicketDraft[ticket.id]) {
        this.checkoutFieldsByTicketDraft[ticket.id] = this.normalizeCheckoutFields(
          this.checkoutFieldsByTicket[ticket.id] || this.checkoutFields,
          ticket.id
        );
      } else {
        this.checkoutFieldsByTicketDraft[ticket.id] = this.normalizeCheckoutFields(
          this.checkoutFieldsByTicketDraft[ticket.id],
          ticket.id
        );
      }
    }
    for (const key of Object.keys(this.checkoutFieldsByTicketDraft)) {
      if (!validTicketIds.has(key)) delete this.checkoutFieldsByTicketDraft[key];
    }
  }

  private normalizeCheckoutFields(fields: CheckoutFieldOption[], ticketId?: string): CheckoutFieldOption[] {
    const byKey = new Map<string, CheckoutFieldOption>();
    for (const field of fields) {
      byKey.set(field.key, { ...field });
    }
    for (const defaultField of DEFAULT_CHECKOUT_FIELDS) {
      if (!byKey.has(defaultField.key)) {
        byKey.set(defaultField.key, { ...defaultField });
      }
    }
    const normalized: CheckoutFieldOption[] = [];
    for (const defaultField of DEFAULT_CHECKOUT_FIELDS) {
      const existing = byKey.get(defaultField.key);
      if (existing) {
        normalized.push({
          ...existing,
          key: defaultField.key,
          label: defaultField.label,
          required: defaultField.required ?? existing.required,
          locked: defaultField.locked ?? existing.locked
        });
      }
    }
    for (const field of fields) {
      if (!DEFAULT_CHECKOUT_FIELDS.some((item) => item.key === field.key)) {
        normalized.push({ ...field });
      }
    }
    if (ticketId && this.isJciIndonesiaTicket(ticketId) && !normalized.some((item) => item.key === JCI_INDONESIA_CHAPTER_FIELD.key)) {
      normalized.push({ ...JCI_INDONESIA_CHAPTER_FIELD });
    }
    return normalized;
  }

  private isJciIndonesiaTicket(ticketId: string): boolean {
    const ticket = this.ticketRows.find((item) => item.id === ticketId);
    if (!ticket) return false;
    return ticket.name.trim().toLowerCase() === 'jci indonesia';
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }
}
